import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Zap,
  Eye,
  Calendar,
  Settings,
  Download,
  BarChart3,
  Video,
  Info,
  ExternalLink,
  Copy,
  DollarSign,
  Users,
  Layers,
  Swords,
  Trophy,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  ArrowRight,
  TrendingDown,
  Play,
  Plus,
  X,
  RotateCcw,
} from 'lucide-react';
import { tournamentAPI, sponsorAPI } from '../../../utils/api';
import { AuthContext } from '../../../context/AuthContext';
import RoundConfigModal from '../ui/RoundConfigModal';
import GroupConfirmModal from '../ui/GroupConfirmModal';
import Lobby5v5PreviewModal from '../ui/Lobby5v5PreviewModal';
import GroupManagementView from '../ui/GroupManagementView';
import MatchConfigModal from '../ui/MatchConfigModal';
import MatchPointsModal from '../ui/MatchPointsModal';
import EliminatedTeamsModal from '../ui/EliminatedTeamsModal';
import PointsTableModal from '../ui/PointsTableModal';
import BulkScheduleModal from '../ui/BulkScheduleModal';
import CountdownTimer from '../../../components/CountdownTimer';
import Toast from '../../../components/Toast';
import ConfirmModal from '../../../components/ConfirmModal';
import TeamPlayersModal from '../ui/TeamPlayersModal';
import RoundNamesModal from '../ui/RoundNamesModal';
import './ManageTournament.css';

// Compact inline countdown for the Start Tournament card
const StartCountdown = ({ targetDate }) => {
  const [timeLeft, setTimeLeft] = React.useState(null);

  React.useEffect(() => {
    const calc = () => {
      const diff = new Date(targetDate) - new Date();
      if (diff <= 0) {
        setTimeLeft(null);
        return;
      }
      setTimeLeft({
        d: Math.floor(diff / 86400000),
        h: Math.floor((diff / 3600000) % 24),
        m: Math.floor((diff / 60000) % 60),
        s: Math.floor((diff / 1000) % 60),
      });
    };
    calc();
    const t = setInterval(calc, 1000);
    return () => clearInterval(t);
  }, [targetDate]);

  if (!timeLeft)
    return <p className="text-xs text-green-400 font-medium">Unlocked — ready to start!</p>;

  const pad = (n) => String(n).padStart(2, '0');
  return (
    <div className="flex items-center gap-2 mt-1">
      <span className="text-xs text-muted-foreground">Starts in</span>
      <div className="flex items-center gap-1 bg-accent/10 border border-accent/20 rounded-lg px-2.5 py-1">
        {timeLeft.d > 0 && (
          <>
            <span className="text-xs font-bold text-accent tabular-nums">{timeLeft.d}d</span>
            <span className="text-accent/40 text-xs mx-0.5">·</span>
          </>
        )}
        <span className="text-sm font-mono font-bold text-accent tabular-nums">
          {pad(timeLeft.h)}:{pad(timeLeft.m)}:{pad(timeLeft.s)}
        </span>
      </div>
    </div>
  );
};

const ManageTournament = ({ inlineId, onBack, onStarted } = {}) => {
  const params = useParams();
  const id = inlineId || params.id;
  const navigate = useNavigate();
  const { isHost, loading: authLoading } = useContext(AuthContext);

  // Convert UTC ISO string → local datetime-local input value (YYYY-MM-DDTHH:mm)
  const toLocalDatetimeInput = (isoString) => {
    if (!isoString) return '';
    const d = new Date(isoString);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const [tournament, setTournament] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentRound, setCurrentRound] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    title: '',
    description: '',
    rules: '',
    banner_image: null,
    rounds: 0,
    entry_fee: 0,
    prize_pool: 0,
    max_participants: 0,
    tournament_start: '',
    live_link: '',
  });
  const [bannerPreview, setBannerPreview] = useState(null);
  const [roundNames, setRoundNames] = useState({});
  const [showRoundNamesModal, setShowRoundNamesModal] = useState(false);
  const [roundDates, setRoundDates] = useState({});
  const [editRoundQualifying, setEditRoundQualifying] = useState({}); // { "1": 16, "2": 8, ... }
  const [editRoundMatches, setEditRoundMatches] = useState({}); // { "1": 4, "2": 3, ... }
  const [editRoundMatchCustom, setEditRoundMatchCustom] = useState({}); // { "1": true } if custom
  const [editPlacementPoints, setEditPlacementPoints] = useState([]);
  const [editPrizeDistribution, setEditPrizeDistribution] = useState([]);
  const [editSpecialAwards, setEditSpecialAwards] = useState([]);
  const [editCouponTiers, setEditCouponTiers] = useState([]);
  const [editCouponSpecialAwards, setEditCouponSpecialAwards] = useState([]);
  const [showTeamsModal, setShowTeamsModal] = useState(false);
  const [teamsTab, setTeamsTab] = useState('active'); // 'active' or 'rejected'
  const [finalStandings, setFinalStandings] = useState(null);

  // Groups and Matches State
  const [showRoundConfigModal, setShowRoundConfigModal] = useState(false);
  const [showGroupConfirmModal, setShowGroupConfirmModal] = useState(false);
  const [pendingRoundConfig, setPendingRoundConfig] = useState(null); // { configData, pendingGroups }
  const [roundGroups, setRoundGroups] = useState([]);
  const [pastRoundsGroups, setPastRoundsGroups] = useState({}); // { [roundNumber]: groups[] }
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [showMatchConfigModal, setShowMatchConfigModal] = useState(false);
  const [showMatchPointsModal, setShowMatchPointsModal] = useState(false);
  const [currentMatch, setCurrentMatch] = useState(null);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [showEliminatedModal, setShowEliminatedModal] = useState(false);
  const [eliminatedData, setEliminatedData] = useState(null);
  const [showPointsTable, setShowPointsTable] = useState(false);
  const [showBulkSchedule, setShowBulkSchedule] = useState(false);
  const [bulkScheduleRound, setBulkScheduleRound] = useState(null);
  const [toast, setToast] = useState(null);
  const [lastSelectedMatch, setLastSelectedMatch] = useState(1);
  const [showTeamDetails, setShowTeamDetails] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
    type: 'danger',
  });

  // Live Stream + Overview
  const [showLiveUrlDialog, setShowLiveUrlDialog] = useState(false);
  const [tempLiveUrl, setTempLiveUrl] = useState('');
  const [showOverviewDialog, setShowOverviewDialog] = useState(false);

  // Sponsors
  const [sponsors, setSponsors] = useState([]);
  const [sponsorForm, setSponsorForm] = useState({
    name: '',
    sponsor_type: '',
    website_url: '',
    logo: null,
  });
  const [sponsorLogoPreview, setSponsorLogoPreview] = useState(null);
  const [sponsorSaving, setSponsorSaving] = useState(false);
  const [sponsorError, setSponsorError] = useState('');
  const [editingSponsor, setEditingSponsor] = useState(null); // { id, name, sponsor_type, website_url, logo, logoPreview }
  const [editSponsorSaving, setEditSponsorSaving] = useState(false);

  // 5v5 lobby preview (inline, not a modal)
  const [lobbyPreview, setLobbyPreview] = useState(null); // { lobbies, bestOf, qualifyingPerGroup, roundNumber }

  // Export state
  const [isGeneratingSlotList, setIsGeneratingSlotList] = useState(false);
  const [showSlotListDropdown, setShowSlotListDropdown] = useState(false);
  const [selectedExportGroups, setSelectedExportGroups] = useState(new Set());
  const slotListDropdownRef = useRef(null);
  const [showIGNDropdown, setShowIGNDropdown] = useState(false);
  const ignDropdownRef = useRef(null);

  // Bracket Generator
  const [showBracketGenerator, setShowBracketGenerator] = useState(false);
  const [bracketExpanded, setBracketExpanded] = useState(true);
  const [selectedBracketTemplate, setSelectedBracketTemplate] = useState('bgis');
  const [bracketCustomRounds, setBracketCustomRounds] = useState([
    {
      name: 'Quarter Finals',
      groups: 4,
      teamsPerGroup: 25,
      matchesPerGroup: 4,
      advancePerGroup: 12,
    },
    { name: 'Semi Finals', groups: 2, teamsPerGroup: 24, matchesPerGroup: 4, advancePerGroup: 8 },
    { name: 'Finals', groups: 1, teamsPerGroup: 16, matchesPerGroup: 6, advancePerGroup: 1 },
  ]);
  const [bracketGenerated, setBracketGenerated] = useState(false);

  const bracketTemplates = [
    {
      id: 'pmgc',
      name: 'PMGC Style',
      icon: '\u{1F3C6}',
      description:
        '48 teams \u2192 Group Stage (3 groups of 16) \u2192 Survival Stage \u2192 Last Chance \u2192 Grand Finals.',
      stages: ['Group Stage', 'Survival Stage', 'Last Chance', 'Grand Finals'],
      defaultRounds: [
        {
          name: 'Group Stage',
          groups: 3,
          teamsPerGroup: 16,
          matchesPerGroup: 6,
          advancePerGroup: 5,
        },
        {
          name: 'Survival Stage',
          groups: 1,
          teamsPerGroup: 16,
          matchesPerGroup: 6,
          advancePerGroup: 8,
        },
        {
          name: 'Last Chance',
          groups: 1,
          teamsPerGroup: 8,
          matchesPerGroup: 4,
          advancePerGroup: 4,
        },
        {
          name: 'Grand Finals',
          groups: 1,
          teamsPerGroup: 16,
          matchesPerGroup: 6,
          advancePerGroup: 1,
        },
      ],
    },
    {
      id: 'bgis',
      name: 'BGIS Style',
      icon: '\u{1F6E1}\uFE0F',
      description:
        '100 teams \u2192 Quarter Finals (4\u00D725) \u2192 Semi Finals (2\u00D724) \u2192 Finals (16 teams).',
      stages: ['Quarter Finals', 'Semi Finals', 'Finals'],
      defaultRounds: [
        {
          name: 'Quarter Finals',
          groups: 4,
          teamsPerGroup: 25,
          matchesPerGroup: 4,
          advancePerGroup: 12,
        },
        {
          name: 'Semi Finals',
          groups: 2,
          teamsPerGroup: 24,
          matchesPerGroup: 4,
          advancePerGroup: 8,
        },
        { name: 'Finals', groups: 1, teamsPerGroup: 16, matchesPerGroup: 6, advancePerGroup: 1 },
      ],
    },
    {
      id: 'league',
      name: 'League Format',
      icon: '\u{1F4CA}',
      description: 'Round-robin within groups. Top teams advance to playoffs then Grand Finals.',
      stages: ['League Stage', 'Playoffs', 'Grand Finals'],
      defaultRounds: [
        {
          name: 'League Stage',
          groups: 4,
          teamsPerGroup: 16,
          matchesPerGroup: 6,
          advancePerGroup: 4,
        },
        { name: 'Playoffs', groups: 1, teamsPerGroup: 16, matchesPerGroup: 6, advancePerGroup: 8 },
        {
          name: 'Grand Finals',
          groups: 1,
          teamsPerGroup: 8,
          matchesPerGroup: 6,
          advancePerGroup: 1,
        },
      ],
    },
    {
      id: 'classic',
      name: 'Classic Elimination',
      icon: '\u{1F3AF}',
      description: 'Groups play matches, bottom half eliminated each round until finals.',
      stages: ['Round 1', 'Round 2', 'Finals'],
      defaultRounds: [
        { name: 'Round 1', groups: 4, teamsPerGroup: 25, matchesPerGroup: 4, advancePerGroup: 12 },
        { name: 'Round 2', groups: 2, teamsPerGroup: 24, matchesPerGroup: 4, advancePerGroup: 8 },
        { name: 'Finals', groups: 1, teamsPerGroup: 16, matchesPerGroup: 6, advancePerGroup: 1 },
      ],
    },
    {
      id: 'custom',
      name: 'Custom Bracket',
      icon: '\u2699\uFE0F',
      description:
        'Build your own bracket structure with custom rounds, groups, and advancement rules.',
      stages: ['Round 1', 'Finals'],
      defaultRounds: [
        { name: 'Round 1', groups: 4, teamsPerGroup: 25, matchesPerGroup: 4, advancePerGroup: 12 },
        { name: 'Finals', groups: 1, teamsPerGroup: 16, matchesPerGroup: 6, advancePerGroup: 1 },
      ],
    },
  ];

  const getRoundStatus = useCallback(
    (roundNum) => {
      const val = tournament?.round_status?.[String(roundNum)];
      if (!val) return 'upcoming';
      // Backend sometimes stores {"status": "ongoing"} and sometimes just "ongoing"
      if (typeof val === 'object' && val !== null) return val.status || 'upcoming';
      return val;
    },
    [tournament?.round_status]
  );

  const isRoundCompleted = (roundNum) => {
    return getRoundStatus(roundNum) === 'completed';
  };

  const isRoundOngoing = (roundNum) => {
    return getRoundStatus(roundNum) === 'ongoing';
  };

  const canStartRound = (roundNum) => {
    if (!tournament || tournament.status === 'completed') return false;
    const status = getRoundStatus(roundNum);
    if (status === 'completed' || status === 'ongoing') return false;
    if (roundNum === 1) {
      return tournament.current_round === 0;
    }
    return isRoundCompleted(roundNum - 1) && tournament.current_round === 0;
  };

  const allRoundsCompleted = () => {
    return tournament?.rounds?.every((round) => isRoundCompleted(round.round)) || false;
  };

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
  }, []);

  // Hardcoded for Tournament
  const getEventLabel = () => 'Tournament';
  const getEventLabelLower = () => 'tournament';

  const fetchRoundGroups = useCallback(
    async (roundNumber) => {
      try {
        setGroupsLoading(true);
        const response = await tournamentAPI.getRoundGroups(id, roundNumber);
        setRoundGroups(response.data.groups || []);
      } catch (error) {
        console.error('Error fetching groups:', error);
        if (error.response?.status !== 404) {
          showToast('Failed to load groups');
        }
        setRoundGroups([]);
      } finally {
        setGroupsLoading(false);
      }
    },
    [id, showToast]
  );

  const fetchFinalStandings = useCallback(
    async (roundNum) => {
      try {
        const response = await tournamentAPI.getRoundResults(id, roundNum);
        const data = response.data;

        // Flatten standings from all groups into a single results array
        let allResults = [];
        if (data.groups) {
          data.groups.forEach((group) => {
            if (group.standings && Array.isArray(group.standings)) {
              allResults = [...allResults, ...group.standings];
            } else if (group.standings && typeof group.standings === 'object') {
              // Handle single group standings object if needed
              const standingsArray = Object.values(group.standings).filter(
                (s) => typeof s === 'object'
              );
              allResults = [...allResults, ...standingsArray];
            }
          });
        }

        // Merge with existing results if any
        if (data.results && Array.isArray(data.results)) {
          allResults = [...allResults, ...data.results];
        }

        // Remove duplicates and sort by total points
        const seenTeams = new Set();
        const uniqueResults = allResults.filter((team) => {
          const id = team.team_id || team.id;
          if (!id || seenTeams.has(id)) return false;
          seenTeams.add(id);
          return true;
        });

        uniqueResults.sort((a, b) => (b.total_points || 0) - (a.total_points || 0));

        // Resolve names from registrations if missing
        const resultsWithNames = uniqueResults.map((result) => {
          if (!result.team_name && (result.team_id || result.id)) {
            const reg = response.data.registrations?.find(
              (r) => r.team === (result.team_id || result.id)
            );
            if (reg) return { ...result, team_name: reg.team_name };
          }
          return result;
        });

        setFinalStandings({ ...data, results: resultsWithNames });
      } catch (error) {
        console.error('Error fetching final standings:', error);
      }
    },
    [id]
  );

  const fetchTournamentData = useCallback(
    async (preserveRound = false) => {
      try {
        const response = await tournamentAPI.getManageTournament(id);
        setTournament(response.data.tournament);
        setRegistrations(response.data.registrations || []);
        const t = response.data.tournament;
        const currentRoundNum =
          t.status === 'completed'
            ? t.rounds?.length || 0
            : t.status === 'upcoming' || t.current_round === 0
              ? 1
              : t.current_round || 0;
        if (!preserveRound) {
          setCurrentRound(currentRoundNum);
        }

        // Initialize edit data
        setEditData({
          title: response.data.tournament.title || '',
          description: response.data.tournament.description || '',
          rules: response.data.tournament.rules || '',
          banner_image: null,
          rounds: response.data.tournament.rounds?.length || 1,
          entry_fee: response.data.tournament.entry_fee || 0,
          prize_pool: response.data.tournament.prize_pool || 0,
          max_participants: response.data.tournament.max_participants || 0,
          tournament_start: toLocalDatetimeInput(response.data.tournament.tournament_start),
          live_link: response.data.tournament.live_link || '',
        });

        // Parse round_names if it's a string, otherwise use as is
        const initialRoundNames =
          typeof response.data.tournament.round_names === 'string'
            ? JSON.parse(response.data.tournament.round_names)
            : response.data.tournament.round_names || {};
        setRoundNames(initialRoundNames);

        // Parse round_dates
        const initialRoundDates =
          typeof response.data.tournament.round_dates === 'string'
            ? JSON.parse(response.data.tournament.round_dates)
            : response.data.tournament.round_dates || {};
        setRoundDates(initialRoundDates);

        // Init qualifying_teams per round
        const initialQualifying = {};
        const initialMatches = {};
        (response.data.tournament.rounds || []).forEach((r) => {
          if (r.qualifying_teams !== undefined)
            initialQualifying[String(r.round)] = r.qualifying_teams;
          if (r.max_matches !== undefined) initialMatches[String(r.round)] = r.max_matches;
        });
        setEditRoundQualifying(initialQualifying);
        setEditRoundMatches(initialMatches);
        const initialMatchCustom = {};
        (response.data.tournament.rounds || []).forEach((r) => {
          if (r.max_matches !== undefined && r.max_matches > 6)
            initialMatchCustom[String(r.round)] = true;
        });
        setEditRoundMatchCustom(initialMatchCustom);

        const pp = response.data.tournament.placement_points || {};
        setEditPlacementPoints(
          Object.entries(pp)
            .map(([pos, pts]) => ({ position: Number(pos), points: Number(pts) }))
            .sort((a, b) => a.position - b.position)
        );

        // Parse prize_distribution
        const pd = response.data.tournament.prize_distribution;
        if (pd && typeof pd === 'object' && Object.keys(pd).length > 0) {
          const pdArray = Object.entries(pd).map(([place, amount]) => ({
            place,
            amount: parseInt(amount) || 0,
          }));
          setEditPrizeDistribution(pdArray);
        } else {
          const pool = parseFloat(response.data.tournament.prize_pool) || 0;
          setEditPrizeDistribution([
            { place: '1st', amount: Math.round(pool * 0.5) },
            { place: '2nd', amount: Math.round(pool * 0.3) },
            { place: '3rd', amount: Math.round(pool * 0.2) },
          ]);
        }

        // Load special_awards and coupon_distribution
        setEditSpecialAwards(response.data.tournament.special_awards || []);
        const cdLoad = response.data.tournament.coupon_distribution;
        if (cdLoad && typeof cdLoad === 'object' && !Array.isArray(cdLoad)) {
          setEditCouponTiers(cdLoad.tiers || []);
          setEditCouponSpecialAwards(cdLoad.special_awards || []);
        } else {
          setEditCouponTiers([]);
          setEditCouponSpecialAwards([]);
        }

        // Load selected teams for current round
        if (currentRoundNum > 0) {
          // Fetch groups only if round is actively ongoing (not pre_configured)
          // pre_configured rounds should show the "Ready to Start" screen first
          const roundStatusVal = response.data.tournament.round_status?.[String(currentRoundNum)];
          const roundStatusStr =
            typeof roundStatusVal === 'object' ? roundStatusVal?.status : roundStatusVal;
          if (
            response.data.tournament.status === 'ongoing' &&
            roundStatusStr !== 'pre_configured'
          ) {
            fetchRoundGroups(currentRoundNum); // fire without await — page renders immediately, groups load in background
          }
          if (response.data.tournament.status === 'completed') {
            fetchFinalStandings(response.data.tournament.rounds.length);
          }
        }
      } catch (error) {
        console.error('Error fetching tournament data:', error);
        showToast(`Failed to load tournament data`, 'error');
      } finally {
        setLoading(false);
      }
    },
    [id, fetchRoundGroups, fetchFinalStandings, showToast]
  );

  const fetchSponsors = useCallback(async () => {
    try {
      const res = await sponsorAPI.list(id);
      const data = res.data;
      setSponsors(Array.isArray(data) ? data : (data?.results ?? []));
    } catch {
      // non-critical, silently ignore
    }
  }, [id]);

  const handleSponsorLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSponsorForm((f) => ({ ...f, logo: file }));
    setSponsorLogoPreview(URL.createObjectURL(file));
  };

  const handleAddSponsor = async (e) => {
    e.preventDefault();
    if (!sponsorForm.name.trim()) {
      setSponsorError('Sponsor name is required.');
      return;
    }
    setSponsorError('');
    setSponsorSaving(true);
    try {
      const fd = new FormData();
      fd.append('name', sponsorForm.name.trim());
      fd.append('sponsor_type', sponsorForm.sponsor_type.trim());
      if (sponsorForm.website_url.trim()) {
        const rawUrl = sponsorForm.website_url.trim();
        const url = /^https?:\/\//i.test(rawUrl) ? rawUrl : `https://${rawUrl}`;
        fd.append('website_url', url);
      }
      if (sponsorForm.logo) fd.append('logo', sponsorForm.logo);
      await sponsorAPI.add(id, fd);
      setSponsorForm({ name: '', sponsor_type: '', website_url: '', logo: null });
      setSponsorLogoPreview(null);
      await fetchSponsors();
      showToast('Sponsor added', 'success');
    } catch {
      setSponsorError('Failed to add sponsor. Please try again.');
    } finally {
      setSponsorSaving(false);
    }
  };

  const handleRemoveSponsor = async (sponsorId) => {
    try {
      await sponsorAPI.remove(id, sponsorId);
      setSponsors((prev) => prev.filter((s) => s.id !== sponsorId));
    } catch {
      showToast('Failed to remove sponsor', 'error');
    }
  };

  const handleMoveOrder = async (sponsorId, direction) => {
    const idx = sponsors.findIndex((s) => s.id === sponsorId);
    if (idx < 0) return;
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= sponsors.length) return;

    const updated = [...sponsors];
    const temp = updated[idx].display_order;
    updated[idx] = { ...updated[idx], display_order: updated[swapIdx].display_order };
    updated[swapIdx] = { ...updated[swapIdx], display_order: temp };
    // Swap in local state immediately for snappy UI
    [updated[idx], updated[swapIdx]] = [updated[swapIdx], updated[idx]];
    setSponsors(updated);

    try {
      const fdA = new FormData();
      fdA.append('display_order', updated[idx].display_order);
      const fdB = new FormData();
      fdB.append('display_order', updated[swapIdx].display_order);
      await Promise.all([
        sponsorAPI.update(id, updated[idx].id, fdA),
        sponsorAPI.update(id, updated[swapIdx].id, fdB),
      ]);
    } catch {
      // Revert on failure
      await fetchSponsors();
      showToast('Failed to reorder sponsors', 'error');
    }
  };

  const handleSaveEditSponsor = async () => {
    if (!editingSponsor?.name?.trim()) return;
    setEditSponsorSaving(true);
    try {
      const fd = new FormData();
      fd.append('name', editingSponsor.name.trim());
      fd.append('sponsor_type', editingSponsor.sponsor_type.trim());
      if (editingSponsor.website_url.trim())
        fd.append('website_url', editingSponsor.website_url.trim());
      if (editingSponsor.logo instanceof File) fd.append('logo', editingSponsor.logo);
      await sponsorAPI.update(id, editingSponsor.id, fd);
      setEditingSponsor(null);
      await fetchSponsors();
      showToast('Sponsor updated', 'success');
    } catch {
      showToast('Failed to update sponsor', 'error');
    } finally {
      setEditSponsorSaving(false);
    }
  };

  useEffect(() => {
    // Wait for auth to load before checking
    if (authLoading) return;

    if (!isHost()) {
      navigate('/');
      return;
    }
    fetchTournamentData();
    fetchSponsors();
  }, [id, authLoading, isHost, navigate, fetchTournamentData, fetchSponsors]);

  const handleEditToggle = () => {
    if (isEditing) {
      // Cancel editing - reset form
      setEditData({
        title: tournament.title || '',
        description: tournament.description || '',
        rules: tournament.rules || '',
        banner_image: null,
        rounds: tournament.rounds?.length || 1,
        entry_fee: tournament.entry_fee || 0,
        prize_pool: tournament.prize_pool || 0,
        max_participants: tournament.max_participants || 0,
        tournament_start: toLocalDatetimeInput(tournament.tournament_start),
        live_link: tournament.live_link || '',
      });
      setBannerPreview(tournament.banner_image || null);
      setRoundNames(tournament.round_names || {});
      setRoundDates(tournament.round_dates || {});
      const resetQualifying = {};
      const resetMatches = {};
      (tournament.rounds || []).forEach((r) => {
        if (r.qualifying_teams !== undefined) resetQualifying[String(r.round)] = r.qualifying_teams;
        if (r.max_matches !== undefined) resetMatches[String(r.round)] = r.max_matches;
      });
      setEditRoundQualifying(resetQualifying);
      setEditRoundMatches(resetMatches);
      const resetMatchCustom = {};
      (tournament.rounds || []).forEach((r) => {
        if (r.max_matches !== undefined && r.max_matches > 6)
          resetMatchCustom[String(r.round)] = true;
      });
      setEditRoundMatchCustom(resetMatchCustom);
      const ppReset = tournament.placement_points || {};
      setEditPlacementPoints(
        Object.entries(ppReset)
          .map(([pos, pts]) => ({ position: Number(pos), points: Number(pts) }))
          .sort((a, b) => a.position - b.position)
      );
      // Reset prize distribution
      const pd = tournament.prize_distribution;
      if (pd && typeof pd === 'object' && Object.keys(pd).length > 0) {
        setEditPrizeDistribution(
          Object.entries(pd).map(([place, amount]) => ({
            place,
            amount: parseInt(amount) || 0,
          }))
        );
      }
      // Reset special_awards and coupon_distribution
      setEditSpecialAwards(tournament.special_awards || []);
      const cdReset = tournament.coupon_distribution;
      if (cdReset && typeof cdReset === 'object' && !Array.isArray(cdReset)) {
        setEditCouponTiers(cdReset.tiers || []);
        setEditCouponSpecialAwards(cdReset.special_awards || []);
      } else {
        setEditCouponTiers([]);
        setEditCouponSpecialAwards([]);
      }
    }
    setIsEditing(!isEditing);
  };

  const handleSaveRoundNames = (names) => {
    setRoundNames(names);
  };

  const handleRoundDateChange = (roundNum, field, value) => {
    setRoundDates((prev) => ({
      ...prev,
      [String(roundNum)]: {
        ...prev[String(roundNum)],
        [field]: value,
      },
    }));
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditData({ ...editData, [name]: value });
  };

  const handleBannerChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        showToast('Banner image size should not exceed 5MB', 'warning');
        return;
      }
      setEditData({ ...editData, banner_image: file });
      const reader = new FileReader();
      reader.onloadend = () => {
        setBannerPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveChanges = async () => {
    const totalDistributed = editPrizeDistribution.reduce(
      (s, p) => s + (parseFloat(p.amount) || 0),
      0
    );
    const prizePool = parseFloat(editData.prize_pool) || 0;
    if (prizePool > 0 && totalDistributed > prizePool) {
      showToast(
        `Prize distribution (₹${totalDistributed.toLocaleString()}) exceeds prize pool (₹${prizePool.toLocaleString()})`,
        'error'
      );
      return;
    }

    try {
      const formData = new FormData();
      formData.append('title', editData.title);
      formData.append('description', editData.description);
      formData.append('rules', editData.rules);
      formData.append('live_link', editData.live_link || '');

      // Add rounds data — always send if count, qualifying, or matches changed
      const existingRounds = tournament.rounds || [];
      const roundCountChanged = editData.rounds && editData.rounds !== existingRounds.length;
      const qualifyingChanged = Object.keys(editRoundQualifying).some((rn) => {
        const orig = existingRounds.find((r) => String(r.round) === rn);
        return (
          orig && String(orig.qualifying_teams ?? '') !== String(editRoundQualifying[rn] ?? '')
        );
      });
      const matchesChanged = Object.keys(editRoundMatches).some((rn) => {
        const orig = existingRounds.find((r) => String(r.round) === rn);
        return orig && String(orig.max_matches ?? '') !== String(editRoundMatches[rn] ?? '');
      });
      const roundNamesChanged = Object.keys(roundNames).some(
        (rn) => roundNames[rn] !== (tournament.round_names || {})[rn]
      );

      if (roundCountChanged || qualifyingChanged || matchesChanged || roundNamesChanged) {
        const count = editData.rounds || existingRounds.length;
        const roundsData = Array.from({ length: count }, (_, i) => {
          const rn = i + 1;
          const existing = existingRounds.find((r) => r.round === rn) || {};
          const qt = editRoundQualifying[String(rn)];
          const rm = editRoundMatches[String(rn)];
          return {
            round: rn,
            ...(rn === 1
              ? { max_teams: parseInt(tournament.max_participants) || existing.max_teams || 0 }
              : {}),
            ...(existing.max_teams && rn !== 1 ? { max_teams: existing.max_teams } : {}),
            max_matches:
              rm !== undefined ? Number(rm) : (existing.max_matches ?? tournament.max_matches ?? 1),
            qualifying_teams:
              qt !== undefined && qt !== '' ? Number(qt) : (existing.qualifying_teams ?? 1),
          };
        });
        formData.append('rounds', JSON.stringify(roundsData));
      }

      // Add round names if they exist
      if (Object.keys(roundNames).length > 0) {
        formData.append('round_names', JSON.stringify(roundNames));
      }

      // Add round dates & mode if they exist
      if (Object.keys(roundDates).length > 0) {
        formData.append('round_dates', JSON.stringify(roundDates));
      }

      // Add placement_points
      if (editPlacementPoints.length > 0) {
        const ppObj = {};
        editPlacementPoints.forEach(({ position, points }) => {
          ppObj[String(position)] = points;
        });
        formData.append('placement_points', JSON.stringify(ppObj));
      }

      // Add prize distribution
      if (editPrizeDistribution.length > 0) {
        const prizeObj = {};
        editPrizeDistribution.forEach((item) => {
          prizeObj[item.place] = item.amount;
        });
        formData.append('prize_distribution', JSON.stringify(prizeObj));
      }

      // Add special_awards
      const validSpecialAwards = editSpecialAwards.filter((a) => a.name && String(a.name).trim());
      formData.append('special_awards', JSON.stringify(validSpecialAwards));

      // Add coupon_distribution
      const validCouponTiers = editCouponTiers
        .filter(
          (t) =>
            t.rank &&
            String(t.rank).trim() &&
            t.coupons?.some((c) => c.name && String(c.name).trim())
        )
        .map((t) => ({
          rank: String(t.rank).trim(),
          coupons: (t.coupons || [])
            .filter((c) => c.name && String(c.name).trim())
            .map((c) => ({
              name: String(c.name).trim(),
              link: String(c.link || '').trim(),
              amount: Number(c.amount) || 0,
            })),
        }));
      const validCouponSpecial = editCouponSpecialAwards
        .filter(
          (a) =>
            a.name &&
            String(a.name).trim() &&
            a.coupons?.some((c) => c.name && String(c.name).trim())
        )
        .map((a) => ({
          name: String(a.name).trim(),
          coupons: (a.coupons || [])
            .filter((c) => c.name && String(c.name).trim())
            .map((c) => ({
              name: String(c.name).trim(),
              link: String(c.link || '').trim(),
              amount: Number(c.amount) || 0,
            })),
        }));
      formData.append(
        'coupon_distribution',
        JSON.stringify({ tiers: validCouponTiers, special_awards: validCouponSpecial })
      );

      // Add banner image if a new file was selected
      if (editData.banner_image instanceof File) {
        formData.append('banner_image', editData.banner_image);
      }

      // Add entry fee, prize pool, max participants if different from current
      if (editData.entry_fee !== tournament.entry_fee) {
        formData.append('entry_fee', editData.entry_fee || 0);
      }
      if (editData.prize_pool !== tournament.prize_pool) {
        formData.append('prize_pool', editData.prize_pool || 0);
      }
      if (editData.max_participants !== tournament.max_participants) {
        formData.append('max_participants', editData.max_participants || 0);
      }

      // Always send tournament_start so host can change it to unlock early
      if (editData.tournament_start) {
        formData.append('tournament_start', new Date(editData.tournament_start).toISOString());
      }

      await tournamentAPI.updateTournamentFields(id, formData);
      showToast(`${getEventLabel()} updated successfully!`);
      setIsEditing(false);
      fetchTournamentData();
    } catch (error) {
      showToast(
        error.response?.data?.error ||
          error.response?.data?.message ||
          `Failed to update ${getEventLabelLower()}`
      );
      console.error('Update error:', error);
    }
  };

  const handleStartRound = async (roundNumber) => {
    // If round was pre-configured, officially start it (sends slot_list notifications) then load groups
    const roundStatus = getRoundStatus(roundNumber);
    if (roundStatus === 'pre_configured') {
      try {
        setLoading(true);
        await tournamentAPI.startRound(tournament.id, roundNumber);
        await fetchTournamentData();
        const res = await tournamentAPI.getRoundGroups(tournament.id, roundNumber);
        const groups = res.data?.groups || [];
        setCurrentRound(roundNumber);
        setRoundGroups(groups);
        setSelectedGroup(groups[0] || null);
        showToast(`Round ${roundNumber} started! Players have been notified.`);
      } catch (err) {
        showToast(err.response?.data?.error || 'Failed to start round', 'error');
      } finally {
        setLoading(false);
      }
      return;
    }

    // New system - show configuration modal
    let totalTeams = 0;
    if (roundNumber === 1) {
      totalTeams = registrations.filter(
        (r) => r.status === 'confirmed' || r.status === 'approved'
      ).length;
    } else {
      const prevRoundKey = String(roundNumber - 1);
      totalTeams = tournament.selected_teams?.[prevRoundKey]?.length || 0;
    }

    if (totalTeams === 0) {
      showToast('No teams available for this round');
      return;
    }

    setCurrentRound(roundNumber);
    setShowRoundConfigModal(true);
  };
  const handleEndTournament = async () => {
    const allRoundsDone = allRoundsCompleted();
    const confirmMessage = allRoundsDone
      ? 'End tournament? This will mark it as completed and cannot be undone.'
      : 'End tournament early? Not all rounds are completed. This will mark it as completed and cannot be undone.';

    setConfirmModal({
      isOpen: true,
      title: `End ${getEventLabel()}`,
      message: confirmMessage,
      confirmText: `End ${getEventLabel()}`,
      type: 'danger',
      onConfirm: async () => {
        try {
          setLoading(true);
          const response = await tournamentAPI.endTournament(id);
          showToast(response.data.message || `${getEventLabel()} ended successfully!`);
          fetchTournamentData();
        } catch (error) {
          console.error('Error ending tournament:', error);
          showToast(
            error.response?.data?.error || `Failed to end ${getEventLabelLower()}`,
            'error'
          );
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const handleTeamClick = (team) => {
    // team.id from groups API is already the registration ID
    const reg = registrations.find((r) => r.id === team.id);
    setSelectedTeam({
      id: team.id,
      team_name: team.team_name,
      ign_submissions: reg?.ign_submissions || {},
    });
    setShowTeamDetails(true);
  };

  const handleWinnerClick = (winner) => {
    // Find the registration by team_id or team_name
    const teamReg = registrations.find(
      (r) => r.team === winner.team_id || r.team_name === winner.team_name
    );
    if (teamReg) {
      setSelectedTeam({
        id: teamReg.id,
        team_name: winner.team_name,
        ign_submissions: teamReg.ign_submissions || {},
      });
      setShowTeamDetails(true);
    }
  };

  // ===== Groups and Matches Handlers =====

  // Helper: build team list for a given round number
  const getTeamListForRound = (roundNumber) => {
    if (roundNumber === 1) {
      return registrations
        .filter((r) => r.status === 'confirmed' || r.status === 'approved')
        .map((r) => r.team_name || `Team ${r.id}`);
    }
    // For subsequent rounds, use selected_teams from prev round
    const prevKey = String(roundNumber - 1);
    const selected = tournament?.selected_teams?.[prevKey] || [];
    return selected.map((t) => {
      if (typeof t === 'number' || (typeof t === 'string' && !isNaN(t))) {
        const reg = registrations.find((r) => r.id === Number(t));
        return reg?.team_name || `Team ${t}`;
      }
      return t.team_name || t.name || String(t);
    });
  };

  // Step 1: called by RoundConfigModal — compute groups preview, show GroupConfirmModal
  const handleSubmitRoundConfig = (configData) => {
    // 5v5 mode: show inline lobby preview before calling API
    if (configData?._5v5Preview) {
      setLobbyPreview({
        lobbies: configData.lobbies,
        bestOf: configData.bestOf,
        qualifyingPerGroup: configData.qualifyingPerGroup,
        roundNumber: configData.roundNumber,
      });
      return;
    }

    // 5v5 rounds are already configured by the modal's API call — skip confirmation
    if (configData?._alreadyConfigured) {
      handleConfirmedRoundConfig(configData);
      return;
    }

    // Compute group preview for BR mode
    const teamsPerGroup = configData.teams_per_group;
    const teamList = getTeamListForRound(currentRound);
    const numGroups = Math.ceil(teamList.length / teamsPerGroup);
    const computedGroups = [];
    for (let i = 0; i < numGroups; i++) {
      const start = i * teamsPerGroup;
      const end = Math.min(start + teamsPerGroup, teamList.length);
      computedGroups.push(teamList.slice(start, end));
    }

    setPendingRoundConfig({ configData, pendingGroups: computedGroups });
    setShowRoundConfigModal(false);
    setShowGroupConfirmModal(true);
  };

  // Step 2: called when user clicks "Confirm & Start" in GroupConfirmModal
  // shuffleRequested=true means user clicked Shuffle in the modal before confirming
  const handleConfirmedRoundConfig = async (configData, shuffleRequested = false) => {
    setShowGroupConfirmModal(false);
    try {
      setLoading(true);
      if (!configData?._alreadyConfigured) {
        await tournamentAPI.configureRound(id, currentRound, configData);
      }
      if (shuffleRequested) {
        try {
          await tournamentAPI.shuffleGroups(id, currentRound);
        } catch (e) {
          console.warn('Shuffle after configure failed:', e?.message);
        }
      }
      showToast('Round configured successfully!');

      // Fetch groups — backend may take a short moment to persist/create them.
      // Poll a few times so the UI updates immediately without requiring a manual refresh.
      const maxAttempts = 8;
      let attempts = 0;
      let groups = [];
      while (attempts < maxAttempts) {
        // Attempt to fetch groups for the current round
        try {
          const resp = await tournamentAPI.getRoundGroups(id, currentRound);
          groups = resp.data.groups || [];
          if (groups.length > 0) break;
        } catch (e) {
          // swallow transient errors and retry
          console.warn('Transient fetchRoundGroups error, retrying...', e?.message || e);
        }

        // wait 750ms before retrying
        // eslint-disable-next-line no-await-in-loop
        await new Promise((res) => setTimeout(res, 750));
        attempts += 1;
      }

      // Update UI state with whatever we got (possibly empty if nothing created)
      setRoundGroups(groups);
      // Also refresh the full tournament object to update counts/status
      await fetchTournamentData();
      // Auto-open Bulk Schedule so host can set maps/times for this round's matches
      setBulkScheduleRound(currentRound);
      setShowBulkSchedule(true);
    } catch (error) {
      console.error('Error configuring round:', error);
      showToast(error.response?.data?.error || 'Failed to configure round', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Step 2 for 5v5: called when user confirms in inline lobby preview ("Start Round N")
  // Configures lobbies in DB (or skips if already done), then opens Bulk Schedule
  const handleLobbyPreviewConfirm = async () => {
    if (!lobbyPreview) return;
    const roundNumber = lobbyPreview.roundNumber;
    try {
      setLoading(true);

      // Check if groups already exist for this round (created by getRoundResults)
      let groups = [];
      try {
        const existing = await tournamentAPI.getRoundGroups(id, roundNumber);
        groups = existing.data.groups || [];
      } catch (e) {
        /* none yet */
      }

      if (groups.length === 0) {
        // Groups don't exist — create them now
        await tournamentAPI.configureRound(id, roundNumber, {
          teams_per_group: 2,
          qualifying_per_group: lobbyPreview.qualifyingPerGroup,
          matches_per_group: lobbyPreview.bestOf,
        });
        // Poll until they appear
        const maxAttempts = 8;
        let attempts = 0;
        while (attempts < maxAttempts) {
          try {
            const resp = await tournamentAPI.getRoundGroups(id, roundNumber);
            groups = resp.data.groups || [];
            if (groups.length > 0) break;
          } catch (e) {
            /* retry */
          }
          // eslint-disable-next-line no-await-in-loop
          await new Promise((res) => setTimeout(res, 750));
          attempts += 1;
        }
      }

      // Only officially start the round if tournament is already ongoing
      // If tournament is still upcoming, this is pre-configure only — startRound is called later
      // when the host clicks "Start Round N" after tournament goes live
      if (tournament?.status === 'ongoing') {
        try {
          await tournamentAPI.startRound(id, roundNumber);
        } catch (e) {
          // May already be ongoing — ignore
        }
      }

      // Open bulk schedule first (before clearing lobbyPreview) to avoid page flicker
      setBulkScheduleRound(roundNumber);
      setShowBulkSchedule(true);
      setLobbyPreview(null);
      setLoading(false);
      setRoundGroups(groups);
      // Refresh tournament but preserve currentRound
      const tResp = await tournamentAPI.getManageTournament(id);
      setTournament(tResp.data.tournament);
      setRegistrations(tResp.data.registrations || []);
      setCurrentRound(roundNumber);
      showToast('Lobbies ready! Set your schedule below.');
    } catch (error) {
      console.error('Error configuring round:', error);
      showToast(error.response?.data?.error || 'Failed to configure round', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleResetRound = async () => {
    setConfirmModal({
      isOpen: true,
      title: 'Reset Round Configuration',
      message:
        'Are you sure you want to reset this round? This will delete groups and matches so you can reconfigure the round.',
      confirmText: 'Reset Round',
      type: 'danger',
      onConfirm: async () => {
        try {
          setLoading(true);
          await tournamentAPI.resetRound(id, currentRound);
          showToast('Round reset. You may reconfigure now.');
          setRoundGroups([]);
          await fetchTournamentData();
        } catch (error) {
          console.error('Error resetting round:', error);
          showToast(error.response?.data?.error || 'Failed to reset round', 'error');
        } finally {
          setLoading(false);
          setConfirmModal((c) => ({ ...c, isOpen: false }));
        }
      },
    });
  };

  const handleShuffleGroups = () => {
    setConfirmModal({
      isOpen: true,
      title: 'Shuffle Groups',
      message:
        'Teams will be randomly redistributed across groups. Use this to balance competitive matchups before starting the round.',
      confirmText: 'Shuffle',
      type: 'warning',
      onConfirm: async () => {
        try {
          setLoading(true);
          await tournamentAPI.shuffleGroups(id, currentRound);
          showToast('Groups reshuffled successfully!');
          const updated = await tournamentAPI.getRoundGroups(id, currentRound);
          setRoundGroups(updated.data.groups || []);
          setSelectedGroup(null);
        } catch (error) {
          console.error('Error shuffling groups:', error);
          showToast(error.response?.data?.error || 'Failed to shuffle groups', 'error');
        } finally {
          setLoading(false);
          setConfirmModal((c) => ({ ...c, isOpen: false }));
        }
      },
    });
  };

  const handleGroupSelect = (group) => {
    setSelectedGroup(group);
  };

  const handleStartMatch = (match) => {
    setCurrentMatch(match);
    setShowMatchConfigModal(true);
  };

  const handleSaveOnlyMatchConfig = async (credData) => {
    if (!currentMatch) return;
    try {
      setLoading(true);
      const currentGroupId = selectedGroup.id;
      await tournamentAPI.updateMatchCredentials(id, currentMatch.id, credData);
      setShowMatchConfigModal(false);
      setCurrentMatch(null);
      showToast('Credentials saved!');
      const updatedGroups = await tournamentAPI.getRoundGroups(id, currentRound);
      setRoundGroups(updatedGroups.data.groups || []);
      const updatedGroup = updatedGroups.data.groups.find((g) => g.id === currentGroupId);
      if (updatedGroup) setSelectedGroup(updatedGroup);
    } catch (error) {
      console.error('Error saving credentials:', error);
      showToast(error.response?.data?.error || 'Failed to save credentials', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitMatchConfig = async (matchConfigData) => {
    try {
      setLoading(true);
      const currentGroupId = selectedGroup.id;
      await tournamentAPI.startMatch(id, currentGroupId, matchConfigData);
      setShowMatchConfigModal(false);
      setCurrentMatch(null);
      showToast('Match started successfully!');
      // Refresh groups and restore selected group
      const updatedGroups = await tournamentAPI.getRoundGroups(id, currentRound);
      setRoundGroups(updatedGroups.data.groups || []);
      const updatedGroup = updatedGroups.data.groups.find((g) => g.id === currentGroupId);
      if (updatedGroup) {
        setSelectedGroup(updatedGroup);
      }
    } catch (error) {
      console.error('Error starting match:', error);
      showToast('Failed to start match', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEndMatch = (match) => {
    setConfirmModal({
      isOpen: true,
      title: 'End Match',
      message: `Are you sure you want to end Match ${match.match_number}?`,
      confirmText: 'End Match',
      type: 'danger',
      onConfirm: async () => {
        try {
          setLoading(true);
          const currentGroupId = selectedGroup.id;
          await tournamentAPI.endMatch(id, match.id);
          showToast('Match ended successfully!');
          // Refresh groups and restore selected group
          const updatedGroups = await tournamentAPI.getRoundGroups(id, currentRound);
          setRoundGroups(updatedGroups.data.groups || []);
          const updatedGroup = updatedGroups.data.groups.find((g) => g.id === currentGroupId);
          if (updatedGroup) {
            setSelectedGroup(updatedGroup);
          }
        } catch (error) {
          console.error('Error ending match:', error);
          showToast('Failed to end match', 'error');
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const handleEnterPoints = (match) => {
    setCurrentMatch(match);
    setShowMatchPointsModal(true);
  };

  const handleSubmitPoints = async (pointsData) => {
    try {
      setLoading(true);
      const currentGroupId = selectedGroup.id;
      await tournamentAPI.submitMatchScores(id, currentMatch.id, pointsData);
      setShowMatchPointsModal(false);
      setCurrentMatch(null);
      showToast('Points submitted successfully!');
      // Refresh groups and restore selected group
      const updatedGroups = await tournamentAPI.getRoundGroups(id, currentRound);
      setRoundGroups(updatedGroups.data.groups || []);
      const updatedGroup = updatedGroups.data.groups.find((g) => g.id === currentGroupId);
      if (updatedGroup) {
        setSelectedGroup(updatedGroup);
      }
    } catch (error) {
      console.error('Error submitting points:', error);
      const errorMsg = error.response?.data?.error || 'Failed to submit points';
      showToast(errorMsg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleAdvanceRound = async (overrideRound) => {
    try {
      setLoading(true);
      const response = await tournamentAPI.getRoundResults(id, overrideRound ?? currentRound);
      const data = response.data;

      if (data.is_final_round) {
        // Final round completed - show winner
        const winnerName = data.winner?.team_name || 'Unknown';
        showToast(
          `Tournament Complete!\n\nWinner: ${winnerName}\n\nTotal Points: ${data.winner?.total_points || 0}`
        );
        await fetchTournamentData();
      } else if (tournament?.is_5v5) {
        // 5v5: skip eliminated modal, go straight to lobby preview for next round
        const nextRound = data.next_round;

        // Refresh tournament state
        const tResp = await tournamentAPI.getManageTournament(id);
        setTournament(tResp.data.tournament);
        setRegistrations(tResp.data.registrations || []);
        setCurrentRound(nextRound);
        setSelectedGroup(null);
        setRoundGroups([]);

        // Build qualified teams list from data.groups[].qualified_teams (already in response)
        const qualifiedTeams = (data.groups || [])
          .flatMap((g) => g.qualified_teams || [])
          .map((t) => ({ id: t.team_id, team_name: t.team_name }));

        const pairs = [];
        for (let i = 0; i < qualifiedTeams.length - 1; i += 2) {
          pairs.push({
            id: `lobby-${i / 2 + 1}`,
            teams: [qualifiedTeams[i], qualifiedTeams[i + 1]],
          });
        }
        if (qualifiedTeams.length % 2 !== 0) {
          pairs.push({ id: `lobby-bye`, teams: [qualifiedTeams[qualifiedTeams.length - 1]] });
        }

        setLobbyPreview({
          lobbies: pairs,
          bestOf: 1,
          qualifyingPerGroup: 1,
          roundNumber: nextRound,
          fromAdvance: true,
        });
      } else {
        // BR mode - show eliminated teams modal
        await fetchTournamentData();
        setEliminatedData(data);
        setShowEliminatedModal(true);
      }
    } catch (error) {
      console.error('Error advancing round:', error);
      const errorMsg = error.response?.data?.error || 'Failed to advance round';
      showToast(errorMsg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleProceedToNextRound = () => {
    setShowEliminatedModal(false);
    const nextRound = eliminatedData.next_round;
    // Save current round's groups before moving on so Round Results can show them
    if (roundGroups.length > 0) {
      setPastRoundsGroups((prev) => ({ ...prev, [currentRound]: roundGroups }));
    }
    setCurrentRound(nextRound);
    setSelectedGroup(null);
    setRoundGroups([]);
    // Don't auto-open RoundConfigModal — host can now use Bulk Schedule
    // to pre-set maps/times, then click "Start Round N" when ready.
  };

  // Fetch groups for all past rounds on load so Round Results shows correct data.
  // This handles page refreshes where pastRoundsGroups state starts empty.
  useEffect(() => {
    if (!id || currentRound <= 1) return;
    for (let r = 1; r < currentRound; r++) {
      tournamentAPI
        .getRoundGroups(id, r)
        .then((res) => {
          const groups = res.data?.groups || [];
          if (groups.length > 0) {
            setPastRoundsGroups((prev) => ({ ...prev, [r]: groups }));
          }
        })
        .catch(() => {}); // silently ignore — completed rounds always have groups
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, currentRound]);

  // Load groups when round changes.
  // Also handles upcoming tournaments (currentRound=0) — always check if round 1 was pre-configured
  // so the host sees the green badge + Reset button when they come back to the page.
  useEffect(() => {
    if (!tournament?.use_groups_system) return;

    if (tournament?.status === 'upcoming' && currentRound === 0) {
      // Silently probe round 1 for pre-configured groups
      tournamentAPI
        .getRoundGroups(tournament.id, 1)
        .then((res) => {
          const groups = res.data?.groups || [];
          setRoundGroups(groups);
          if (groups.length > 0) setSelectedGroup(groups[0]);
        })
        .catch(() => {
          setRoundGroups([]);
          setSelectedGroup(null);
        });
      return;
    }

    if (currentRound > 0) {
      const roundStatus = getRoundStatus(currentRound);
      if (roundStatus === 'completed') {
        // Auto-load completed rounds (for round navigation / history)
        fetchRoundGroups(currentRound);
      }
      // For 'ongoing' rounds: groups are only loaded after host explicitly clicks "Start Round"
      // via handleStartRound — not auto-loaded on page mount, so host always sees the
      // "Ready to Start" screen first when navigating to an ongoing tournament.
    }
  }, [
    currentRound,
    tournament?.use_groups_system,
    tournament?.status,
    tournament?.id,
    getRoundStatus,
    fetchRoundGroups,
  ]);

  // Close slot list dropdown on outside click
  useEffect(() => {
    if (!showSlotListDropdown) return;
    const handleOutside = (e) => {
      if (slotListDropdownRef.current && !slotListDropdownRef.current.contains(e.target)) {
        setShowSlotListDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [showSlotListDropdown]);

  // Close IGN dropdown on outside click
  useEffect(() => {
    if (!showIGNDropdown) return;
    const handleOutside = (e) => {
      if (ignDropdownRef.current && !ignDropdownRef.current.contains(e.target)) {
        setShowIGNDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [showIGNDropdown]);

  const handleSaveLiveUrl = async () => {
    try {
      await tournamentAPI.updateTournamentFields(id, { live_link: tempLiveUrl || '' });
      setTournament((prev) => ({ ...prev, live_link: tempLiveUrl || null }));
      setShowLiveUrlDialog(false);
      showToast(tempLiveUrl ? 'Live stream published!' : 'Live stream removed');
    } catch (error) {
      showToast('Failed to update live stream', 'error');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-transparent flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[hsl(var(--accent))]"></div>
      </div>
    );
  }

  if (!tournament) {
    return (
      <div className="min-h-screen bg-transparent flex items-center justify-center">
        <p className="text-gray-400">{getEventLabel()} not found</p>
      </div>
    );
  }

  const currentRoundConfig = tournament.rounds.find((r) => r.round === tournament.current_round);
  // For display: use qualifying_teams if set, otherwise max_teams
  const qualifyingTeams = Number(currentRoundConfig?.qualifying_teams) || 0;
  const maxTeams = Number(currentRoundConfig?.max_teams) || 0;
  const _qualifyingLimit = qualifyingTeams > 0 ? qualifyingTeams : maxTeams; // eslint-disable-line no-unused-vars

  // Check if current round is final round (last round, no qualifying_teams or qualifying_teams = 0)
  const _isCurrentRoundFinal = // eslint-disable-line no-unused-vars
    currentRoundConfig &&
    tournament.rounds.length > 0 &&
    tournament.current_round === tournament.rounds[tournament.rounds.length - 1].round &&
    (!currentRoundConfig.qualifying_teams || Number(currentRoundConfig.qualifying_teams) === 0);

  const currentRoundName =
    roundNames?.[String(currentRound)] || (currentRound > 0 ? `Round ${currentRound}` : null);

  // ── Slot List: shared canvas builder ────────────────────────────────────
  const buildSlotListCanvas = (groupsOnPage) => {
    const W = 1080;
    const PAD = 24;
    const COLS = Math.min(3, groupsOnPage.length);
    const GROUP_W = COLS === 1 ? W - PAD * 2 : (W - PAD * (COLS + 1)) / COLS;
    const ROW_H = 32;
    const GROUP_TITLE_H = 46;
    const GROUP_GAP = 16;
    const HEADER_H = 165;
    const FOOTER_H = 62;

    const numGroupRows = Math.ceil(groupsOnPage.length / COLS);
    const maxTeamsInRow = (ri) => {
      const slice = groupsOnPage.slice(ri * COLS, (ri + 1) * COLS);
      return Math.max(...slice.map((g) => g.teams.length));
    };
    let totalGroupsH = 0;
    for (let r = 0; r < numGroupRows; r++) {
      totalGroupsH += GROUP_TITLE_H + maxTeamsInRow(r) * ROW_H + GROUP_GAP;
    }

    const canvas = document.createElement('canvas');
    canvas.width = W;
    canvas.height = HEADER_H + totalGroupsH + FOOTER_H;
    const ctx = canvas.getContext('2d');
    const H = canvas.height;

    // Background
    ctx.fillStyle = '#0d0d1a';
    ctx.fillRect(0, 0, W, H);

    // Dot grid
    ctx.fillStyle = 'rgba(139,92,246,0.06)';
    for (let gx = 0; gx < W; gx += 36) {
      for (let gy = 0; gy < H; gy += 36) {
        ctx.beginPath();
        ctx.arc(gx, gy, 1, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    // Top accent line
    const topGrad = ctx.createLinearGradient(0, 0, W, 0);
    topGrad.addColorStop(0, 'rgba(139,92,246,0)');
    topGrad.addColorStop(0.35, 'rgba(139,92,246,0.9)');
    topGrad.addColorStop(0.65, 'rgba(139,92,246,0.9)');
    topGrad.addColorStop(1, 'rgba(139,92,246,0)');
    ctx.fillStyle = topGrad;
    ctx.fillRect(0, 0, W, 3);

    // Watermark
    ctx.fillStyle = 'rgba(139,92,246,0.35)';
    ctx.font = 'bold 11px Inter, sans-serif';
    ctx.textAlign = 'right';
    ctx.fillText('SCRIMVERSE', W - PAD, 22);

    // Title
    ctx.fillStyle = '#f1f5f9';
    ctx.font = 'bold 28px Outfit, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(tournament.title.toUpperCase(), W / 2, 62);

    // Round label
    ctx.fillStyle = '#8b5cf6';
    ctx.font = 'bold 15px Outfit, sans-serif';
    ctx.fillText(
      `${(currentRoundName || `Round ${currentRound}`).toUpperCase()} — SLOT LIST`,
      W / 2,
      92
    );

    // Date
    ctx.fillStyle = '#475569';
    ctx.font = '12px Inter, sans-serif';
    ctx.fillText(
      new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }),
      W / 2,
      114
    );

    // Separator
    const sepGrad = ctx.createLinearGradient(PAD, 0, W - PAD, 0);
    sepGrad.addColorStop(0, 'rgba(139,92,246,0)');
    sepGrad.addColorStop(0.3, 'rgba(139,92,246,0.4)');
    sepGrad.addColorStop(0.7, 'rgba(139,92,246,0.4)');
    sepGrad.addColorStop(1, 'rgba(139,92,246,0)');
    ctx.strokeStyle = sepGrad;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(PAD, 136);
    ctx.lineTo(W - PAD, 136);
    ctx.stroke();

    // Draw groups
    let groupRowY = HEADER_H;
    for (let rowIdx = 0; rowIdx < numGroupRows; rowIdx++) {
      const groupsInRow = groupsOnPage.slice(rowIdx * COLS, (rowIdx + 1) * COLS);
      const maxTeams = maxTeamsInRow(rowIdx);
      const rowStartY = groupRowY;

      groupsInRow.forEach((group, colIdx) => {
        const x = PAD + colIdx * (GROUP_W + PAD);
        const y = rowStartY;

        // Header bg
        ctx.fillStyle = 'rgba(139,92,246,0.12)';
        ctx.fillRect(x, y, GROUP_W, GROUP_TITLE_H);

        // Left accent bar
        ctx.fillStyle = '#8b5cf6';
        ctx.fillRect(x, y, 3, GROUP_TITLE_H);

        // Group name
        ctx.fillStyle = '#e2e8f0';
        ctx.font = 'bold 13px Outfit, sans-serif';
        ctx.textAlign = 'left';
        ctx.fillText(group.group_name.toUpperCase(), x + 14, y + 28);

        // Team count
        ctx.fillStyle = '#7c3aed';
        ctx.font = '11px Inter, sans-serif';
        ctx.textAlign = 'right';
        ctx.fillText(`${group.teams.length} TEAMS`, x + GROUP_W - 10, y + 28);

        // Team rows
        group.teams.forEach((team, teamIdx) => {
          const ry = y + GROUP_TITLE_H + teamIdx * ROW_H;
          const slotNum = String(teamIdx + 3).padStart(2, '0');

          if (teamIdx % 2 === 0) {
            ctx.fillStyle = 'rgba(255,255,255,0.025)';
            ctx.fillRect(x, ry, GROUP_W, ROW_H);
          }

          // Slot number
          ctx.fillStyle = '#8b5cf6';
          ctx.font = 'bold 11px Courier New, monospace';
          ctx.textAlign = 'left';
          ctx.fillText(slotNum, x + 10, ry + 21);

          // Divider
          ctx.strokeStyle = 'rgba(139,92,246,0.2)';
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(x + 30, ry + 7);
          ctx.lineTo(x + 30, ry + ROW_H - 7);
          ctx.stroke();

          // Team name
          ctx.fillStyle = '#cbd5e1';
          ctx.font = '11px Outfit, sans-serif';
          ctx.textAlign = 'left';
          const maxNameW = GROUP_W - 46;
          let name = team.team_name;
          while (ctx.measureText(name).width > maxNameW && name.length > 3) {
            name = name.slice(0, -1);
          }
          if (name !== team.team_name) name += '…';
          ctx.fillText(name, x + 38, ry + 21);
        });

        // Outer border
        ctx.strokeStyle = 'rgba(139,92,246,0.15)';
        ctx.lineWidth = 1;
        ctx.strokeRect(x, y, GROUP_W, GROUP_TITLE_H + group.teams.length * ROW_H);
      });

      groupRowY += GROUP_TITLE_H + maxTeams * ROW_H + GROUP_GAP;
    }

    // Footer
    ctx.fillStyle = 'rgba(139,92,246,0.07)';
    ctx.fillRect(0, H - FOOTER_H, W, FOOTER_H);

    const flGrad = ctx.createLinearGradient(PAD, 0, W - PAD, 0);
    flGrad.addColorStop(0, 'rgba(139,92,246,0)');
    flGrad.addColorStop(0.4, 'rgba(139,92,246,0.3)');
    flGrad.addColorStop(0.6, 'rgba(139,92,246,0.3)');
    flGrad.addColorStop(1, 'rgba(139,92,246,0)');
    ctx.strokeStyle = flGrad;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(PAD, H - FOOTER_H);
    ctx.lineTo(W - PAD, H - FOOTER_H);
    ctx.stroke();

    ctx.fillStyle = '#8b5cf6';
    ctx.font = 'bold 13px Outfit, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('SCRIMVERSE', W / 2, H - 26);

    ctx.fillStyle = '#4b5563';
    ctx.font = '11px Inter, sans-serif';
    ctx.fillText('scrimverse.com', W / 2, H - 10);

    return canvas;
  };

  // ── Download Slot List as PNG (multiple files if > 6 groups) ────────────
  const handleDownloadSlotListPNG = () => {
    const groups = roundGroups.filter((g) => selectedExportGroups.has(g.id));
    if (groups.length === 0) {
      showToast('Select at least one group', 'error');
      return;
    }
    setIsGeneratingSlotList(true);
    try {
      const GROUPS_PER_PAGE = 6;
      const totalPages = Math.ceil(groups.length / GROUPS_PER_PAGE);
      const roundLabel = (currentRoundName || `Round ${currentRound}`)
        .replace(/\s+/g, '_')
        .toUpperCase();
      const titleLabel = tournament.title.replace(/[^a-zA-Z0-9]/g, '_');

      for (let p = 0; p < totalPages; p++) {
        const pageGroups = groups.slice(p * GROUPS_PER_PAGE, (p + 1) * GROUPS_PER_PAGE);
        const canvas = buildSlotListCanvas(pageGroups);
        const link = document.createElement('a');
        const suffix = totalPages > 1 ? `_${p + 1}of${totalPages}` : '';
        link.download = `${titleLabel}_${roundLabel}_SlotList${suffix}.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
      }
    } catch (err) {
      console.error('Slot list PNG failed:', err);
      showToast('Failed to generate slot list PNG', 'error');
    } finally {
      setIsGeneratingSlotList(false);
    }
  };

  // ── Download Slot List as PDF (all pages in one file) ───────────────────
  const handleDownloadSlotListPDF = async () => {
    const groups = roundGroups.filter((g) => selectedExportGroups.has(g.id));
    if (groups.length === 0) {
      showToast('Select at least one group', 'error');
      return;
    }
    setIsGeneratingSlotList(true);
    try {
      const { jsPDF } = await import('jspdf');
      const GROUPS_PER_PAGE = 6;
      const PT = 72 / 96; // px → pt
      let pdf = null;

      const totalPages = Math.ceil(groups.length / GROUPS_PER_PAGE);
      for (let p = 0; p < totalPages; p++) {
        const pageGroups = groups.slice(p * GROUPS_PER_PAGE, (p + 1) * GROUPS_PER_PAGE);
        const canvas = buildSlotListCanvas(pageGroups);
        const W = canvas.width;
        const H = canvas.height;

        if (!pdf) {
          pdf = new jsPDF({ unit: 'pt', format: [W * PT, H * PT] });
        } else {
          pdf.addPage([W * PT, H * PT]);
        }
        pdf.addImage(canvas.toDataURL('image/jpeg', 0.92), 'JPEG', 0, 0, W * PT, H * PT);
      }

      const roundLabel = (currentRoundName || `Round ${currentRound}`)
        .replace(/\s+/g, '_')
        .toUpperCase();
      const titleLabel = tournament.title.replace(/[^a-zA-Z0-9]/g, '_');
      pdf.save(`${titleLabel}_${roundLabel}_SlotList.pdf`);
    } catch (err) {
      console.error('Slot list PDF failed:', err);
      showToast('Failed to generate slot list PDF', 'error');
    } finally {
      setIsGeneratingSlotList(false);
    }
  };

  // ── Shared IGN extraction (used by CSV + PDF) ────────────────────────────
  const extractIGNs = (team) => {
    const reg = registrations.find((r) => r.id === team.id);
    const ignSubs = reg?.ign_submissions || {};
    const members = reg?.team_members || [];
    const captain = members.find((m) => m.is_captain) || members[0];
    const captainKey = captain?.username || captain?.player_name || null;
    const captainIGN = captainKey ? ignSubs[captainKey] || '' : '';
    const otherIGNs = Object.entries(ignSubs)
      .filter(([k]) => k !== captainKey)
      .map(([, v]) => v);
    return captainIGN
      ? [captainIGN, otherIGNs[0] || '', otherIGNs[1] || '', otherIGNs[2] || '']
      : [otherIGNs[0] || '', otherIGNs[1] || '', otherIGNs[2] || '', otherIGNs[3] || ''];
  };

  // ── Export IGNs as PDF (clean table, A4 landscape) ──────────────────────
  const handleExportIGNsPDF = async () => {
    if (!roundGroups || roundGroups.length === 0) {
      showToast('No groups configured for the current round', 'error');
      return;
    }
    try {
      const { jsPDF } = await import('jspdf');

      const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const PW = 297;
      const PH = 210;
      const ML = 10;
      const MR = 10;
      const MT = 10;
      const usableW = PW - ML - MR;

      // Column widths (mm)
      const cGroup = 22;
      const cSlot = 12;
      const cTeam = 52;
      const cIGN = (usableW - cGroup - cSlot - cTeam - 6) / 4; // ~51mm each
      const xGroup = ML;
      const xSlot = xGroup + cGroup + 2;
      const xTeam = xSlot + cSlot + 2;
      const xP1 = xTeam + cTeam + 2;
      const xP2 = xP1 + cIGN;
      const xP3 = xP2 + cIGN;
      const xP4 = xP3 + cIGN;

      const ROW_H = 6;
      const TH_H = 8;
      const GH = 7;

      const roundLabel = (currentRoundName || `Round ${currentRound}`)
        .replace(/\s+/g, '_')
        .toUpperCase();
      const titleLabel = tournament.title.replace(/[^a-zA-Z0-9]/g, '_');
      const roundDisplay = (currentRoundName || `Round ${currentRound}`).toUpperCase();

      let y = MT;

      const drawTableHeader = () => {
        pdf.setFillColor(45, 25, 95);
        pdf.rect(ML, y, usableW, TH_H, 'F');
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(7);
        pdf.setTextColor(200, 180, 255);
        const cols = [
          ['Group', xGroup],
          ['Slot', xSlot],
          ['Team Name', xTeam],
          ['Player 1 IGN', xP1],
          ['Player 2 IGN', xP2],
          ['Player 3 IGN', xP3],
          ['Player 4 IGN', xP4],
        ];
        cols.forEach(([label, x]) => pdf.text(label, x + 1, y + TH_H / 2 + 1.5));
        y += TH_H;
      };

      const checkPage = (needed) => {
        if (y + needed > PH - 8) {
          pdf.addPage();
          y = MT;
          // Repeat header
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(8);
          pdf.setTextColor(80, 80, 80);
          pdf.text(`${tournament.title}  —  ${roundDisplay}  (continued)`, ML, y + 4);
          y += 8;
          drawTableHeader();
        }
      };

      // Page header
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(13);
      pdf.setTextColor(20, 20, 20);
      pdf.text(tournament.title.toUpperCase(), PW / 2, y + 5, { align: 'center' });
      y += 7;
      pdf.setFontSize(8);
      pdf.setTextColor(100, 60, 200);
      pdf.text(`${roundDisplay} — IGN LIST`, PW / 2, y + 3, { align: 'center' });
      y += 5;
      pdf.setFontSize(7);
      pdf.setTextColor(130, 130, 130);
      pdf.text(
        new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }),
        PW / 2,
        y + 3,
        { align: 'center' }
      );
      y += 6;
      pdf.setDrawColor(140, 80, 240);
      pdf.setLineWidth(0.3);
      pdf.line(ML, y, PW - MR, y);
      y += 4;

      drawTableHeader();

      let altRow = 0;
      roundGroups.forEach((group) => {
        checkPage(GH);
        pdf.setFillColor(230, 220, 255);
        pdf.rect(ML, y, usableW, GH, 'F');
        pdf.setDrawColor(160, 100, 255);
        pdf.setLineWidth(0.6);
        pdf.line(ML, y, ML, y + GH);
        pdf.setLineWidth(0.1);
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(7.5);
        pdf.setTextColor(60, 20, 140);
        pdf.text(
          `${group.group_name.toUpperCase()}   •   ${group.teams.length} TEAMS`,
          ML + 3,
          y + GH / 2 + 1.5
        );
        y += GH;

        group.teams.forEach((team, idx) => {
          checkPage(ROW_H);
          const slotNum = String(idx + 3).padStart(2, '0');
          const igns = extractIGNs(team);

          if (altRow % 2 === 0) {
            pdf.setFillColor(248, 248, 252);
            pdf.rect(ML, y, usableW, ROW_H, 'F');
          }
          pdf.setDrawColor(220, 220, 235);
          pdf.setLineWidth(0.1);
          pdf.line(ML, y + ROW_H, ML + usableW, y + ROW_H);

          const ty = y + ROW_H / 2 + 1.5;

          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(6);
          pdf.setTextColor(140, 140, 160);
          pdf.text(group.group_name, xGroup + 1, ty);

          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(7);
          pdf.setTextColor(100, 50, 200);
          pdf.text(slotNum, xSlot + 1, ty);

          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(7);
          pdf.setTextColor(20, 20, 20);
          const teamTrunc =
            team.team_name.length > 26 ? team.team_name.slice(0, 25) + '…' : team.team_name;
          pdf.text(teamTrunc, xTeam + 1, ty);

          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(6.5);
          [
            [xP1, igns[0]],
            [xP2, igns[1]],
            [xP3, igns[2]],
            [xP4, igns[3]],
          ].forEach(([x, ign]) => {
            if (ign) {
              pdf.setTextColor(10, 130, 70);
              const t = ign.length > 22 ? ign.slice(0, 21) + '…' : ign;
              pdf.text(t, x + 1, ty);
            } else {
              pdf.setTextColor(190, 190, 200);
              pdf.text('—', x + 1, ty);
            }
          });

          y += ROW_H;
          altRow++;
        });
      });

      // Page numbers
      const total = pdf.getNumberOfPages();
      for (let i = 1; i <= total; i++) {
        pdf.setPage(i);
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(6.5);
        pdf.setTextColor(160, 160, 160);
        pdf.text(`Page ${i} of ${total}  •  SCRIMVERSE`, PW / 2, PH - 4, { align: 'center' });
      }

      pdf.save(`${titleLabel}_${roundLabel}_IGNs.pdf`);
    } catch (err) {
      console.error('IGN PDF failed:', err);
      showToast('Failed to generate IGN PDF', 'error');
    }
  };

  // ── Export IGNs as CSV ───────────────────────────────────────────────────
  const handleExportIGNs = () => {
    if (!roundGroups || roundGroups.length === 0) {
      showToast('No groups configured for the current round', 'error');
      return;
    }

    const rows = [
      [
        'Group',
        'Slot',
        'Team Name',
        'Player 1 IGN',
        'Player 2 IGN',
        'Player 3 IGN',
        'Player 4 IGN',
      ],
    ];

    roundGroups.forEach((group) => {
      group.teams.forEach((team, idx) => {
        const slotNum = String(idx + 3).padStart(2, '0');
        const igns = extractIGNs(team);
        rows.push([group.group_name, slotNum, team.team_name, igns[0], igns[1], igns[2], igns[3]]);
      });
      rows.push(['', '', '', '', '', '', '']); // blank row between groups
    });

    const csvContent = rows
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const blob = new Blob(['﻿' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const roundLabel = (currentRoundName || `Round ${currentRound}`)
      .replace(/\s+/g, '_')
      .toUpperCase();
    const titleLabel = tournament.title.replace(/[^a-zA-Z0-9]/g, '_');
    const link = document.createElement('a');
    link.download = `${titleLabel}_${roundLabel}_IGNs.csv`;
    link.href = URL.createObjectURL(blob);
    link.click();
    URL.revokeObjectURL(link.href);
  };

  // 5v5 Lobby Preview — renders inline, replacing the full page (sidebar still visible via HostDashboard)
  if (lobbyPreview) {
    return (
      <div className="min-h-screen bg-transparent manage-tournament-container">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Lobby5v5PreviewModal
            isOpen={true}
            onClose={() => {
              setLobbyPreview(null);
              if (!lobbyPreview?.fromAdvance) {
                setShowRoundConfigModal(true);
              }
            }}
            onConfirm={handleLobbyPreviewConfirm}
            lobbies={lobbyPreview.lobbies}
            bestOf={lobbyPreview.bestOf}
            totalTeams={lobbyPreview.lobbies.reduce((acc, l) => acc + l.teams.length, 0)}
            loading={loading}
            roundNumber={lobbyPreview.roundNumber}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-transparent manage-tournament-container">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Back button — own row */}
        <button
          onClick={() => (onBack ? onBack() : navigate('/host/dashboard'))}
          className="mt-back-btn mb-4"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>Back</span>
        </button>

        {/* Header */}
        <div className="mt-header">
          <div className="mt-header-top">
            <div className="mt-title-group">
              <h1 className="mt-title">{tournament.title}</h1>
              {tournament?.is_5v5 && tournament.status === 'ongoing' ? (
                <p className="mt-subtitle flex items-center gap-2 flex-wrap">
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-accent/15 text-accent border border-accent/30">
                    {tournament.game} • 5v5
                  </span>
                  <span className="text-muted-foreground text-xs">
                    {currentRoundName || `Round ${currentRound}`} • {roundGroups.length} Lobbies
                  </span>
                </p>
              ) : (
                <p className="mt-subtitle">Tournament Management</p>
              )}
            </div>

            {/* Round badge — only for non-5v5 ongoing */}
            {tournament.status === 'ongoing' && currentRoundName && !tournament?.is_5v5 && (
              <span className="mt-round-badge">
                <Zap className="h-3 w-3" />
                {currentRoundName}
              </span>
            )}

            {/* Generate Next Round — 5v5 only, top-right header, when all lobbies complete */}
            {tournament?.is_5v5 &&
              tournament.status === 'ongoing' &&
              roundGroups.length > 0 &&
              roundGroups.every((g) => g.status === 'completed') &&
              (() => {
                const isFinalRound =
                  tournament.rounds?.findIndex((r) => r.round === currentRound) ===
                  (tournament.rounds?.length ?? 0) - 1;
                const roundAtRender = currentRound;
                return (
                  <button
                    onClick={() => handleAdvanceRound(roundAtRender)}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg text-white bg-green-600 hover:bg-green-700 transition-colors shadow-sm"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                      />
                    </svg>
                    {isFinalRound ? 'End Tournament' : 'Generate Next Round'}
                  </button>
                );
              })()}
          </div>

          {/* Action buttons */}
          <div className="mt-actions-bar">
            <button
              onClick={() => navigate(`/tournaments/${id}`, { state: { fromManage: true } })}
              className="mt-action-btn"
            >
              <Eye className="h-3.5 w-3.5" />
              View Details
            </button>
            <button
              onClick={() => setShowPointsTable(true)}
              className="mt-action-btn mt-action-btn-purple"
            >
              <BarChart3 className="h-3.5 w-3.5" />
              Points Table
            </button>
            {tournament?.is_5v5 && (
              <button
                onClick={() => {
                  // Open match config for the first pending match across all groups
                  const firstPending = roundGroups.find((g) =>
                    g.matches?.some((m) => m.status !== 'completed')
                  );
                  if (firstPending) {
                    setSelectedGroup(firstPending);
                    const match = firstPending.matches?.find((m) => m.status !== 'completed');
                    if (match) handleStartMatch(match);
                  }
                }}
                className="mt-action-btn"
              >
                <Settings className="h-3.5 w-3.5" />
                Match IDP
              </button>
            )}
            <button
              onClick={() => {
                setBulkScheduleRound(null);
                setShowBulkSchedule(true);
              }}
              className="mt-action-btn mt-action-btn-green"
            >
              <Calendar className="h-3.5 w-3.5" />
              Bulk Schedule
            </button>
            {roundGroups.length > 0 && (
              <>
                {/* Slot List dropdown */}
                <div className="relative" ref={slotListDropdownRef}>
                  <button
                    onClick={() => {
                      if (!showSlotListDropdown && selectedExportGroups.size === 0) {
                        setSelectedExportGroups(new Set(roundGroups.map((g) => g.id)));
                      }
                      setShowSlotListDropdown((v) => !v);
                    }}
                    className="mt-action-btn"
                  >
                    <Download className="h-3.5 w-3.5" />
                    Slot List
                    <ChevronDown className="h-3 w-3 ml-0.5" />
                  </button>

                  {showSlotListDropdown && (
                    <div
                      className="absolute top-full left-0 mt-1 z-50 rounded-lg shadow-xl p-3 min-w-[190px]"
                      style={{
                        background: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                      }}
                    >
                      {/* Select All */}
                      <label
                        className="flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer text-xs font-semibold hover:bg-accent/10 transition-colors"
                        style={{ color: 'hsl(var(--foreground))' }}
                      >
                        <input
                          type="checkbox"
                          checked={selectedExportGroups.size === roundGroups.length}
                          onChange={(e) =>
                            setSelectedExportGroups(
                              e.target.checked ? new Set(roundGroups.map((g) => g.id)) : new Set()
                            )
                          }
                          className="accent-accent"
                        />
                        Select All
                      </label>

                      <div
                        className="my-1.5"
                        style={{ borderTop: '1px solid hsl(var(--border))' }}
                      />

                      {/* Group list */}
                      <div className="max-h-48 overflow-y-auto space-y-0.5">
                        {roundGroups.map((group) => (
                          <label
                            key={group.id}
                            className="flex items-center gap-2 px-2 py-1 rounded cursor-pointer text-xs hover:bg-accent/10 transition-colors"
                            style={{ color: 'hsl(var(--muted-foreground))' }}
                          >
                            <input
                              type="checkbox"
                              checked={selectedExportGroups.has(group.id)}
                              onChange={(e) => {
                                setSelectedExportGroups((prev) => {
                                  const next = new Set(prev);
                                  if (e.target.checked) next.add(group.id);
                                  else next.delete(group.id);
                                  return next;
                                });
                              }}
                              className="accent-accent"
                            />
                            {group.group_name}
                          </label>
                        ))}
                      </div>

                      <div
                        className="mt-2 pt-2 flex gap-2"
                        style={{ borderTop: '1px solid hsl(var(--border))' }}
                      >
                        <button
                          onClick={() => {
                            handleDownloadSlotListPNG();
                            setShowSlotListDropdown(false);
                          }}
                          disabled={selectedExportGroups.size === 0 || isGeneratingSlotList}
                          className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded text-xs font-semibold transition-colors disabled:opacity-50"
                          style={{
                            background: 'hsl(var(--accent)/0.15)',
                            color: 'hsl(var(--accent))',
                          }}
                        >
                          <Download className="h-3 w-3" />
                          PNG
                        </button>
                        <button
                          onClick={() => {
                            handleDownloadSlotListPDF();
                            setShowSlotListDropdown(false);
                          }}
                          disabled={selectedExportGroups.size === 0 || isGeneratingSlotList}
                          className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded text-xs font-semibold transition-colors disabled:opacity-50"
                          style={{
                            background: 'hsl(var(--accent)/0.15)',
                            color: 'hsl(var(--accent))',
                          }}
                        >
                          <Download className="h-3 w-3" />
                          PDF
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Export IGNs dropdown */}
                <div className="relative" ref={ignDropdownRef}>
                  <button onClick={() => setShowIGNDropdown((v) => !v)} className="mt-action-btn">
                    <Download className="h-3.5 w-3.5" />
                    Export IGNs
                    <ChevronDown className="h-3 w-3 ml-0.5" />
                  </button>

                  {showIGNDropdown && (
                    <div
                      className="absolute top-full left-0 mt-1 z-50 rounded-lg shadow-xl p-2 min-w-[120px]"
                      style={{
                        background: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                      }}
                    >
                      <button
                        onClick={() => {
                          handleExportIGNs();
                          setShowIGNDropdown(false);
                        }}
                        className="flex w-full items-center gap-2 px-3 py-2 rounded text-xs font-semibold hover:bg-accent/10 transition-colors"
                        style={{ color: 'hsl(var(--foreground))' }}
                      >
                        <Download className="h-3 w-3" />
                        CSV
                      </button>
                      <button
                        onClick={() => {
                          handleExportIGNsPDF();
                          setShowIGNDropdown(false);
                        }}
                        className="flex w-full items-center gap-2 px-3 py-2 rounded text-xs font-semibold hover:bg-accent/10 transition-colors"
                        style={{ color: 'hsl(var(--foreground))' }}
                      >
                        <Download className="h-3 w-3" />
                        PDF
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
            <button
              onClick={() => {
                setTempLiveUrl(tournament.live_link || '');
                setShowLiveUrlDialog(true);
              }}
              className={`mt-action-btn mt-action-btn-red`}
            >
              <Video className="h-3.5 w-3.5" />
              {tournament.live_link ? 'Live Active' : 'Live Stream'}
            </button>
            <button onClick={() => setShowOverviewDialog(true)} className="mt-action-btn">
              <Info className="h-3.5 w-3.5" />
              Overview
            </button>
            <button
              onClick={handleEditToggle}
              className={`mt-action-btn ${isEditing ? 'active' : ''}`}
            >
              <Settings className="h-3.5 w-3.5" />
              {isEditing ? 'Cancel Edit' : 'Edit'}
            </button>
          </div>
        </div>

        {/* Live Stream Banner */}
        {tournament.live_link && (
          <div
            className="mt-cyber-card p-3 sm:p-4 mb-4"
            style={{ borderColor: 'hsl(0 84% 60% / 0.3)', background: 'hsl(0 84% 60% / 0.05)' }}
          >
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-red-500 flex items-center justify-center flex-shrink-0">
                  <Video className="h-4 w-4 text-white" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-red-400 flex items-center gap-2">
                    <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                    Live Stream Active
                  </p>
                  <p className="text-xs text-[hsl(var(--muted-foreground))] truncate max-w-[200px] sm:max-w-md">
                    {tournament.live_link}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 w-full sm:w-auto">
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(tournament.live_link);
                    showToast('Live URL copied!');
                  }}
                  className="mt-action-btn flex-1 sm:flex-none text-red-400 border-red-500/30 hover:bg-red-500/10"
                >
                  <Copy className="h-3 w-3" />
                  Copy
                </button>
                <button
                  onClick={() => window.open(tournament.live_link, '_blank')}
                  className="flex-1 sm:flex-none px-3 py-1.5 rounded-lg text-xs font-medium bg-red-600 hover:bg-red-700 text-white transition-colors flex items-center gap-1.5 justify-center"
                >
                  <ExternalLink className="h-3 w-3" />
                  Watch
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="w-full mx-auto space-y-8">
          {/* Edit/View Tournament Section */}
          {isEditing && (
            <div className="info-grid-card rounded-xl p-6 mb-6">
              <div className="flex items-center gap-3 mb-6">
                <div>
                  <h2 className="text-lg font-bold text-[hsl(var(--foreground))]">
                    {tournament.status === 'upcoming' ? 'Edit Tournament' : 'Tournament Details'}
                  </h2>
                  <p className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">
                    {tournament.status === 'upcoming'
                      ? 'Update the core details of your tournament'
                      : 'View tournament information (editing locked after start)'}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                <div className="space-y-4">
                  {/* Combined Round Structure */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-xs text-[hsl(var(--muted-foreground))] font-medium">
                        Round Structure
                      </label>
                      {tournament.status === 'upcoming' && (
                        <div className="flex items-center gap-2">
                          {(editData.rounds || tournament.rounds?.length || 1) < 6 && (
                            <button
                              type="button"
                              onClick={() =>
                                setEditData((prev) => ({
                                  ...prev,
                                  rounds: Math.min(
                                    6,
                                    (prev.rounds || tournament.rounds?.length || 1) + 1
                                  ),
                                }))
                              }
                              className="text-xs text-[hsl(var(--accent))] hover:text-[hsl(var(--accent)/0.8)] font-semibold flex items-center gap-1"
                            >
                              + Add Round
                            </button>
                          )}
                          {(editData.rounds || tournament.rounds?.length || 1) > 1 && (
                            <button
                              type="button"
                              onClick={() =>
                                setEditData((prev) => ({
                                  ...prev,
                                  rounds: Math.max(
                                    1,
                                    (prev.rounds || tournament.rounds?.length || 1) - 1
                                  ),
                                }))
                              }
                              className="text-xs text-red-400 hover:text-red-300 font-semibold flex items-center gap-1"
                            >
                              − Remove
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="space-y-3">
                      {Array.from(
                        { length: editData.rounds || tournament.rounds?.length || 1 },
                        (_, i) => {
                          const rn = i + 1;
                          const rd = roundDates[String(rn)] || {};
                          const existingRound =
                            (tournament.rounds || []).find((r) => r.round === rn) || {};
                          const isFinalRound =
                            rn === (editData.rounds || tournament.rounds?.length || 1);
                          const isUpcoming = tournament.status === 'upcoming';
                          return (
                            <div
                              key={rn}
                              className="bg-[hsl(var(--card))] border border-[hsl(var(--border)/0.2)] rounded-lg p-4 space-y-3"
                            >
                              {/* Round header with name */}
                              <div className="flex items-center gap-3">
                                <span className="w-7 h-7 flex items-center justify-center bg-[hsl(var(--accent)/0.15)] text-[hsl(var(--accent))] rounded-md text-xs font-bold shrink-0">
                                  R{rn}
                                </span>
                                <input
                                  type="text"
                                  value={roundNames[String(rn)] || ''}
                                  onChange={(e) =>
                                    setRoundNames((prev) => ({
                                      ...prev,
                                      [String(rn)]: e.target.value,
                                    }))
                                  }
                                  disabled={!isUpcoming}
                                  placeholder={`Round ${rn} name`}
                                  className="flex-1 px-3 py-1.5 bg-[hsl(var(--background))] border border-[hsl(var(--border)/0.3)] rounded-lg text-[hsl(var(--foreground))] text-sm focus:outline-none focus:border-[hsl(var(--accent)/0.5)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                />
                              </div>

                              {/* Dates + Mode */}
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <label className="block text-[10px] text-[hsl(var(--muted-foreground))] font-medium mb-1 uppercase tracking-wider">
                                    Start Date
                                  </label>
                                  <input
                                    type="date"
                                    value={rd.start_date || ''}
                                    onChange={(e) =>
                                      handleRoundDateChange(rn, 'start_date', e.target.value)
                                    }
                                    className="w-full px-3 py-2 bg-[hsl(var(--background))] border border-[hsl(var(--border)/0.3)] rounded-lg text-[hsl(var(--foreground))] text-sm focus:outline-none focus:border-[hsl(var(--accent)/0.5)] transition-colors [color-scheme:dark]"
                                  />
                                </div>
                                <div>
                                  <label className="block text-[10px] text-[hsl(var(--muted-foreground))] font-medium mb-1 uppercase tracking-wider">
                                    End Date
                                  </label>
                                  <input
                                    type="date"
                                    value={rd.end_date || ''}
                                    onChange={(e) =>
                                      handleRoundDateChange(rn, 'end_date', e.target.value)
                                    }
                                    className="w-full px-3 py-2 bg-[hsl(var(--background))] border border-[hsl(var(--border)/0.3)] rounded-lg text-[hsl(var(--foreground))] text-sm focus:outline-none focus:border-[hsl(var(--accent)/0.5)] transition-colors [color-scheme:dark]"
                                  />
                                </div>
                              </div>

                              <div className="grid grid-cols-2 gap-3">
                                {/* Mode */}
                                <div>
                                  <label className="block text-[10px] text-[hsl(var(--muted-foreground))] font-medium mb-1 uppercase tracking-wider">
                                    Mode
                                  </label>
                                  <select
                                    value={rd.mode || 'online'}
                                    onChange={(e) =>
                                      handleRoundDateChange(rn, 'mode', e.target.value)
                                    }
                                    className="w-full px-3 py-2 bg-[hsl(var(--background))] border border-[hsl(var(--border)/0.3)] rounded-lg text-[hsl(var(--foreground))] text-sm focus:outline-none focus:border-[hsl(var(--accent)/0.5)] transition-colors appearance-none cursor-pointer"
                                  >
                                    <option value="online" className="bg-[hsl(var(--background))]">
                                      Online
                                    </option>
                                    <option value="offline" className="bg-[hsl(var(--background))]">
                                      Offline
                                    </option>
                                  </select>
                                </div>

                                {/* Matches per round */}
                                <div>
                                  <label className="block text-[10px] text-[hsl(var(--muted-foreground))] font-medium mb-1 uppercase tracking-wider">
                                    Matches per Round
                                  </label>
                                  <select
                                    value={
                                      editRoundMatchCustom[String(rn)]
                                        ? 'custom'
                                        : (editRoundMatches[String(rn)] ??
                                          existingRound.max_matches ??
                                          tournament.max_matches ??
                                          1)
                                    }
                                    onChange={(e) => {
                                      if (e.target.value === 'custom') {
                                        setEditRoundMatchCustom((prev) => ({
                                          ...prev,
                                          [String(rn)]: true,
                                        }));
                                        setEditRoundMatches((prev) => ({
                                          ...prev,
                                          [String(rn)]: prev[String(rn)] > 6 ? prev[String(rn)] : 7,
                                        }));
                                      } else {
                                        setEditRoundMatchCustom((prev) => ({
                                          ...prev,
                                          [String(rn)]: false,
                                        }));
                                        setEditRoundMatches((prev) => ({
                                          ...prev,
                                          [String(rn)]: Number(e.target.value),
                                        }));
                                      }
                                    }}
                                    className="w-full px-3 py-2 bg-[hsl(var(--background))] border border-[hsl(var(--border)/0.3)] rounded-lg text-[hsl(var(--foreground))] text-sm focus:outline-none focus:border-[hsl(var(--accent)/0.5)] transition-colors appearance-none cursor-pointer"
                                  >
                                    {[1, 2, 3, 4, 5, 6].map((n) => (
                                      <option
                                        key={n}
                                        value={n}
                                        className="bg-[hsl(var(--background))]"
                                      >
                                        {n}
                                      </option>
                                    ))}
                                    <option value="custom" className="bg-[hsl(var(--background))]">
                                      Custom
                                    </option>
                                  </select>
                                  {editRoundMatchCustom[String(rn)] && (
                                    <input
                                      type="number"
                                      min="1"
                                      placeholder="Enter number"
                                      value={
                                        editRoundMatches[String(rn)] > 6
                                          ? editRoundMatches[String(rn)]
                                          : ''
                                      }
                                      onChange={(e) =>
                                        setEditRoundMatches((prev) => ({
                                          ...prev,
                                          [String(rn)]: Number(e.target.value) || 1,
                                        }))
                                      }
                                      className="w-full mt-1.5 px-3 py-2 bg-[hsl(var(--background))] border border-[hsl(var(--accent)/0.5)] rounded-lg text-[hsl(var(--foreground))] text-sm focus:outline-none transition-colors"
                                    />
                                  )}
                                </div>
                              </div>

                              {/* Qualifying teams */}
                              <div>
                                <label className="block text-[10px] text-[hsl(var(--muted-foreground))] font-medium mb-1 uppercase tracking-wider">
                                  Teams that qualify
                                </label>
                                <input
                                  type="number"
                                  min="0"
                                  value={editRoundQualifying[String(rn)] ?? ''}
                                  onChange={(e) =>
                                    setEditRoundQualifying((prev) => ({
                                      ...prev,
                                      [String(rn)]: e.target.value,
                                    }))
                                  }
                                  placeholder={isFinalRound ? '0 (final round)' : 'e.g. 16'}
                                  className="w-full px-3 py-2 bg-[hsl(var(--background))] border border-[hsl(var(--border)/0.3)] rounded-lg text-[hsl(var(--foreground))] text-sm focus:outline-none focus:border-[hsl(var(--accent)/0.5)] transition-colors"
                                />
                                <p className="text-[10px] text-[hsl(var(--muted-foreground))] mt-1">
                                  {isFinalRound
                                    ? 'Final round — set 0'
                                    : 'Teams advancing to next round'}
                                </p>
                              </div>
                            </div>
                          );
                        }
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs text-[hsl(var(--muted-foreground))] font-medium mb-1.5">
                      Event Description
                    </label>
                    <textarea
                      name="description"
                      value={editData.description}
                      onChange={handleEditChange}
                      rows={6}
                      className="w-full px-3 py-2.5 bg-[hsl(var(--card))] border border-[hsl(var(--border)/0.3)] rounded-lg text-[hsl(var(--foreground))] text-sm focus:outline-none focus:border-[hsl(var(--accent)/0.5)] transition-colors resize-none"
                      placeholder="Detailed description..."
                      required
                    />
                  </div>

                  {/* Banner Image Upload */}
                  {true && (
                    <div>
                      <label className="block text-xs text-[hsl(var(--muted-foreground))] font-medium mb-1.5">
                        Banner Image
                      </label>
                      {bannerPreview && (
                        <img
                          src={bannerPreview}
                          alt="Banner preview"
                          className="w-full h-28 object-cover rounded-lg mb-2 border border-[hsl(var(--border)/0.3)]"
                        />
                      )}
                      <label
                        htmlFor="banner-edit-upload"
                        className="flex items-center justify-center gap-2 w-full py-3 bg-[hsl(var(--card))] border border-dashed border-[hsl(var(--border)/0.4)] hover:border-[hsl(var(--accent)/0.5)] hover:bg-[hsl(var(--secondary)/0.3)] rounded-lg cursor-pointer transition-colors group"
                      >
                        <svg
                          className="w-4 h-4 text-[hsl(var(--muted-foreground))] group-hover:text-[hsl(var(--accent))] transition-colors"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                        <span className="text-xs text-gray-400 group-hover:text-white transition-colors font-semibold">
                          {editData.banner_image instanceof File
                            ? editData.banner_image.name
                            : 'Change Banner Image'}
                        </span>
                        <input
                          id="banner-edit-upload"
                          type="file"
                          accept="image/*"
                          onChange={handleBannerChange}
                          className="hidden"
                        />
                      </label>
                    </div>
                  )}
                </div>

                <div className="space-y-4">
                  {/* Settings Card - editable core fields */}
                  <div>
                    <label className="block text-xs text-[hsl(var(--muted-foreground))] font-medium mb-1.5 flex items-center gap-2">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="text-[hsl(var(--accent))]"
                      >
                        <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"></path>
                        <circle cx="12" cy="12" r="3"></circle>
                      </svg>
                      Settings
                    </label>
                    <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border)/0.2)] rounded-lg p-4 space-y-3">
                      {/* Tournament Start Date — controls "Locked until" timer */}
                      <div>
                        <label className="block text-xs text-[hsl(var(--muted-foreground))] font-medium mb-1">
                          Tournament Start Date &amp; Time
                        </label>
                        <input
                          type="datetime-local"
                          value={editData.tournament_start}
                          onChange={(e) =>
                            setEditData({ ...editData, tournament_start: e.target.value })
                          }
                          className="w-full px-3 py-2 bg-[hsl(var(--card))] border border-[hsl(var(--border)/0.3)] rounded-lg text-[hsl(var(--foreground))] text-sm focus:outline-none focus:border-[hsl(var(--accent)/0.5)] transition-colors [color-scheme:dark]"
                        />
                        <p className="text-[10px] text-[hsl(var(--muted-foreground))] mt-1">
                          Set to now or past to unlock "Start Tournament"
                        </p>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs text-[hsl(var(--muted-foreground))] font-medium mb-1">
                            Max Teams
                          </label>
                          <input
                            type="number"
                            min="2"
                            value={editData.max_participants}
                            onChange={(e) =>
                              setEditData({
                                ...editData,
                                max_participants: parseInt(e.target.value) || 0,
                              })
                            }
                            disabled={tournament.status !== 'upcoming'}
                            className="w-full px-3 py-2 bg-[hsl(var(--card))] border border-[hsl(var(--border)/0.3)] rounded-lg text-[hsl(var(--foreground))] text-sm focus:outline-none focus:border-[hsl(var(--accent)/0.5)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-[hsl(var(--muted-foreground))] font-medium mb-1">
                            Entry (₹)
                          </label>
                          <input
                            type="number"
                            min="0"
                            value={editData.entry_fee}
                            onChange={(e) =>
                              setEditData({
                                ...editData,
                                entry_fee: parseFloat(e.target.value) || 0,
                              })
                            }
                            onWheel={(e) => e.target.blur()}
                            disabled={tournament.status !== 'upcoming'}
                            className="w-full px-3 py-2 bg-[hsl(var(--card))] border border-[hsl(var(--border)/0.3)] rounded-lg text-[hsl(var(--foreground))] text-sm focus:outline-none focus:border-[hsl(var(--accent)/0.5)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs text-[hsl(var(--muted-foreground))] font-medium mb-1">
                          Prize Pool (₹)
                        </label>
                        <input
                          type="number"
                          min="0"
                          value={editData.prize_pool}
                          onChange={(e) =>
                            setEditData({
                              ...editData,
                              prize_pool: parseFloat(e.target.value) || 0,
                            })
                          }
                          onWheel={(e) => e.target.blur()}
                          disabled={tournament.status !== 'upcoming'}
                          className="w-full px-3 py-2 bg-[hsl(var(--card))] border border-[hsl(var(--border)/0.3)] rounded-lg text-[hsl(var(--foreground))] text-sm focus:outline-none focus:border-[hsl(var(--accent)/0.5)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs text-[hsl(var(--muted-foreground))] font-medium mb-1.5">
                      Live Stream URL
                    </label>
                    <input
                      type="url"
                      name="live_link"
                      value={editData.live_link}
                      onChange={handleEditChange}
                      placeholder="https://youtube.com/..."
                      className="w-full px-3 py-2 bg-[hsl(var(--card))] border border-[hsl(var(--border)/0.3)] rounded-lg text-[hsl(var(--foreground))] text-sm focus:outline-none focus:border-[hsl(var(--accent)/0.5)] transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-xs text-[hsl(var(--muted-foreground))] font-medium mb-1.5">
                      Rules & Guidelines
                    </label>
                    <textarea
                      name="rules"
                      value={editData.rules}
                      onChange={handleEditChange}
                      rows={8}
                      className="w-full px-3 py-2.5 bg-[hsl(var(--card))] border border-[hsl(var(--border)/0.3)] rounded-lg text-[hsl(var(--foreground))] font-mono text-sm focus:outline-none focus:border-[hsl(var(--accent)/0.5)] transition-colors resize-none"
                      placeholder="Rules & guidelines..."
                      required
                    />
                  </div>

                  {tournament.status === 'upcoming' && (
                    <>
                      <div>
                        <label className="block text-xs text-[hsl(var(--muted-foreground))] font-medium mb-1.5">
                          Prize Distribution
                        </label>
                        <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border)/0.2)] rounded-lg p-4 space-y-2">
                          {editPrizeDistribution.map((prize, index) => {
                            let trophyColor = 'text-gray-400';
                            if (prize.place === '1st') trophyColor = 'text-yellow-500';
                            else if (prize.place === '2nd') trophyColor = 'text-gray-400';
                            else if (prize.place === '3rd') trophyColor = 'text-orange-500';
                            return (
                              <div key={index} className="flex items-center gap-2">
                                <div className="flex items-center gap-1.5 min-w-[56px]">
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="14"
                                    height="14"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    className={`h-3.5 w-3.5 ${trophyColor}`}
                                  >
                                    <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"></path>
                                    <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"></path>
                                    <path d="M4 22h16"></path>
                                    <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"></path>
                                    <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"></path>
                                    <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"></path>
                                  </svg>
                                  <span className="text-xs font-medium text-gray-300">
                                    {prize.place}
                                  </span>
                                </div>
                                <div className="flex-1 relative">
                                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                                    ₹
                                  </span>
                                  <input
                                    type="number"
                                    min="0"
                                    value={prize.amount}
                                    onChange={(e) => {
                                      const updated = [...editPrizeDistribution];
                                      updated[index].amount = parseInt(e.target.value) || 0;
                                      setEditPrizeDistribution(updated);
                                    }}
                                    onWheel={(e) => e.target.blur()}
                                    className="w-full rounded-xl border border-white/20 px-3 py-1.5 pl-6 text-xs bg-white/10 text-white focus:outline-none focus:ring-2 focus:ring-yellow-500/30 focus:border-yellow-500/50"
                                  />
                                </div>
                                {editPrizeDistribution.length > 1 && (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setEditPrizeDistribution(
                                        editPrizeDistribution.filter((_, i) => i !== index)
                                      )
                                    }
                                    className="w-7 h-7 flex items-center justify-center text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-all flex-shrink-0"
                                  >
                                    <svg
                                      className="w-3.5 h-3.5"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M6 18L18 6M6 6l12 12"
                                      />
                                    </svg>
                                  </button>
                                )}
                              </div>
                            );
                          })}
                          <button
                            type="button"
                            onClick={() => {
                              const places = [
                                '1st',
                                '2nd',
                                '3rd',
                                '4th',
                                '5th',
                                '6th',
                                '7th',
                                '8th',
                              ];
                              const nextPlace =
                                places[editPrizeDistribution.length] ||
                                `${editPrizeDistribution.length + 1}th`;
                              setEditPrizeDistribution([
                                ...editPrizeDistribution,
                                { place: nextPlace, amount: 0 },
                              ]);
                            }}
                            disabled={editPrizeDistribution.length >= 8}
                            className="w-full py-1.5 text-xs text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] border border-[hsl(var(--border)/0.3)] hover:border-[hsl(var(--border)/0.5)] rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            + Add Place
                          </button>
                          <div className="pt-2 border-t border-[hsl(var(--border)/0.2)] flex justify-between items-center">
                            <span className="text-xs text-[hsl(var(--muted-foreground))] font-medium">
                              Total Distributed
                            </span>
                            <span
                              className={`text-sm font-semibold ${
                                editPrizeDistribution.reduce((s, p) => s + p.amount, 0) ===
                                parseFloat(editData.prize_pool || 0)
                                  ? 'text-green-400'
                                  : 'text-yellow-400'
                              }`}
                            >
                              ₹
                              {editPrizeDistribution
                                .reduce((s, p) => s + p.amount, 0)
                                .toLocaleString()}{' '}
                              / ₹{parseFloat(editData.prize_pool || 0).toLocaleString()}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Special Awards (cash) */}
                      <div className="space-y-2 pt-2 border-t border-[hsl(var(--border)/0.4)]">
                        <div className="flex items-center justify-between">
                          <label className="text-xs font-semibold text-[hsl(var(--foreground))]">
                            ⭐ Special Awards (Cash)
                          </label>
                          <button
                            type="button"
                            onClick={() =>
                              setEditSpecialAwards((p) => [...p, { name: '', amount: '' }])
                            }
                            className="flex items-center gap-1 text-xs text-[hsl(var(--foreground))] hover:text-[hsl(var(--accent))] transition-colors"
                          >
                            <Plus className="w-3 h-3" /> Add Award
                          </button>
                        </div>
                        <p className="text-xs text-[hsl(var(--muted-foreground))]">
                          e.g. Best IGL Award, MVP Award
                        </p>
                        {editSpecialAwards.map((award, idx) => (
                          <div key={idx} className="flex items-center gap-2">
                            <input
                              type="text"
                              value={award.name}
                              placeholder="Award name"
                              onChange={(e) => {
                                const updated = [...editSpecialAwards];
                                updated[idx] = { ...updated[idx], name: e.target.value };
                                setEditSpecialAwards(updated);
                              }}
                              className="flex-1 bg-black border border-[hsl(var(--border))] rounded-lg text-xs px-2 py-1.5 text-[hsl(var(--foreground))] focus:outline-none h-8"
                            />
                            <input
                              type="number"
                              min="0"
                              value={award.amount}
                              placeholder="₹ Amount"
                              onChange={(e) => {
                                const updated = [...editSpecialAwards];
                                updated[idx] = { ...updated[idx], amount: e.target.value };
                                setEditSpecialAwards(updated);
                              }}
                              onWheel={(e) => e.target.blur()}
                              className="w-28 bg-black border border-[hsl(var(--border))] rounded-lg text-xs px-2 py-1.5 text-[hsl(var(--foreground))] focus:outline-none h-8"
                            />
                            <button
                              type="button"
                              onClick={() =>
                                setEditSpecialAwards((p) => p.filter((_, i) => i !== idx))
                              }
                              className="flex items-center justify-center w-8 h-8 text-red-400 hover:bg-red-500/10 rounded-md transition-colors flex-shrink-0"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ))}
                      </div>

                      {/* Coupon Prizes */}
                      <div className="space-y-3 pt-2 border-t border-[hsl(var(--border)/0.4)]">
                        <div className="flex items-center justify-between">
                          <label className="text-xs font-semibold text-[hsl(var(--foreground))]">
                            🎟 Coupon Prizes
                          </label>
                          <button
                            type="button"
                            onClick={() =>
                              setEditCouponTiers((p) => [
                                ...p,
                                {
                                  rank: `${p.length + 1}${['th', 'st', 'nd', 'rd'][(p.length + 1) % 10] || 'th'}`,
                                  coupons: [{ name: '', link: '', amount: '' }],
                                },
                              ])
                            }
                            className="flex items-center gap-1 text-xs text-[hsl(var(--foreground))] hover:text-[hsl(var(--accent))] transition-colors"
                          >
                            <Plus className="w-3 h-3" /> Add Placement
                          </button>
                        </div>
                        <p className="text-xs text-[hsl(var(--muted-foreground))]">
                          Add coupon prizes from sponsors (e.g. SPINBOT, UniPin) per placement.
                        </p>

                        {editCouponTiers.map((tier, tIdx) => (
                          <div
                            key={tIdx}
                            className="rounded-lg border border-[hsl(var(--border)/0.5)] p-3 space-y-2"
                          >
                            <div className="flex items-center gap-2">
                              <input
                                type="text"
                                value={tier.rank}
                                placeholder="Placement (e.g. 1st)"
                                onChange={(e) => {
                                  const updated = [...editCouponTiers];
                                  updated[tIdx] = { ...updated[tIdx], rank: e.target.value };
                                  setEditCouponTiers(updated);
                                }}
                                className="w-24 bg-black border border-[hsl(var(--border))] rounded-lg text-xs px-2 py-1.5 text-[hsl(var(--foreground))] focus:outline-none h-8"
                              />
                              <span className="text-xs text-[hsl(var(--muted-foreground))] flex-1">
                                Place
                              </span>
                              {editCouponTiers.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() =>
                                    setEditCouponTiers((p) => p.filter((_, i) => i !== tIdx))
                                  }
                                  className="text-xs text-red-400 hover:underline"
                                >
                                  Remove
                                </button>
                              )}
                            </div>
                            {tier.coupons.map((coupon, cIdx) => (
                              <div key={cIdx} className="flex items-center gap-1.5">
                                <input
                                  type="text"
                                  value={coupon.name}
                                  placeholder="Sponsor name"
                                  onChange={(e) => {
                                    const updated = [...editCouponTiers];
                                    updated[tIdx].coupons[cIdx] = {
                                      ...coupon,
                                      name: e.target.value,
                                    };
                                    setEditCouponTiers(updated);
                                  }}
                                  className="flex-1 bg-black border border-[hsl(var(--border))] rounded-lg text-xs px-2 py-1.5 text-[hsl(var(--foreground))] focus:outline-none h-8"
                                />
                                <input
                                  type="text"
                                  value={coupon.link}
                                  placeholder="Website link"
                                  onChange={(e) => {
                                    const updated = [...editCouponTiers];
                                    updated[tIdx].coupons[cIdx] = {
                                      ...coupon,
                                      link: e.target.value,
                                    };
                                    setEditCouponTiers(updated);
                                  }}
                                  className="flex-1 bg-black border border-[hsl(var(--border))] rounded-lg text-xs px-2 py-1.5 text-[hsl(var(--foreground))] focus:outline-none h-8"
                                />
                                <input
                                  type="number"
                                  value={coupon.amount}
                                  placeholder="Amount"
                                  min="0"
                                  onChange={(e) => {
                                    const updated = [...editCouponTiers];
                                    updated[tIdx].coupons[cIdx] = {
                                      ...coupon,
                                      amount: e.target.value,
                                    };
                                    setEditCouponTiers(updated);
                                  }}
                                  onWheel={(e) => e.target.blur()}
                                  className="w-24 bg-black border border-[hsl(var(--border))] rounded-lg text-xs px-2 py-1.5 text-[hsl(var(--foreground))] focus:outline-none h-8"
                                />
                                {tier.coupons.length > 1 && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const updated = [...editCouponTiers];
                                      updated[tIdx].coupons = updated[tIdx].coupons.filter(
                                        (_, i) => i !== cIdx
                                      );
                                      setEditCouponTiers(updated);
                                    }}
                                    className="text-red-400 flex-shrink-0"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                )}
                              </div>
                            ))}
                            <button
                              type="button"
                              onClick={() => {
                                const updated = [...editCouponTiers];
                                updated[tIdx].coupons.push({ name: '', link: '', amount: '' });
                                setEditCouponTiers(updated);
                              }}
                              className="text-xs text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors"
                            >
                              + Add Coupon Sponsor
                            </button>
                          </div>
                        ))}

                        {/* Special Coupon Awards */}
                        <div className="space-y-2 pt-2 border-t border-[hsl(var(--border)/0.3)]">
                          <div className="flex items-center justify-between">
                            <p className="text-xs font-semibold text-[hsl(var(--muted-foreground))]">
                              Special Coupon Awards
                            </p>
                            <button
                              type="button"
                              onClick={() =>
                                setEditCouponSpecialAwards((p) => [
                                  ...p,
                                  { name: '', coupons: [{ name: '', link: '', amount: '' }] },
                                ])
                              }
                              className="flex items-center gap-1 text-xs text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors"
                            >
                              <Plus className="w-3 h-3" /> Add
                            </button>
                          </div>
                          {editCouponSpecialAwards.map((award, aIdx) => (
                            <div
                              key={aIdx}
                              className="rounded-lg border border-[hsl(var(--border)/0.4)] p-3 space-y-2"
                            >
                              <div className="flex items-center gap-2">
                                <input
                                  type="text"
                                  value={award.name}
                                  placeholder="Award name (e.g. Best IGL Award)"
                                  onChange={(e) => {
                                    const updated = [...editCouponSpecialAwards];
                                    updated[aIdx] = { ...updated[aIdx], name: e.target.value };
                                    setEditCouponSpecialAwards(updated);
                                  }}
                                  className="flex-1 bg-black border border-[hsl(var(--border))] rounded-lg text-xs px-2 py-1.5 text-[hsl(var(--foreground))] focus:outline-none h-8"
                                />
                                <button
                                  type="button"
                                  onClick={() =>
                                    setEditCouponSpecialAwards((p) =>
                                      p.filter((_, i) => i !== aIdx)
                                    )
                                  }
                                  className="text-red-400"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </div>
                              {award.coupons.map((coupon, cIdx) => (
                                <div key={cIdx} className="flex items-center gap-1.5">
                                  <input
                                    type="text"
                                    value={coupon.name}
                                    placeholder="Sponsor name"
                                    onChange={(e) => {
                                      const updated = [...editCouponSpecialAwards];
                                      updated[aIdx].coupons[cIdx] = {
                                        ...coupon,
                                        name: e.target.value,
                                      };
                                      setEditCouponSpecialAwards(updated);
                                    }}
                                    className="flex-1 bg-black border border-[hsl(var(--border))] rounded-lg text-xs px-2 py-1.5 text-[hsl(var(--foreground))] focus:outline-none h-8"
                                  />
                                  <input
                                    type="text"
                                    value={coupon.link}
                                    placeholder="Website link"
                                    onChange={(e) => {
                                      const updated = [...editCouponSpecialAwards];
                                      updated[aIdx].coupons[cIdx] = {
                                        ...coupon,
                                        link: e.target.value,
                                      };
                                      setEditCouponSpecialAwards(updated);
                                    }}
                                    className="flex-1 bg-black border border-[hsl(var(--border))] rounded-lg text-xs px-2 py-1.5 text-[hsl(var(--foreground))] focus:outline-none h-8"
                                  />
                                  <input
                                    type="number"
                                    value={coupon.amount}
                                    placeholder="Amount"
                                    min="0"
                                    onChange={(e) => {
                                      const updated = [...editCouponSpecialAwards];
                                      updated[aIdx].coupons[cIdx] = {
                                        ...coupon,
                                        amount: e.target.value,
                                      };
                                      setEditCouponSpecialAwards(updated);
                                    }}
                                    onWheel={(e) => e.target.blur()}
                                    className="w-24 bg-black border border-[hsl(var(--border))] rounded-lg text-xs px-2 py-1.5 text-[hsl(var(--foreground))] focus:outline-none h-8"
                                  />
                                  {award.coupons.length > 1 && (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const updated = [...editCouponSpecialAwards];
                                        updated[aIdx].coupons = updated[aIdx].coupons.filter(
                                          (_, i) => i !== cIdx
                                        );
                                        setEditCouponSpecialAwards(updated);
                                      }}
                                      className="text-red-400"
                                    >
                                      <X className="w-3 h-3" />
                                    </button>
                                  )}
                                </div>
                              ))}
                              <button
                                type="button"
                                onClick={() => {
                                  const updated = [...editCouponSpecialAwards];
                                  updated[aIdx].coupons.push({ name: '', link: '', amount: '' });
                                  setEditCouponSpecialAwards(updated);
                                }}
                                className="text-xs text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors"
                              >
                                + Add Sponsor
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  )}

                  {/* Placement Points — always editable, non-Valorant/COD only */}
                  {!['Valorant', 'COD'].includes(tournament.game) &&
                    editPlacementPoints.length > 0 && (
                      <div className="space-y-3 pt-4 border-t border-[hsl(var(--border)/0.2)]">
                        <div className="flex items-center gap-2">
                          <label className="text-sm font-semibold text-[hsl(var(--foreground))]">
                            🎯 Placement Points
                          </label>
                        </div>
                        <div className="grid grid-cols-4 gap-2">
                          {editPlacementPoints.map((item, index) => (
                            <div
                              key={index}
                              className="text-center p-2 rounded-lg border"
                              style={{
                                background: 'hsl(var(--secondary)/0.3)',
                                borderColor: 'hsl(var(--border)/0.3)',
                              }}
                            >
                              <span className="text-[10px] text-[hsl(var(--muted-foreground))]">
                                #{item.position}
                              </span>
                              <input
                                type="number"
                                min="0"
                                max="100"
                                value={item.points}
                                onChange={(e) => {
                                  const updated = [...editPlacementPoints];
                                  updated[index] = {
                                    ...updated[index],
                                    points: Number(e.target.value) || 0,
                                  };
                                  setEditPlacementPoints(updated);
                                }}
                                className="w-full bg-transparent text-sm font-bold text-center text-[hsl(var(--foreground))] focus:outline-none mt-0.5"
                              />
                            </div>
                          ))}
                        </div>
                        <p className="text-[10px] text-[hsl(var(--muted-foreground))]">
                          Points per placement (9th onwards = 0). Every kill = 1 point.
                        </p>
                      </div>
                    )}
                </div>
              </div>

              {/* ── Sponsors ── (managed independently — no Save required) */}
              <hr className="border-[hsl(var(--border)/0.2)] my-6" />
              <div className="info-grid-card rounded-xl p-6">
                <p className="text-xs text-[hsl(var(--muted-foreground))] mb-4 bg-[hsl(var(--secondary)/0.4)] border border-[hsl(var(--border)/0.2)] rounded-lg px-3 py-2">
                  Sponsors are saved immediately — no need to click Save Changes above.
                </p>
                <div className="flex items-center gap-2 mb-5">
                  <h2 className="text-lg font-bold text-[hsl(var(--foreground))]">Sponsors</h2>
                  {sponsors.length > 0 && (
                    <span className="text-xs bg-[hsl(var(--accent)/0.15)] text-[hsl(var(--accent))] px-2 py-0.5 rounded-full font-medium">
                      {sponsors.length}
                    </span>
                  )}
                </div>

                {/* Add sponsor form */}
                <form
                  onSubmit={handleAddSponsor}
                  className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5"
                >
                  <div>
                    <label className="block text-xs text-[hsl(var(--muted-foreground))] font-medium mb-1">
                      Sponsor Name <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      value={sponsorForm.name}
                      onChange={(e) => setSponsorForm((f) => ({ ...f, name: e.target.value }))}
                      placeholder="e.g. Red Bull"
                      className="w-full px-3 py-2 bg-[hsl(var(--card))] border border-[hsl(var(--border)/0.3)] rounded-lg text-[hsl(var(--foreground))] text-sm focus:outline-none focus:border-[hsl(var(--accent)/0.5)] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-[hsl(var(--muted-foreground))] font-medium mb-1">
                      Sponsor Type
                    </label>
                    <input
                      type="text"
                      value={sponsorForm.sponsor_type}
                      onChange={(e) =>
                        setSponsorForm((f) => ({ ...f, sponsor_type: e.target.value }))
                      }
                      placeholder="e.g. Food Partner, Title Sponsor"
                      className="w-full px-3 py-2 bg-[hsl(var(--card))] border border-[hsl(var(--border)/0.3)] rounded-lg text-[hsl(var(--foreground))] text-sm focus:outline-none focus:border-[hsl(var(--accent)/0.5)] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-[hsl(var(--muted-foreground))] font-medium mb-1">
                      Website URL
                    </label>
                    <input
                      type="url"
                      value={sponsorForm.website_url}
                      onChange={(e) =>
                        setSponsorForm((f) => ({ ...f, website_url: e.target.value }))
                      }
                      placeholder="https://example.com"
                      className="w-full px-3 py-2 bg-[hsl(var(--card))] border border-[hsl(var(--border)/0.3)] rounded-lg text-[hsl(var(--foreground))] text-sm focus:outline-none focus:border-[hsl(var(--accent)/0.5)] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-[hsl(var(--muted-foreground))] font-medium mb-1">
                      Logo (optional)
                    </label>
                    <div className="flex items-center gap-2">
                      {sponsorLogoPreview && (
                        <img
                          src={sponsorLogoPreview}
                          alt="preview"
                          className="w-10 h-10 rounded-lg object-cover border border-[hsl(var(--border)/0.3)] shrink-0"
                        />
                      )}
                      <label className="flex-1 cursor-pointer px-3 py-2 bg-[hsl(var(--card))] border border-[hsl(var(--border)/0.3)] border-dashed rounded-lg text-[hsl(var(--muted-foreground))] text-xs text-center hover:border-[hsl(var(--accent)/0.4)] transition-colors">
                        {sponsorForm.logo ? sponsorForm.logo.name : 'Click to upload'}
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={handleSponsorLogoChange}
                        />
                      </label>
                    </div>
                  </div>
                  {sponsorError && (
                    <div className="sm:col-span-2 text-red-400 text-xs font-medium">
                      {sponsorError}
                    </div>
                  )}
                  <div className="sm:col-span-2 flex justify-end">
                    <button
                      type="submit"
                      disabled={sponsorSaving}
                      className="px-5 py-2 bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent)/0.85)] text-white rounded-lg text-sm font-semibold transition-colors disabled:opacity-60"
                    >
                      {sponsorSaving ? 'Adding…' : '+ Add Sponsor'}
                    </button>
                  </div>
                </form>

                {/* Existing sponsors list */}
                {sponsors.length > 0 && (
                  <div className="space-y-2">
                    {sponsors.map((sp, idx) => (
                      <div key={sp.id}>
                        {editingSponsor?.id === sp.id ? (
                          /* ── Inline edit form ── */
                          <div className="p-4 bg-[hsl(var(--card)/0.8)] border border-[hsl(var(--accent)/0.3)] rounded-xl space-y-3">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              <div>
                                <label className="block text-xs text-[hsl(var(--muted-foreground))] font-medium mb-1">
                                  Sponsor Name *
                                </label>
                                <input
                                  type="text"
                                  value={editingSponsor.name}
                                  onChange={(e) =>
                                    setEditingSponsor((s) => ({ ...s, name: e.target.value }))
                                  }
                                  className="w-full px-3 py-2 bg-[hsl(var(--card))] border border-[hsl(var(--border)/0.3)] rounded-lg text-[hsl(var(--foreground))] text-sm focus:outline-none focus:border-[hsl(var(--accent)/0.5)] transition-colors"
                                />
                              </div>
                              <div>
                                <label className="block text-xs text-[hsl(var(--muted-foreground))] font-medium mb-1">
                                  Sponsor Type
                                </label>
                                <input
                                  type="text"
                                  value={editingSponsor.sponsor_type}
                                  onChange={(e) =>
                                    setEditingSponsor((s) => ({
                                      ...s,
                                      sponsor_type: e.target.value,
                                    }))
                                  }
                                  placeholder="e.g. Title Sponsor"
                                  className="w-full px-3 py-2 bg-[hsl(var(--card))] border border-[hsl(var(--border)/0.3)] rounded-lg text-[hsl(var(--foreground))] text-sm focus:outline-none focus:border-[hsl(var(--accent)/0.5)] transition-colors"
                                />
                              </div>
                              <div>
                                <label className="block text-xs text-[hsl(var(--muted-foreground))] font-medium mb-1">
                                  Website URL
                                </label>
                                <input
                                  type="url"
                                  value={editingSponsor.website_url}
                                  onChange={(e) =>
                                    setEditingSponsor((s) => ({
                                      ...s,
                                      website_url: e.target.value,
                                    }))
                                  }
                                  placeholder="https://example.com"
                                  className="w-full px-3 py-2 bg-[hsl(var(--card))] border border-[hsl(var(--border)/0.3)] rounded-lg text-[hsl(var(--foreground))] text-sm focus:outline-none focus:border-[hsl(var(--accent)/0.5)] transition-colors"
                                />
                              </div>
                              <div>
                                <label className="block text-xs text-[hsl(var(--muted-foreground))] font-medium mb-1">
                                  Logo (optional)
                                </label>
                                <div className="flex items-center gap-2">
                                  {(editingSponsor.logoPreview || editingSponsor.existingLogo) && (
                                    <img
                                      src={
                                        editingSponsor.logoPreview || editingSponsor.existingLogo
                                      }
                                      alt="preview"
                                      className="w-10 h-10 rounded-lg object-cover border border-[hsl(var(--border)/0.3)] shrink-0"
                                    />
                                  )}
                                  <label className="flex-1 cursor-pointer px-3 py-2 bg-[hsl(var(--card))] border border-[hsl(var(--border)/0.3)] border-dashed rounded-lg text-[hsl(var(--muted-foreground))] text-xs text-center hover:border-[hsl(var(--accent)/0.4)] transition-colors">
                                    {editingSponsor.logo instanceof File
                                      ? editingSponsor.logo.name
                                      : 'Click to change logo'}
                                    <input
                                      type="file"
                                      accept="image/*"
                                      className="hidden"
                                      onChange={(e) => {
                                        const file = e.target.files[0];
                                        if (!file) return;
                                        setEditingSponsor((s) => ({
                                          ...s,
                                          logo: file,
                                          logoPreview: URL.createObjectURL(file),
                                        }));
                                      }}
                                    />
                                  </label>
                                </div>
                              </div>
                            </div>
                            <div className="flex gap-2 justify-end">
                              <button
                                onClick={() => setEditingSponsor(null)}
                                className="px-4 py-1.5 text-sm text-[hsl(var(--muted-foreground))] border border-[hsl(var(--border)/0.3)] rounded-lg hover:bg-[hsl(var(--secondary))] transition-colors"
                              >
                                Cancel
                              </button>
                              <button
                                onClick={handleSaveEditSponsor}
                                disabled={editSponsorSaving}
                                className="px-4 py-1.5 text-sm bg-[hsl(var(--accent))] text-white rounded-lg hover:bg-[hsl(var(--accent)/0.85)] transition-colors disabled:opacity-60"
                              >
                                {editSponsorSaving ? 'Saving…' : 'Save'}
                              </button>
                            </div>
                          </div>
                        ) : (
                          /* ── Read-only card ── */
                          <div className="flex items-center gap-3 p-3 bg-[hsl(var(--card)/0.6)] border border-[hsl(var(--border)/0.25)] rounded-xl">
                            {sp.logo ? (
                              <img
                                src={sp.logo}
                                alt={sp.name}
                                className="w-12 h-12 rounded-lg object-cover border border-[hsl(var(--border)/0.3)] shrink-0"
                              />
                            ) : (
                              <div className="w-12 h-12 rounded-lg bg-[hsl(var(--secondary))] flex items-center justify-center shrink-0 text-[hsl(var(--muted-foreground))] text-xl font-bold">
                                {sp.name.charAt(0).toUpperCase()}
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-[hsl(var(--foreground))] truncate">
                                {sp.name}
                              </p>
                              {sp.sponsor_type && (
                                <p className="text-xs text-[hsl(var(--muted-foreground))] truncate">
                                  {sp.sponsor_type}
                                </p>
                              )}
                              {sp.website_url && (
                                <a
                                  href={sp.website_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-xs text-[hsl(var(--accent))] truncate hover:underline"
                                >
                                  {sp.website_url}
                                </a>
                              )}
                            </div>
                            {/* Edit button */}
                            <button
                              onClick={() =>
                                setEditingSponsor({
                                  id: sp.id,
                                  name: sp.name,
                                  sponsor_type: sp.sponsor_type || '',
                                  website_url: sp.website_url || '',
                                  logo: null,
                                  logoPreview: null,
                                  existingLogo: sp.logo || null,
                                })
                              }
                              className="p-1.5 rounded-lg hover:bg-[hsl(var(--accent)/0.15)] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--accent))] transition-colors shrink-0"
                              title="Edit sponsor"
                            >
                              <svg
                                width="15"
                                height="15"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                              >
                                <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
                                <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
                              </svg>
                            </button>
                            {/* Reorder buttons */}
                            <div className="flex flex-col gap-0.5">
                              <button
                                onClick={() => handleMoveOrder(sp.id, 'up')}
                                disabled={idx === 0}
                                className="p-1 rounded hover:bg-[hsl(var(--secondary))] text-[hsl(var(--muted-foreground))] disabled:opacity-30 transition-colors"
                                title="Move up"
                              >
                                ▲
                              </button>
                              <button
                                onClick={() => handleMoveOrder(sp.id, 'down')}
                                disabled={idx === sponsors.length - 1}
                                className="p-1 rounded hover:bg-[hsl(var(--secondary))] text-[hsl(var(--muted-foreground))] disabled:opacity-30 transition-colors"
                                title="Move down"
                              >
                                ▼
                              </button>
                            </div>
                            <button
                              onClick={() => handleRemoveSponsor(sp.id)}
                              className="p-1.5 rounded-lg hover:bg-red-500/15 text-red-400 transition-colors shrink-0"
                              title="Remove sponsor"
                            >
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                              >
                                <polyline points="3 6 5 6 21 6" />
                                <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
                                <path d="M10 11v6M14 11v6" />
                                <path d="M9 6V4h6v2" />
                              </svg>
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {sponsors.length === 0 && (
                  <p className="text-xs text-[hsl(var(--muted-foreground))] text-center py-3">
                    No sponsors added yet.
                  </p>
                )}
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-border/20 mt-6">
                <button
                  onClick={handleSaveChanges}
                  className="flex-1 bg-accent hover:bg-accent/90 text-white px-5 py-2.5 rounded-lg font-semibold text-sm transition-colors"
                >
                  Save Changes
                </button>
                <button
                  onClick={handleEditToggle}
                  className="flex-1 bg-secondary/50 text-muted-foreground px-5 py-2.5 rounded-lg font-medium text-sm border border-border/30 hover:bg-secondary transition-colors"
                >
                  Discard
                </button>
              </div>
              {tournament.status === 'upcoming' && (
                <p className="mt-4 text-muted-foreground text-xs bg-accent/5 border border-accent/10 rounded-lg px-4 py-3">
                  <strong className="text-foreground">Note:</strong> Some parameters are locked once
                  the tournament starts.
                </p>
              )}
            </div>
          )}

          {/* Tournament Actions & Status Section - Only show when NOT ongoing */}
          {tournament.status !== 'ongoing' && (
            <div className="mt-cyber-card p-5">
              <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                {/* Start Tournament Button - Only show when status is upcoming */}
                {tournament.status === 'upcoming' && (
                  <>
                    <div className="flex-1">
                      <h2 className="text-lg font-bold text-foreground mb-0.5">Start Tournament</h2>
                      {new Date() < new Date(tournament.tournament_start) ? (
                        <StartCountdown targetDate={tournament.tournament_start} />
                      ) : (
                        <p className="text-muted-foreground text-xs">
                          Begin the competition once registration closes.
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                      {/* Pre-configure Round 1 — lets host set up groups + bulk schedule before start */}
                      {roundGroups.length === 0 && (
                        <button
                          onClick={async () => {
                            // Check if groups already exist before opening config modal
                            try {
                              const res = await tournamentAPI.getRoundGroups(tournament.id, 1);
                              const existing = res.data?.groups || [];
                              if (existing.length > 0) {
                                // Already pre-configured — load them and show reset option
                                setRoundGroups(existing);
                                setSelectedGroup(existing[0]);
                                showToast(
                                  'Round 1 is already pre-configured. Use Reset to reconfigure.',
                                  'info'
                                );
                                return;
                              }
                            } catch {
                              // No groups yet — fall through to open modal
                            }
                            setCurrentRound(1);
                            setShowRoundConfigModal(true);
                          }}
                          className="px-4 py-2.5 rounded-lg font-semibold text-sm transition-colors flex items-center gap-2 bg-accent/15 hover:bg-accent/25 text-accent border border-accent/30"
                          title="Pre-configure Round 1 groups so you can set maps and schedules before the tournament starts"
                        >
                          <Settings className="h-4 w-4" />
                          Pre-configure Groups
                        </button>
                      )}
                      {roundGroups.length > 0 && (
                        <div className="flex items-center gap-2">
                          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-green-500/10 border border-green-500/20 text-green-400 text-xs font-medium">
                            <Zap className="h-3.5 w-3.5" />
                            Round 1 groups ready — Bulk Schedule available
                          </div>
                          <button
                            onClick={() => {
                              setConfirmModal({
                                isOpen: true,
                                title: 'Reset Pre-configured Groups',
                                message:
                                  'This will delete all Round 1 groups and matches so you can reconfigure from scratch. No players have been notified yet.',
                                confirmText: 'Reset Groups',
                                type: 'danger',
                                onConfirm: async () => {
                                  try {
                                    setLoading(true);
                                    await tournamentAPI.resetRound(tournament.id, 1);
                                    showToast('Round 1 reset. You can pre-configure again.');
                                    setRoundGroups([]);
                                    setSelectedGroup(null);
                                    await fetchTournamentData();
                                  } catch (error) {
                                    showToast(
                                      error.response?.data?.error || 'Failed to reset round',
                                      'error'
                                    );
                                  } finally {
                                    setLoading(false);
                                  }
                                },
                              });
                            }}
                            className="px-3 py-2 rounded-lg text-xs font-medium transition-colors text-red-400 border border-red-500/30 hover:bg-red-500/10 flex items-center gap-1.5"
                            title="Reset pre-configured groups and start over"
                          >
                            <RotateCcw className="h-3.5 w-3.5" />
                            Reset
                          </button>
                        </div>
                      )}
                      <button
                        onClick={async () => {
                          try {
                            await tournamentAPI.startTournament(tournament.id);
                            showToast('Tournament started successfully!');
                            if (onStarted) {
                              onStarted();
                            } else {
                              navigate('/host/dashboard?tab=tournaments');
                            }
                          } catch (error) {
                            console.error('Error starting tournament:', error);
                            const errorMsg =
                              error.response?.data?.error ||
                              error.response?.data?.message ||
                              'Failed to start tournament';
                            showToast(errorMsg, 'error');
                          }
                        }}
                        disabled={new Date() < new Date(tournament.tournament_start)}
                        title={
                          new Date() < new Date(tournament.tournament_start)
                            ? `Locked until ${new Date(tournament.tournament_start).toLocaleString()}`
                            : 'Start Tournament'
                        }
                        className={`px-5 py-2.5 rounded-lg font-semibold text-sm transition-colors flex items-center gap-2 ${
                          new Date() < new Date(tournament.tournament_start)
                            ? 'bg-secondary text-muted-foreground cursor-not-allowed opacity-50'
                            : 'bg-red-600 hover:bg-red-700 text-white'
                        }`}
                      >
                        Start Tournament
                      </button>
                    </div>
                  </>
                )}

                {/* If completed, show basic summary */}
                {tournament.status === 'completed' && (
                  <div className="flex items-center gap-3 bg-green-500/10 px-4 py-2.5 rounded-lg border border-green-500/20">
                    <span className="text-xl">🏆</span>
                    <div>
                      <p className="text-green-400 font-semibold text-sm">Tournament Completed</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Tournament Completion Summary - Show ONLY when completed */}
              {tournament.status === 'completed' && (
                <div className="space-y-4 mt-4">
                  {/* Victory Header */}
                  <div className="info-grid-card p-5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-yellow-500/15 flex items-center justify-center">
                          <svg
                            className="w-5 h-5 text-yellow-500"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-foreground">Tournament Complete</h3>
                          <p className="text-yellow-500 text-xs font-medium">
                            {getEventLabel()} Concluded
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-muted-foreground text-xs">Final Results</p>
                        <p className="text-foreground text-sm font-bold">
                          {finalStandings?.results?.length ||
                            finalStandings?.groups?.reduce(
                              (total, group) => total + (group.standings?.team_a ? 2 : 0),
                              0
                            ) ||
                            tournament.current_participants ||
                            0}{' '}
                          Teams
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Champion Podium - Horizontal Premium Cards */}
                  {((finalStandings?.results && finalStandings.results.length > 0) ||
                    finalStandings?.winner) && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {/* 1st Place - Champion */}
                      {(() => {
                        const champion = finalStandings?.winner || finalStandings.results?.[0];
                        return (
                          champion && (
                            <div
                              onClick={() => handleWinnerClick(champion)}
                              className="cursor-pointer"
                            >
                              <div className="info-grid-card p-3 border-yellow-500/30 hover:border-yellow-500/50 transition-colors h-full">
                                <div className="flex items-center gap-3 h-full">
                                  <div className="w-11 h-11 rounded-lg bg-yellow-500/20 flex items-center justify-center text-xl shrink-0">
                                    🥇
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <span className="text-yellow-500 text-[10px] font-semibold uppercase flex items-center gap-1">
                                      🥇 CHAMPION
                                    </span>
                                    <h4 className="text-foreground font-bold text-base truncate">
                                      {champion.team_name}
                                    </h4>
                                    <p className="text-yellow-500 font-semibold text-sm">
                                      {champion.total_points || champion.match_wins || 0}{' '}
                                      <span className="text-xs opacity-60">
                                        {champion.match_wins !== undefined ? 'WINS' : 'PTS'}
                                      </span>
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )
                        );
                      })()}

                      {/* 2nd Place */}
                      {finalStandings.results?.[1] && (
                        <div
                          onClick={() => handleWinnerClick(finalStandings.results[1])}
                          className="cursor-pointer h-full"
                        >
                          <div className="info-grid-card p-3 hover:border-muted-foreground/30 transition-colors h-full">
                            <div className="flex items-center gap-3 h-full">
                              <div className="w-11 h-11 rounded-lg bg-secondary flex items-center justify-center text-xl shrink-0">
                                🥈
                              </div>
                              <div className="flex-1 min-w-0">
                                <span className="text-gray-400 text-[10px] font-semibold uppercase">
                                  RUNNER UP
                                </span>
                                <h4 className="text-foreground font-bold text-base truncate">
                                  {finalStandings.results[1].team_name ||
                                    finalStandings.results[1].name ||
                                    'Team 2'}
                                </h4>
                                <p className="text-muted-foreground font-semibold text-sm">
                                  {finalStandings.results[1].total_points ||
                                    finalStandings.results[1].match_wins ||
                                    0}{' '}
                                  <span className="text-xs opacity-60">
                                    {finalStandings.results[1].match_wins !== undefined
                                      ? 'WINS'
                                      : 'PTS'}
                                  </span>
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* 3rd Place */}
                      {finalStandings.results?.[2] && (
                        <div
                          onClick={() => handleWinnerClick(finalStandings.results[2])}
                          className="cursor-pointer h-full"
                        >
                          <div className="info-grid-card p-3 hover:border-orange-500/30 transition-colors h-full">
                            <div className="flex items-center gap-3 h-full">
                              <div className="w-11 h-11 rounded-lg bg-orange-500/15 flex items-center justify-center text-xl shrink-0">
                                🥉
                              </div>
                              <div className="flex-1 min-w-0">
                                <span className="text-orange-500/70 text-[10px] font-semibold uppercase">
                                  3RD PLACE
                                </span>
                                <h4 className="text-foreground font-bold text-base truncate">
                                  {finalStandings.results[2].team_name ||
                                    finalStandings.results[2].name ||
                                    'Team 3'}
                                </h4>
                                <p className="text-muted-foreground font-semibold text-sm">
                                  {finalStandings.results[2].total_points ||
                                    finalStandings.results[2].match_wins ||
                                    0}{' '}
                                  <span className="text-xs opacity-60">
                                    {finalStandings.results[2].match_wins !== undefined
                                      ? 'WINS'
                                      : 'PTS'}
                                  </span>
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Tournament Stats */}
                  <div className="info-grid-card p-4">
                    <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                      <BarChart3 className="h-4 w-4 text-accent" />
                      Tournament Stats
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {[
                        { label: 'Rounds', value: tournament.rounds.length },
                        { label: 'Teams', value: tournament.current_participants },
                        {
                          label: 'Prize Pool',
                          value: `₹${tournament.prize_pool?.toLocaleString()}`,
                          highlight: true,
                        },
                        {
                          label: 'Winner',
                          value:
                            finalStandings?.winner?.team_name ||
                            finalStandings?.results?.[0]?.team_name ||
                            'TBD',
                          highlight: true,
                        },
                      ].map((stat, i) => (
                        <div key={i} className="mt-stat-tile">
                          <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-0.5">
                            {stat.label}
                          </p>
                          <p
                            className={`font-bold text-sm truncate ${stat.highlight ? 'text-yellow-500' : 'text-foreground'}`}
                          >
                            {stat.value}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {tournament.status === 'ongoing' && (
            <div>
              {/* Round Progress */}
              <div className="mt-cyber-card p-3 sm:p-4 mb-4">
                {tournament?.is_5v5 ? (
                  /* ── 5v5: Lovable pill-style round navigation ── */
                  <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
                    {tournament.rounds.map((round, index) => {
                      const roundNum = round.round;
                      const isActive = roundNum === currentRound;
                      const isCompleted = getRoundStatus(roundNum) === 'completed';
                      const isFinal = index === tournament.rounds.length - 1;
                      const isLastItem = index === tournament.rounds.length - 1;
                      const rName =
                        roundNames?.[String(roundNum)] ||
                        (isFinal ? 'Finals' : `Round ${roundNum}`);
                      const teamCount =
                        roundNum === currentRound
                          ? roundGroups.reduce((a, g) => a + (g.teams?.length || 0), 0)
                          : null;
                      return (
                        <div key={roundNum} className="flex items-center gap-1.5 sm:gap-2">
                          <button
                            onClick={() => setCurrentRound(roundNum)}
                            className={`px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-lg transition-all text-[11px] sm:text-sm font-medium ${
                              isActive
                                ? 'bg-accent/20 text-accent border border-accent/50 font-semibold'
                                : isCompleted
                                  ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                                  : 'bg-secondary/20 text-muted-foreground/60 border border-dashed border-border/30'
                            }`}
                          >
                            {rName}
                            <span className="ml-1 opacity-70">
                              ({teamCount != null ? teamCount : '—'})
                            </span>
                          </button>
                          {!isLastItem && (
                            <ChevronRight className="h-3 w-3 sm:h-4 sm:w-4 text-muted-foreground/50 flex-shrink-0" />
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  /* ── BR mode: circular stepper ── */
                  <div className="round-stepper flex items-center justify-center overflow-x-auto no-scrollbar px-2">
                    {tournament.rounds.map((round, index) => {
                      const roundNum = round.round;
                      const isActive = roundNum === currentRound;
                      const isCompleted = getRoundStatus(roundNum) === 'completed';
                      const isFinal = index === tournament.rounds.length - 1;
                      const isLastItem = index === tournament.rounds.length - 1;
                      return (
                        <div key={roundNum} className="flex items-center">
                          <div
                            onClick={() => setCurrentRound(roundNum)}
                            className="flex flex-col items-center cursor-pointer group/step"
                          >
                            <div
                              className={`stepper-circle relative w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm border-2 ${
                                isActive
                                  ? 'bg-accent border-accent text-white'
                                  : isCompleted
                                    ? 'bg-green-500/15 border-green-500 text-green-500'
                                    : 'bg-secondary/30 border-border/50 text-muted-foreground group-hover/step:border-muted-foreground/50'
                              }`}
                            >
                              {isCompleted ? (
                                <svg
                                  className="w-4 h-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2.5"
                                    d="M5 13l4 4L19 7"
                                  />
                                </svg>
                              ) : (
                                roundNum
                              )}
                            </div>
                            <span
                              className={`mt-1.5 text-[10px] font-semibold whitespace-nowrap max-w-[72px] text-center truncate ${
                                isActive
                                  ? 'text-accent'
                                  : isCompleted
                                    ? 'text-green-500/70'
                                    : 'text-muted-foreground/50'
                              }`}
                            >
                              {roundNames?.[String(roundNum)] ||
                                (isFinal ? 'Finals' : `Round ${roundNum}`)}
                            </span>
                          </div>
                          {!isLastItem && (
                            <div className="stepper-line w-10 sm:w-16 lg:w-24 h-0.5 mx-1 mt-[-16px] relative">
                              <div className="absolute inset-0 bg-border/30 rounded-full"></div>
                              <div
                                className={`absolute inset-y-0 left-0 rounded-full transition-all duration-500 ${
                                  isCompleted
                                    ? 'bg-green-500 w-full'
                                    : isActive
                                      ? 'bg-accent w-1/2'
                                      : 'w-0'
                                }`}
                              ></div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Content Area — separate card */}
              <div className="mt-cyber-card p-5 lg:p-6 mb-6">
                <div className="relative">
                  {groupsLoading && roundGroups.length === 0 ? (
                    <div className="text-center py-16">
                      <div className="w-10 h-10 border-3 border-accent border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                      <p className="text-muted-foreground text-xs font-medium">
                        Loading match data...
                      </p>
                    </div>
                  ) : (
                    <>
                      {groupsLoading && (
                        <div className="absolute top-3 right-3 z-10 flex items-center gap-1.5 bg-secondary/50 px-2.5 py-1 rounded-full border border-border/30">
                          <div className="w-1.5 h-1.5 bg-accent rounded-full animate-pulse"></div>
                          <span className="text-[10px] text-muted-foreground font-medium">
                            Refreshing
                          </span>
                        </div>
                      )}

                      {roundGroups.length === 0 ? (
                        <div className="space-y-6">
                          {/* Ready to Start Card */}
                          <div className="text-center py-12">
                            <div className="w-16 h-16 rounded-full bg-accent/15 flex items-center justify-center mx-auto mb-5 border border-accent/20">
                              <Trophy className="h-8 w-8 text-accent" />
                            </div>
                            <h2 className="text-xl sm:text-2xl font-bold text-foreground mb-2">
                              Ready to Start{' '}
                              {roundNames?.[String(currentRound)] || `Round ${currentRound}`}?
                            </h2>
                            <p className="text-muted-foreground mb-6 max-w-md mx-auto text-sm">
                              {tournament?.is_5v5 && currentRound > 1
                                ? 'Qualifying teams from the previous round are ready. Configure lobbies to begin.'
                                : 'Configure group settings and begin the tournament round'}
                            </p>
                            <div className="flex flex-col sm:flex-row gap-3 justify-center">
                              {tournament?.is_5v5 && currentRound > 1 ? (
                                /* 5v5 round 2+: advance from prev round (marks it complete) then show lobby preview */
                                <button
                                  onClick={() => handleAdvanceRound(currentRound - 1)}
                                  className="inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-green-600 hover:bg-green-700 text-white rounded-lg font-bold text-sm sm:text-base transition-colors"
                                >
                                  <Play className="h-4 w-4 sm:h-5 sm:w-5" />
                                  Configure Lobbies for{' '}
                                  {roundNames?.[String(currentRound)] || `Round ${currentRound}`}
                                </button>
                              ) : (
                                <button
                                  onClick={() => handleStartRound(currentRound || 1)}
                                  className="inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-red-600 hover:bg-red-700 text-white rounded-lg font-bold text-sm sm:text-base transition-colors"
                                >
                                  <Play className="h-4 w-4 sm:h-5 sm:w-5" />
                                  Start{' '}
                                  {roundNames?.[String(currentRound)] || `Round ${currentRound}`}
                                </button>
                              )}
                              {!tournament?.is_5v5 && (
                                <button
                                  onClick={() => setShowBracketGenerator(!showBracketGenerator)}
                                  className={`inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-bold text-sm sm:text-base transition-colors ${
                                    showBracketGenerator
                                      ? 'bg-red-700 hover:bg-red-800 text-white'
                                      : 'bg-red-600 hover:bg-red-700 text-white'
                                  }`}
                                >
                                  <Swords className="h-4 w-4 sm:h-5 sm:w-5" />
                                  {showBracketGenerator
                                    ? 'Hide Bracket Generator'
                                    : 'Show Bracket Generator'}
                                </button>
                              )}
                            </div>
                          </div>

                          {/* Inline Bracket Generator */}
                          {showBracketGenerator && !tournament?.is_5v5 && (
                            <div
                              className="mt-cyber-card p-0 overflow-hidden"
                              style={{ borderColor: 'hsl(var(--accent) / 0.3)' }}
                            >
                              {/* Header */}
                              <div className="flex items-center justify-between p-4 border-b border-[hsl(var(--border)/0.3)]">
                                <h3 className="flex items-center gap-2 text-base sm:text-lg font-bold">
                                  <Trophy className="h-5 w-5 text-amber-400" />
                                  BGMI Bracket Generator
                                </h3>
                                <div className="flex items-center gap-2">
                                  <span className="text-xs font-medium text-accent border border-accent/30 rounded-full px-2.5 py-0.5">
                                    {registrations?.length || tournament?.registered_count || 0}{' '}
                                    Teams
                                  </span>
                                  <button
                                    onClick={() => setBracketExpanded(!bracketExpanded)}
                                    className="w-7 h-7 flex items-center justify-center rounded hover:bg-secondary/50 transition-colors"
                                  >
                                    {bracketExpanded ? (
                                      <ChevronUp className="h-4 w-4" />
                                    ) : (
                                      <ChevronDown className="h-4 w-4" />
                                    )}
                                  </button>
                                </div>
                              </div>

                              {bracketExpanded && (
                                <div className="p-4 space-y-5">
                                  {/* Total Teams Input */}
                                  <div className="space-y-1.5">
                                    <label className="text-sm font-bold text-foreground">
                                      Total Teams
                                    </label>
                                    <div className="text-xs text-muted-foreground mb-1">
                                      {registrations?.length || tournament?.registered_count || 0}{' '}
                                      teams registered
                                    </div>
                                  </div>

                                  {/* Select Bracket Template */}
                                  <div className="space-y-3">
                                    <label className="text-sm font-bold text-foreground">
                                      Select Bracket Template
                                    </label>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                      {bracketTemplates.map((tmpl) => (
                                        <button
                                          key={tmpl.id}
                                          onClick={() => {
                                            setSelectedBracketTemplate(tmpl.id);
                                            if (tmpl.id !== 'custom') {
                                              setBracketCustomRounds(
                                                tmpl.defaultRounds.map((r) => ({ ...r }))
                                              );
                                            }
                                          }}
                                          className={`text-left p-4 rounded-xl border-2 transition-all ${
                                            selectedBracketTemplate === tmpl.id
                                              ? 'border-accent bg-accent/10 shadow-[0_0_20px_hsl(var(--accent)/0.15)]'
                                              : 'border-[hsl(var(--border)/0.3)] bg-[hsl(var(--card))] hover:border-accent/30'
                                          }`}
                                        >
                                          <div className="flex items-center gap-2 mb-2">
                                            <span className="text-lg">{tmpl.icon}</span>
                                            <span className="font-bold text-sm">{tmpl.name}</span>
                                          </div>
                                          <p className="text-[11px] text-muted-foreground mb-3">
                                            {tmpl.description}
                                          </p>
                                          <div className="flex items-center gap-1.5 flex-wrap">
                                            {tmpl.stages.map((stage, si) => (
                                              <div key={si} className="flex items-center gap-1">
                                                <span className="text-[10px] px-2 py-0.5 rounded bg-secondary/50 text-muted-foreground">
                                                  {stage}
                                                </span>
                                                {si < tmpl.stages.length - 1 && (
                                                  <ChevronRight className="h-3 w-3 text-muted-foreground" />
                                                )}
                                              </div>
                                            ))}
                                          </div>
                                        </button>
                                      ))}
                                    </div>
                                  </div>

                                  {/* Custom Rounds Config */}
                                  {selectedBracketTemplate === 'custom' && (
                                    <div className="space-y-4">
                                      <div className="flex items-center justify-between">
                                        <label className="text-sm font-bold text-foreground">
                                          Custom Rounds
                                        </label>
                                        <button
                                          onClick={() =>
                                            setBracketCustomRounds((prev) => [
                                              ...prev,
                                              {
                                                name: `Round ${prev.length + 1}`,
                                                groups: 1,
                                                teamsPerGroup: 16,
                                                matchesPerGroup: 4,
                                                advancePerGroup: 8,
                                              },
                                            ])
                                          }
                                          className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded border border-border/50 hover:bg-secondary/50 transition-colors"
                                        >
                                          <Plus className="h-3 w-3" /> Add Round
                                        </button>
                                      </div>
                                      {bracketCustomRounds.map((round, ri) => (
                                        <div
                                          key={ri}
                                          className="p-4 rounded-xl bg-secondary/10 border border-border/30 space-y-3"
                                        >
                                          <div className="flex items-center justify-between">
                                            <input
                                              value={round.name}
                                              onChange={(e) => {
                                                const updated = [...bracketCustomRounds];
                                                updated[ri] = {
                                                  ...updated[ri],
                                                  name: e.target.value,
                                                };
                                                setBracketCustomRounds(updated);
                                              }}
                                              className="h-8 w-40 text-sm font-bold bg-secondary/30 border border-border/30 rounded px-2 text-foreground"
                                            />
                                            {bracketCustomRounds.length > 1 && (
                                              <button
                                                onClick={() =>
                                                  setBracketCustomRounds((prev) =>
                                                    prev.filter((_, i) => i !== ri)
                                                  )
                                                }
                                                className="text-red-400 hover:text-red-300"
                                              >
                                                <X className="h-4 w-4" />
                                              </button>
                                            )}
                                          </div>
                                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                            {[
                                              { key: 'groups', label: 'Groups' },
                                              { key: 'teamsPerGroup', label: 'Teams/Group' },
                                              { key: 'matchesPerGroup', label: 'Matches/Group' },
                                              { key: 'advancePerGroup', label: 'Advance/Group' },
                                            ].map(({ key, label }) => (
                                              <div key={key} className="space-y-1">
                                                <label className="text-[10px] text-muted-foreground">
                                                  {label}
                                                </label>
                                                <input
                                                  type="number"
                                                  value={round[key]}
                                                  onChange={(e) => {
                                                    const updated = [...bracketCustomRounds];
                                                    updated[ri] = {
                                                      ...updated[ri],
                                                      [key]: parseInt(e.target.value) || 1,
                                                    };
                                                    setBracketCustomRounds(updated);
                                                  }}
                                                  className="w-full h-9 bg-secondary/30 border border-border/30 rounded px-2 text-sm text-foreground"
                                                  min={1}
                                                />
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}

                                  {/* Bracket Overview Preview */}
                                  <div className="space-y-3">
                                    <h4 className="text-sm font-bold text-foreground">
                                      Bracket Preview
                                    </h4>
                                    <div className="overflow-x-auto">
                                      <div className="flex items-center gap-2 pb-2 min-w-max px-1">
                                        {/* Starting Teams */}
                                        <div className="flex flex-col items-center p-3 rounded-lg bg-accent/10 border border-accent/30 min-w-[100px]">
                                          <Users className="h-5 w-5 text-accent mb-1" />
                                          <span className="text-lg font-bold text-accent">
                                            {registrations?.length ||
                                              tournament?.registered_count ||
                                              0}
                                          </span>
                                          <span className="text-[10px] text-muted-foreground uppercase">
                                            Teams Start
                                          </span>
                                        </div>

                                        {bracketCustomRounds.map((round, ri) => {
                                          const teamsIn = round.groups * round.teamsPerGroup;
                                          const teamsOut = round.advancePerGroup * round.groups;
                                          const eliminated = teamsIn - teamsOut;
                                          return (
                                            <div key={ri} className="flex items-center">
                                              <div className="flex flex-col items-center mx-2">
                                                <ArrowRight className="h-5 w-5 text-muted-foreground" />
                                                {eliminated > 0 && (
                                                  <span className="text-[9px] text-red-400 flex items-center gap-0.5 mt-0.5">
                                                    <TrendingDown className="h-3 w-3" />-
                                                    {eliminated}
                                                  </span>
                                                )}
                                              </div>
                                              <div className="flex flex-col items-center p-3 rounded-lg border bg-secondary/20 border-border/40 min-w-[110px]">
                                                <span className="text-[10px] px-2 py-0.5 rounded bg-secondary/50 text-muted-foreground mb-2">
                                                  {round.name}
                                                </span>
                                                <span className="text-lg font-bold text-foreground">
                                                  {teamsOut}
                                                </span>
                                                <span className="text-[9px] text-muted-foreground">
                                                  Teams
                                                </span>
                                                <div className="flex items-center gap-2 mt-1 text-[9px] text-muted-foreground">
                                                  <span>{round.groups} Groups</span>
                                                  <span>&bull;</span>
                                                  <span>{round.matchesPerGroup} Matches</span>
                                                </div>
                                              </div>
                                            </div>
                                          );
                                        })}

                                        {/* Winner */}
                                        <div className="flex items-center">
                                          <ArrowRight className="h-5 w-5 text-yellow-500 mx-2" />
                                          <div className="flex flex-col items-center p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30 min-w-[100px]">
                                            <Trophy className="h-5 w-5 text-yellow-500 mb-1" />
                                            <span className="text-lg font-bold text-yellow-500">
                                              1
                                            </span>
                                            <span className="text-[10px] text-muted-foreground uppercase">
                                              Winner
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>

                                  {/* Round Details Summary */}
                                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                    {bracketCustomRounds.map((round, ri) => {
                                      const teamsIn = round.groups * round.teamsPerGroup;
                                      const teamsOut = round.advancePerGroup * round.groups;
                                      const eliminated = teamsIn - teamsOut;
                                      return (
                                        <div
                                          key={ri}
                                          className={`p-2 rounded-lg border text-center ${
                                            ri === 0
                                              ? 'bg-accent/10 border-accent/30'
                                              : 'bg-secondary/10 border-border/30'
                                          }`}
                                        >
                                          <div className="text-[10px] font-medium text-muted-foreground uppercase mb-1">
                                            {round.name}
                                          </div>
                                          <div className="flex items-center justify-center gap-2 text-xs">
                                            <span className="text-foreground font-medium">
                                              {teamsIn}
                                            </span>
                                            <ArrowRight className="h-3 w-3 text-muted-foreground" />
                                            <span className="text-accent font-bold">
                                              {teamsOut}
                                            </span>
                                          </div>
                                          <div className="text-[9px] text-red-400 mt-0.5">
                                            -{eliminated} eliminated
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>

                                  {/* Info note */}
                                  <p className="text-xs text-muted-foreground text-center">
                                    This is a visual reference for your tournament structure. Use
                                    "Start Round" above to configure the actual round.
                                  </p>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Bracket Preview Section (shown when bracket has been generated from template) */}
                          {bracketGenerated && !showBracketGenerator && (
                            <div className="mt-cyber-card p-4">
                              <h4 className="text-sm font-bold flex items-center gap-2 text-green-400 mb-3">
                                <svg
                                  className="h-4 w-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2.5"
                                    d="M5 13l4 4L19 7"
                                  />
                                </svg>
                                Bracket Generated — Quick Overview
                              </h4>
                              <div className="flex items-center gap-2 flex-wrap">
                                {bracketCustomRounds.map((round, ri) => (
                                  <div key={ri} className="flex items-center gap-1.5">
                                    <div
                                      className={`px-3 py-2 rounded-lg border text-center ${ri === 0 ? 'bg-accent/10 border-accent/30' : 'bg-secondary/10 border-border/30'}`}
                                    >
                                      <div className="text-xs font-bold">{round.name}</div>
                                      <div className="text-[9px] text-muted-foreground">
                                        {round.groups * round.teamsPerGroup} teams
                                      </div>
                                      <div className="text-[9px] text-muted-foreground">
                                        {round.groups}G × {round.matchesPerGroup}M
                                      </div>
                                      <div className="text-[9px] text-green-400">
                                        {round.advancePerGroup * round.groups} advance
                                      </div>
                                    </div>
                                    {ri < bracketCustomRounds.length - 1 && (
                                      <ArrowRight className="h-4 w-4 text-muted-foreground" />
                                    )}
                                  </div>
                                ))}
                                <div className="flex items-center gap-1.5">
                                  <ArrowRight className="h-4 w-4 text-yellow-500" />
                                  <div className="px-3 py-2 rounded-lg bg-yellow-500/10 border border-yellow-500/30 text-center">
                                    <Trophy className="h-4 w-4 text-yellow-500 mx-auto" />
                                    <div className="text-[9px] text-muted-foreground">Winner</div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <>
                          {/* Group / Lobby Selector */}
                          <div className="mb-6">
                            <div className="flex justify-between items-center mb-4">
                              <div>
                                <h3 className="text-lg font-bold text-foreground">
                                  {tournament?.is_5v5 ? 'Lobbies' : 'Groups'}
                                </h3>
                                <p className="text-muted-foreground text-xs mt-0.5">
                                  {roundGroups.length} {tournament?.is_5v5 ? 'lobby' : 'group'}
                                  {roundGroups.length !== 1 ? 's' : ''} active
                                </p>
                              </div>
                              <div className="flex items-center gap-2">
                                {roundGroups.length > 0 &&
                                  getRoundStatus(currentRound) === 'pre_configured' && (
                                    <button
                                      onClick={handleShuffleGroups}
                                      className="px-3 py-1.5 text-xs font-medium text-purple-400 border border-purple-500/20 rounded-lg hover:bg-purple-500/10 transition-colors"
                                    >
                                      Shuffle
                                    </button>
                                  )}
                                {roundGroups.length > 0 && (
                                  <button
                                    onClick={handleResetRound}
                                    className="px-3 py-1.5 text-xs font-medium text-red-400 border border-red-500/20 rounded-lg hover:bg-red-500/10 transition-colors"
                                  >
                                    Reconfigure
                                  </button>
                                )}
                              </div>
                            </div>

                            {tournament?.is_5v5 ? (
                              /* ── 5v5 Lovable-style lobby cards ── */
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 items-start">
                                {roundGroups.map((group, gi) => {
                                  const lobbyMapNames = [
                                    'Bind',
                                    'Haven',
                                    'Split',
                                    'Ascent',
                                    'Icebox',
                                    'Breeze',
                                    'Fracture',
                                    'Pearl',
                                    'Lotus',
                                    'Sunset',
                                    'Abyss',
                                  ];
                                  const mapName =
                                    group.matches?.[0]?.map_name ||
                                    lobbyMapNames[gi % lobbyMapNames.length];
                                  const hasLiveMatch = group.matches?.some(
                                    (m) => m.status === 'ongoing'
                                  );
                                  const completedMatches =
                                    group.matches?.filter((m) => m.status === 'completed').length ||
                                    0;
                                  const totalMatches = group.matches?.length || 0;
                                  const allComplete =
                                    totalMatches > 0 && completedMatches === totalMatches;
                                  const teamA = group.teams?.[0];
                                  const teamB = group.teams?.[1];
                                  const getTag = (t) =>
                                    (t?.team_name || t?.name || '???')
                                      .substring(0, 3)
                                      .toUpperCase();
                                  const getName = (t) =>
                                    t?.team_name || t?.name || `Team ${t?.id || '?'}`;
                                  const credMatch =
                                    group.matches?.find((m) => m.match_id) || group.matches?.[0];
                                  const roomId = credMatch?.match_id || group.room_id;
                                  const password = credMatch?.match_password || group.password;
                                  // Winner + scores from submitted scores
                                  const doneMatch = group.matches?.find(
                                    (m) => m.status === 'completed' && m.scores_submitted
                                  );
                                  const scores = doneMatch?.scores || [];
                                  const hasScores =
                                    scores.length > 0 && scores.some((s) => s.total_points > 0);
                                  const winnerScore = hasScores
                                    ? scores.reduce(
                                        (a, b) => (b.total_points > a.total_points ? b : a),
                                        scores[0]
                                      )
                                    : null;
                                  const getScoreFor = (team) =>
                                    scores.find(
                                      (s) => s.team_name === (team?.team_name || team?.name)
                                    );

                                  return (
                                    <div
                                      key={group.id}
                                      className={`bg-card rounded-xl border transition-all ${
                                        allComplete
                                          ? 'border-green-500/30'
                                          : hasLiveMatch
                                            ? 'border-destructive/50'
                                            : 'border-border/50'
                                      }`}
                                    >
                                      {/* Card Header */}
                                      <div className="flex items-center justify-between p-3 sm:p-4 pb-2">
                                        <div className="flex items-center gap-2">
                                          <div className="p-1.5 rounded bg-accent/10">
                                            <Swords className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-accent" />
                                          </div>
                                          <span className="font-semibold text-sm sm:text-base">
                                            {group.group_name}
                                          </span>
                                          <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                                            <svg
                                              className="h-2.5 w-2.5"
                                              fill="none"
                                              stroke="currentColor"
                                              viewBox="0 0 24 24"
                                            >
                                              <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth="2"
                                                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                                              />
                                              <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth="2"
                                                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                                              />
                                            </svg>
                                            {mapName}
                                          </span>
                                        </div>
                                        <span
                                          className={`inline-flex items-center text-[10px] sm:text-xs px-2 py-0.5 rounded-full border font-medium ${
                                            allComplete
                                              ? 'bg-green-500/20 text-green-500 border-green-500/30'
                                              : hasLiveMatch
                                                ? 'bg-destructive/20 text-destructive border-destructive/30 animate-pulse'
                                                : 'bg-secondary/50 text-muted-foreground border-border/40'
                                          }`}
                                        >
                                          {allComplete
                                            ? 'Complete'
                                            : hasLiveMatch
                                              ? 'LIVE'
                                              : 'Pending'}
                                        </span>
                                      </div>

                                      {/* Teams */}
                                      <div className="px-3 sm:px-4 space-y-2 pb-3 sm:pb-4">
                                        {/* Team A */}
                                        {(() => {
                                          const scoreA = getScoreFor(teamA);
                                          const isWinnerA =
                                            winnerScore &&
                                            winnerScore.team_name ===
                                              (teamA?.team_name || teamA?.name);
                                          return (
                                            <div
                                              className={`flex items-center justify-between p-2 sm:p-2.5 rounded-lg border transition-all ${isWinnerA ? 'bg-green-500/10 border-green-500/50' : 'bg-secondary/20 border-border/30'}`}
                                            >
                                              <div className="flex items-center gap-2">
                                                <span className="inline-flex items-center justify-center px-2 py-0.5 rounded text-[10px] font-bold bg-accent/15 text-accent shrink-0">
                                                  {getTag(teamA)}
                                                </span>
                                                <span className="font-medium text-xs sm:text-sm">
                                                  {getName(teamA)}
                                                </span>
                                                {isWinnerA && (
                                                  <Trophy className="h-3 w-3 text-yellow-500" />
                                                )}
                                              </div>
                                              {scoreA && (
                                                <span className="font-bold text-sm sm:text-base text-foreground">
                                                  {scoreA.total_points}
                                                </span>
                                              )}
                                            </div>
                                          );
                                        })()}

                                        {/* VS divider */}
                                        <div className="flex items-center justify-center">
                                          <span className="text-xs text-muted-foreground px-2">
                                            VS
                                          </span>
                                        </div>

                                        {/* Team B */}
                                        {teamB ? (
                                          (() => {
                                            const scoreB = getScoreFor(teamB);
                                            const isWinnerB =
                                              winnerScore &&
                                              winnerScore.team_name ===
                                                (teamB?.team_name || teamB?.name);
                                            return (
                                              <div
                                                className={`flex items-center justify-between p-2 sm:p-2.5 rounded-lg border transition-all ${isWinnerB ? 'bg-green-500/10 border-green-500/50' : 'bg-secondary/20 border-border/30'}`}
                                              >
                                                <div className="flex items-center gap-2">
                                                  <span className="inline-flex items-center justify-center px-2 py-0.5 rounded text-[10px] font-bold bg-accent/15 text-accent shrink-0">
                                                    {getTag(teamB)}
                                                  </span>
                                                  <span className="font-medium text-xs sm:text-sm">
                                                    {getName(teamB)}
                                                  </span>
                                                  {isWinnerB && (
                                                    <Trophy className="h-3 w-3 text-yellow-500" />
                                                  )}
                                                </div>
                                                {scoreB && (
                                                  <span className="font-bold text-sm sm:text-base text-foreground">
                                                    {scoreB.total_points}
                                                  </span>
                                                )}
                                              </div>
                                            );
                                          })()
                                        ) : (
                                          <div className="flex items-center p-2 sm:p-2.5 rounded-lg bg-secondary/10 border border-border/20 opacity-50">
                                            <span className="text-xs text-accent italic">
                                              BYE — Advances free
                                            </span>
                                          </div>
                                        )}

                                        {/* Credentials (if available) */}
                                        {roomId && (
                                          <div className="p-2 sm:p-3 bg-accent/10 rounded-lg border border-accent/20">
                                            <span className="text-[10px] sm:text-xs font-medium text-accent flex items-center gap-1 mb-2">
                                              <svg
                                                className="h-3 w-3"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                              >
                                                <path
                                                  strokeLinecap="round"
                                                  strokeLinejoin="round"
                                                  strokeWidth="2"
                                                  d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
                                                />
                                              </svg>
                                              Credentials
                                            </span>
                                            <div
                                              className={`grid gap-2 ${password && tournament?.requires_password ? 'grid-cols-2' : 'grid-cols-1'}`}
                                            >
                                              <div className="flex items-center justify-between bg-background/50 px-2 py-1.5 rounded">
                                                <div>
                                                  <div className="text-[10px] text-muted-foreground">
                                                    Room ID
                                                  </div>
                                                  <div className="font-mono font-semibold text-xs sm:text-sm">
                                                    {roomId}
                                                  </div>
                                                </div>
                                                <button
                                                  type="button"
                                                  className="ml-2 p-1 rounded hover:bg-secondary/50 text-muted-foreground hover:text-foreground transition-colors"
                                                  onClick={() =>
                                                    navigator.clipboard?.writeText(roomId)
                                                  }
                                                  title="Copy Room ID"
                                                >
                                                  <Copy className="h-3 w-3" />
                                                </button>
                                              </div>
                                              {password && (
                                                <div className="flex items-center justify-between bg-background/50 px-2 py-1.5 rounded">
                                                  <div>
                                                    <div className="text-[10px] text-muted-foreground">
                                                      Password
                                                    </div>
                                                    <div className="font-mono font-semibold text-xs sm:text-sm">
                                                      {password}
                                                    </div>
                                                  </div>
                                                  <button
                                                    type="button"
                                                    className="ml-2 p-1 rounded hover:bg-secondary/50 text-muted-foreground hover:text-foreground transition-colors"
                                                    onClick={() =>
                                                      navigator.clipboard?.writeText(password)
                                                    }
                                                    title="Copy Password"
                                                  >
                                                    <Copy className="h-3 w-3" />
                                                  </button>
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        )}

                                        {/* Action Buttons */}
                                        <div className="flex gap-2 pt-1">
                                          {allComplete ? (
                                            hasScores && winnerScore ? (
                                              /* Winner declared — show winner banner */
                                              <div className="w-full flex items-center justify-center gap-2 py-2.5 sm:py-3 bg-green-500/15 rounded-lg text-xs sm:text-sm text-green-500 font-semibold border border-green-500/20">
                                                <Trophy className="h-3.5 w-3.5 text-yellow-500" />
                                                Winner: {winnerScore.team_name}
                                              </div>
                                            ) : (
                                              /* No scores yet — show Match Complete + Enter Points */
                                              <>
                                                <div className="flex-1 flex items-center justify-center gap-1.5 py-2.5 sm:py-3 bg-green-500/15 rounded-lg text-xs sm:text-sm text-green-500 font-semibold border border-green-500/20">
                                                  <svg
                                                    className="h-3.5 w-3.5"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                  >
                                                    <path
                                                      strokeLinecap="round"
                                                      strokeLinejoin="round"
                                                      strokeWidth="2"
                                                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                                    />
                                                  </svg>
                                                  Match Complete
                                                </div>
                                                <button
                                                  onClick={() => {
                                                    setSelectedGroup(group);
                                                    const completedMatch = group.matches?.find(
                                                      (m) => m.status === 'completed'
                                                    );
                                                    if (completedMatch)
                                                      handleEnterPoints(completedMatch);
                                                  }}
                                                  className="flex-1 flex items-center justify-center gap-1.5 py-2.5 sm:py-3 text-xs sm:text-sm rounded-lg font-semibold transition-colors bg-secondary/60 hover:bg-secondary text-foreground border border-border/40"
                                                >
                                                  <Trophy className="h-3.5 w-3.5" />
                                                  Enter Points
                                                </button>
                                              </>
                                            )
                                          ) : hasLiveMatch ? (
                                            <button
                                              onClick={() => {
                                                setSelectedGroup(group);
                                                const liveMatch = group.matches?.find(
                                                  (m) => m.status === 'ongoing'
                                                );
                                                if (liveMatch) handleEndMatch(liveMatch);
                                              }}
                                              className="w-full flex items-center justify-center gap-2 py-2.5 sm:py-3 text-xs sm:text-sm rounded-lg font-semibold transition-colors text-white bg-red-500 hover:bg-red-600"
                                            >
                                              <svg
                                                className="h-3.5 w-3.5 sm:h-4 sm:w-4"
                                                fill="currentColor"
                                                viewBox="0 0 24 24"
                                              >
                                                <rect x="6" y="6" width="12" height="12" rx="1" />
                                              </svg>
                                              End Match
                                            </button>
                                          ) : (
                                            <button
                                              onClick={() => {
                                                setSelectedGroup(group);
                                                const firstMatch = group.matches?.[0];
                                                if (firstMatch) handleStartMatch(firstMatch);
                                              }}
                                              className="w-full flex items-center justify-center gap-2 py-2.5 sm:py-3 text-xs sm:text-sm rounded-lg font-semibold transition-colors text-white bg-accent hover:bg-accent/90"
                                            >
                                              <Play className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                                              Start Match
                                            </button>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              /* ── BR mode: small group selector grid ── */
                              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 sm:gap-3">
                                {roundGroups.map((group, gi) => {
                                  const completedMatches =
                                    group.matches?.filter((m) => m.status === 'completed').length ||
                                    0;
                                  const totalMatches = group.matches?.length || 0;
                                  const progressPercent =
                                    totalMatches > 0 ? (completedMatches / totalMatches) * 100 : 0;
                                  const isSelected = selectedGroup?.id === group.id;
                                  const hasLiveMatch = group.matches?.some(
                                    (m) => m.status === 'ongoing'
                                  );
                                  const allComplete =
                                    totalMatches > 0 && completedMatches === totalMatches;
                                  const hasInProgress = completedMatches > 0 && !allComplete;

                                  return (
                                    <div key={group.id} className="relative group/card">
                                      <div
                                        onClick={() => setSelectedGroup(group)}
                                        className={`relative p-3 sm:p-4 rounded-lg border-2 cursor-pointer transition-all hover:scale-[1.02] ${
                                          isSelected
                                            ? 'border-accent bg-accent/10 ring-2 ring-accent/30'
                                            : allComplete
                                              ? 'border-green-500/50 bg-green-500/10'
                                              : hasLiveMatch
                                                ? 'border-red-500/50 bg-red-500/10 animate-pulse'
                                                : hasInProgress
                                                  ? 'border-yellow-500/50 bg-yellow-500/10'
                                                  : 'border-border/50 bg-secondary/30 hover:border-accent/50'
                                        }`}
                                      >
                                        <div className="absolute top-1.5 right-1.5 sm:top-2 sm:right-2">
                                          {allComplete && (
                                            <svg
                                              className="h-4 w-4 sm:h-5 sm:w-5 text-green-500"
                                              fill="none"
                                              stroke="currentColor"
                                              viewBox="0 0 24 24"
                                            >
                                              <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth="2"
                                                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                              />
                                            </svg>
                                          )}
                                          {hasLiveMatch && (
                                            <div className="flex items-center gap-1">
                                              <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-red-500 rounded-full animate-pulse" />
                                              <span className="text-[8px] sm:text-[10px] text-red-400 font-bold">
                                                LIVE
                                              </span>
                                            </div>
                                          )}
                                        </div>
                                        <div
                                          className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] sm:text-xs font-semibold mb-1.5 sm:mb-2 ${
                                            allComplete
                                              ? 'text-green-400 border-green-500'
                                              : hasLiveMatch
                                                ? 'text-red-400 border-red-500'
                                                : 'text-accent border-accent'
                                          }`}
                                        >
                                          {group.group_name}
                                        </div>
                                        <div className="text-xs sm:text-sm text-muted-foreground mb-1.5 sm:mb-2">
                                          {group.teams?.length || 0} teams
                                        </div>
                                        <div className="h-1.5 sm:h-2 bg-secondary/50 rounded-full overflow-hidden">
                                          <div
                                            className={`h-full transition-all duration-500 ${allComplete ? 'bg-green-500' : 'bg-accent'}`}
                                            style={{ width: `${progressPercent}%` }}
                                          />
                                        </div>
                                        <div className="text-[10px] sm:text-xs text-muted-foreground mt-1">
                                          {completedMatches}/{totalMatches} matches
                                        </div>
                                      </div>
                                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-50 hidden group-hover/card:block pointer-events-none">
                                        <div className="bg-popover border border-border/50 rounded-lg p-3 shadow-xl min-w-[160px] text-xs space-y-1.5">
                                          <p className="font-semibold text-foreground">
                                            {group.group_name} Summary
                                          </p>
                                          <div className="flex justify-between gap-4">
                                            <span className="text-muted-foreground">Teams:</span>
                                            <span className="font-medium">
                                              {group.teams?.length || 0}
                                            </span>
                                          </div>
                                          <div className="flex justify-between gap-4">
                                            <span className="text-muted-foreground">Status:</span>
                                            <span
                                              className={`font-medium ${allComplete ? 'text-green-400' : hasLiveMatch ? 'text-red-400' : hasInProgress ? 'text-yellow-400' : 'text-muted-foreground'}`}
                                            >
                                              {allComplete
                                                ? 'Complete'
                                                : hasLiveMatch
                                                  ? 'Live'
                                                  : hasInProgress
                                                    ? 'In Progress'
                                                    : 'Idle'}
                                            </span>
                                          </div>
                                          <div className="flex justify-between gap-4">
                                            <span className="text-muted-foreground">Progress:</span>
                                            <span className="font-medium">
                                              {completedMatches}/{totalMatches} matches
                                            </span>
                                          </div>
                                        </div>
                                        <div className="w-2 h-2 bg-popover border-b border-r border-border/50 rotate-45 mx-auto -mt-1" />
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>

                          {/* Selected Group Management View — BR only (5v5 uses lobby cards above) */}
                          {selectedGroup && !tournament?.is_5v5 && (
                            <div className="animate-in fade-in slide-in-from-top-4 duration-700">
                              <GroupManagementView
                                group={selectedGroup}
                                onStartMatch={handleStartMatch}
                                onEndMatch={handleEndMatch}
                                onEnterPoints={handleEnterPoints}
                                onTeamClick={handleTeamClick}
                                initialMatchIndex={lastSelectedMatch - 1}
                                onMatchIndexChange={(index) => setLastSelectedMatch(index + 1)}
                                roundNumber={currentRound}
                                qualifyingTeams={selectedGroup.qualifying_teams}
                                tournamentId={id}
                                is5v5Game={tournament?.is_5v5 || false}
                              />
                            </div>
                          )}

                          {/* Config Summary Cards — BR only, not 5v5 */}
                          {!tournament?.is_5v5 &&
                            (() => {
                              const allGroupsCompleted =
                                roundGroups.length > 0 &&
                                roundGroups.every((g) => g.status === 'completed');
                              const isFinalRound =
                                tournament.rounds.findIndex((r) => r.round === currentRound) ===
                                tournament.rounds.length - 1;
                              const teamsPerGroupVal =
                                roundGroups.length > 0
                                  ? Math.max(...roundGroups.map((g) => g.teams?.length || 0))
                                  : currentRoundConfig?.max_teams || '—';
                              const teamsQualifyVal =
                                roundGroups[0]?.qualifying_teams ?? qualifyingTeams;
                              const liveMatchesCount = roundGroups.reduce(
                                (acc, g) =>
                                  acc +
                                  (g.matches?.filter((m) => m.status === 'ongoing').length || 0),
                                0
                              );
                              const completedGroupsCount = roundGroups.filter(
                                (g) => g.status === 'completed'
                              ).length;

                              return (
                                <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
                                  {/* Round Config */}
                                  <div className="bg-card/60 border border-accent/20 rounded-lg p-3 sm:p-4 hover:border-accent/40 transition-colors">
                                    <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                                      <Settings className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
                                      <span className="font-semibold text-sm sm:text-base">
                                        Round Config
                                      </span>
                                    </div>
                                    <div className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm">
                                      <div className="flex justify-between">
                                        <span className="text-muted-foreground">
                                          Teams per Group
                                        </span>
                                        <span className="font-bold text-accent">
                                          {teamsPerGroupVal}
                                        </span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-muted-foreground">Teams Qualify</span>
                                        <span className="font-bold text-green-400">
                                          {teamsQualifyVal}
                                        </span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-muted-foreground">Total Groups</span>
                                        <span className="font-bold">{roundGroups.length}</span>
                                      </div>
                                    </div>
                                  </div>

                                  {/* Progress */}
                                  <div className="bg-card/60 border border-accent/20 rounded-lg p-3 sm:p-4 hover:border-accent/40 transition-colors">
                                    <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                                      <Trophy className="h-4 w-4 sm:h-5 sm:w-5 text-accent" />
                                      <span className="font-semibold text-sm sm:text-base">
                                        Progress
                                      </span>
                                    </div>
                                    <div className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm">
                                      <div className="flex justify-between">
                                        <span className="text-muted-foreground">
                                          Groups Complete
                                        </span>
                                        <span className="font-bold text-green-400">
                                          {completedGroupsCount}/{roundGroups.length}
                                        </span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-muted-foreground">Live Matches</span>
                                        <span className="font-bold text-red-400">
                                          {liveMatchesCount}
                                        </span>
                                      </div>
                                    </div>
                                  </div>

                                  {/* Next Action */}
                                  <div className="bg-white/5 border border-white/10 rounded-lg p-3 sm:p-4 flex flex-col justify-center items-center min-h-[80px]">
                                    {allGroupsCompleted ? (
                                      <button
                                        onClick={() => handleAdvanceRound()}
                                        className="flex items-center justify-center gap-2 py-2.5 px-6 rounded-lg text-sm font-bold bg-white hover:bg-gray-100 text-black transition-colors shadow-sm"
                                      >
                                        {isFinalRound
                                          ? 'End Tournament'
                                          : `Advance to ${roundNames?.[String(currentRound + 1)] || `Round ${currentRound + 1}`}`}
                                        <ChevronRight className="h-4 w-4" />
                                      </button>
                                    ) : (
                                      <div className="text-center text-muted-foreground text-xs sm:text-sm">
                                        Complete all groups to finalize round
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })()}

                          {/* Round Results — BR only */}
                          {!tournament?.is_5v5 && (
                            <div className="mt-6 space-y-3">
                              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                                Round Results
                              </h4>
                              {tournament?.rounds?.map((r, idx) => {
                                const rNum = r.round;
                                if (rNum > currentRound) return null;
                                const rName = roundNames?.[String(rNum)] || `Round ${rNum}`;
                                const rGroups =
                                  idx === currentRound - 1
                                    ? roundGroups
                                    : pastRoundsGroups[rNum] || [];
                                return (
                                  <div key={rNum} className="space-y-1.5">
                                    <p className="text-xs font-medium text-purple-400">{rName}</p>
                                    <div className="flex flex-wrap gap-1.5">
                                      {rGroups.map((g, gi) => {
                                        const gComplete = g.status === 'completed';
                                        const hasData = g.matches?.some(
                                          (m) => m.status === 'completed'
                                        );
                                        return (
                                          <button
                                            key={gi}
                                            disabled={!hasData && !gComplete}
                                            onClick={() => {
                                              setSelectedGroup(g);
                                            }}
                                            className={`flex items-center gap-1 px-2.5 py-1 rounded text-[10px] font-medium border transition-colors ${
                                              gComplete
                                                ? 'bg-green-600 hover:bg-green-700 text-white border-green-600'
                                                : hasData
                                                  ? 'border-purple-500/50 text-purple-400 hover:bg-purple-500/10'
                                                  : 'border-border/30 text-muted-foreground/40 cursor-not-allowed'
                                            }`}
                                          >
                                            <svg
                                              className="w-3 h-3"
                                              fill="none"
                                              stroke="currentColor"
                                              viewBox="0 0 24 24"
                                            >
                                              <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth="2"
                                                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                              />
                                              <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth="2"
                                                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                              />
                                            </svg>
                                            {rName} Group {String.fromCharCode(65 + gi)}
                                          </button>
                                        );
                                      })}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Round Config Modal */}
      <RoundConfigModal
        isOpen={showRoundConfigModal}
        onClose={() => setShowRoundConfigModal(false)}
        onSubmit={handleSubmitRoundConfig}
        onReset={handleResetRound}
        isRoundConfigured={roundGroups.length > 0}
        roundNumber={currentRound === 0 ? 1 : currentRound}
        totalTeams={
          currentRound <= 1
            ? registrations.filter((r) => r.status === 'confirmed' || r.status === 'approved')
                .length
            : tournament.selected_teams?.[String(currentRound - 1)]?.length || 0
        }
        isFinalRound={(currentRound === 0 ? 1 : currentRound) === tournament.rounds?.length}
        tournament={tournament}
        teamList={
          currentRound <= 1
            ? registrations
                .filter((r) => r.status === 'confirmed' || r.status === 'approved')
                .map((r) => ({ team_name: r.team_name, id: r.team }))
            : tournament.selected_teams?.[String(currentRound - 1)] || []
        }
      />

      {/* Group Confirmation Modal — shown between RoundConfigModal and API call */}
      <GroupConfirmModal
        isOpen={showGroupConfirmModal}
        onClose={() => {
          setShowGroupConfirmModal(false);
          // Go back to config modal so user can adjust
          setShowRoundConfigModal(true);
        }}
        onConfirm={(shuffleRequested) =>
          handleConfirmedRoundConfig(pendingRoundConfig?.configData, shuffleRequested)
        }
        roundName={tournament?.round_names?.[String(currentRound)] || `Round ${currentRound}`}
        groups={pendingRoundConfig?.pendingGroups || []}
        teamsPerGroup={pendingRoundConfig?.configData?.teams_per_group}
        qualifying={pendingRoundConfig?.configData?.qualifying_per_group}
        matches={pendingRoundConfig?.configData?.matches_per_group}
        loading={loading}
      />

      {/* Match Config Modal */}
      <MatchConfigModal
        isOpen={showMatchConfigModal}
        onClose={() => setShowMatchConfigModal(false)}
        onSubmit={handleSubmitMatchConfig}
        onSaveOnly={handleSaveOnlyMatchConfig}
        mode={currentMatch?.status === 'ongoing' ? 'edit' : 'start'}
        matchNumber={currentMatch?.match_number}
        groupName={selectedGroup?.group_name}
        initialMatchId={currentMatch?.match_id || ''}
        initialMatchPassword={currentMatch?.match_password || ''}
        is5v5Game={tournament?.is_5v5 || false}
        requiresPassword={tournament?.requires_password ?? true}
        teamA={selectedGroup?.teams?.[0]}
        teamB={selectedGroup?.teams?.[1]}
      />

      {/* Match Points Modal */}
      <MatchPointsModal
        isOpen={showMatchPointsModal}
        onClose={() => setShowMatchPointsModal(false)}
        onSubmit={handleSubmitPoints}
        match={currentMatch}
        teams={selectedGroup?.teams || []}
        is5v5Game={tournament?.is_5v5 || false}
        tournamentId={tournament?.id}
        matchId={currentMatch?.id}
      />

      {/* Eliminated Teams Modal */}
      <EliminatedTeamsModal
        isOpen={showEliminatedModal}
        onClose={() => setShowEliminatedModal(false)}
        onProceed={handleProceedToNextRound}
        roundData={eliminatedData}
        tournament={tournament}
      />

      {/* Points Table Modal */}
      <PointsTableModal
        isOpen={showPointsTable}
        onClose={() => setShowPointsTable(false)}
        tournament={tournament}
        currentRound={currentRound}
        initialMatch={lastSelectedMatch}
        onMatchChange={setLastSelectedMatch}
      />

      {/* Bulk Schedule Modal */}
      <BulkScheduleModal
        isOpen={showBulkSchedule}
        onClose={() => setShowBulkSchedule(false)}
        tournament={tournament}
        defaultRound={bulkScheduleRound ?? currentRound}
        onSuccess={() => {
          showToast('Matches scheduled successfully!', 'success');
          fetchRoundGroups(bulkScheduleRound ?? currentRound);
        }}
      />

      {/* Teams Modal */}
      {showTeamsModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="mt-cyber-card w-full max-w-2xl flex flex-col max-h-[85vh] overflow-hidden">
            <div className="p-5 border-b border-[hsl(var(--border)/0.3)]">
              <div className="flex justify-between items-center mb-3">
                <div>
                  <h2 className="text-lg font-bold text-[hsl(var(--foreground))]">
                    Registered Teams
                  </h2>
                  <p className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">
                    {tournament.title}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowTeamsModal(false);
                    setTeamsTab('active');
                  }}
                  className="w-8 h-8 rounded-lg border border-[hsl(var(--border)/0.5)] flex items-center justify-center text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--secondary)/0.5)] transition-colors text-sm"
                >
                  ✕
                </button>
              </div>

              {/* Tabs */}
              <div className="flex gap-2">
                <button
                  onClick={() => setTeamsTab('active')}
                  className={`px-4 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    teamsTab === 'active'
                      ? 'bg-[hsl(var(--accent)/0.15)] text-[hsl(var(--accent))] border border-[hsl(var(--accent)/0.3)]'
                      : 'text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--secondary)/0.5)] border border-transparent'
                  }`}
                >
                  Active (
                  {
                    registrations.filter(
                      (r) =>
                        r.status === 'confirmed' ||
                        r.status === 'approved' ||
                        r.status === 'pending'
                    ).length
                  }
                  )
                </button>
                <button
                  onClick={() => setTeamsTab('rejected')}
                  className={`px-4 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    teamsTab === 'rejected'
                      ? 'bg-red-500/15 text-red-400 border border-red-500/30'
                      : 'text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--secondary)/0.5)] border border-transparent'
                  }`}
                >
                  Rejected ({registrations.filter((r) => r.status === 'rejected').length})
                </button>
              </div>
            </div>

            <div className="p-5 overflow-y-auto custom-scrollbar flex-1">
              {teamsTab === 'active' ? (
                // Active Teams Tab
                registrations.filter(
                  (reg) =>
                    reg.status === 'confirmed' ||
                    reg.status === 'approved' ||
                    reg.status === 'pending'
                ).length === 0 ? (
                  <div className="text-center py-16">
                    <p className="text-sm text-[hsl(var(--muted-foreground))]">
                      No active teams registered yet
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Approve All Button */}
                    {registrations.filter((reg) => reg.status === 'pending').length > 0 && (
                      <div className="mb-4 flex justify-end">
                        <button
                          onClick={async () => {
                            const pendingTeams = registrations.filter(
                              (r) => r.status === 'pending'
                            );
                            setConfirmModal({
                              isOpen: true,
                              title: 'Approve All Pending',
                              message: `Approve all ${pendingTeams.length} pending teams?`,
                              confirmText: 'Approve All',
                              type: 'primary',
                              onConfirm: async () => {
                                try {
                                  await Promise.all(
                                    pendingTeams.map((reg) =>
                                      tournamentAPI.updateTeamStatus(
                                        tournament.id,
                                        reg.id,
                                        'confirmed'
                                      )
                                    )
                                  );
                                  showToast(`Approved ${pendingTeams.length} teams!`);
                                  fetchTournamentData();
                                } catch (error) {
                                  showToast('Operation failed partially.', 'error');
                                }
                              },
                            });
                          }}
                          className="px-4 py-2 bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent)/0.9)] text-white rounded-lg text-xs font-semibold transition-colors flex items-center gap-1.5"
                        >
                          Approve All (
                          {registrations.filter((reg) => reg.status === 'pending').length})
                        </button>
                      </div>
                    )}
                    <div className="grid gap-2">
                      {registrations
                        .filter(
                          (reg) =>
                            reg.status === 'confirmed' ||
                            reg.status === 'approved' ||
                            reg.status === 'pending'
                        )
                        .map((reg) => (
                          <div key={reg.id} className="info-grid-card p-3">
                            <div className="flex items-center justify-between gap-3">
                              <div className="flex items-center gap-3 flex-1 min-w-0">
                                <div className="w-9 h-9 rounded-full bg-[hsl(var(--accent)/0.15)] border border-[hsl(var(--accent)/0.3)] flex items-center justify-center text-[hsl(var(--accent))] font-bold text-sm flex-shrink-0">
                                  {(reg.team_name || reg.player?.user?.username || '?')
                                    .charAt(0)
                                    .toUpperCase()}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <h3 className="text-sm font-semibold text-[hsl(var(--foreground))] truncate">
                                    {reg.team_name || reg.player?.user?.username || 'Unknown'}
                                  </h3>
                                  <p className="text-xs text-[hsl(var(--muted-foreground))]">
                                    Lead: {reg.player?.user?.username || 'Unknown'}
                                  </p>
                                  {reg.ign_submissions &&
                                    Object.keys(reg.ign_submissions).length > 0 && (
                                      <p
                                        className="text-[10px] mt-0.5"
                                        style={{ color: '#10b981' }}
                                      >
                                        {Object.entries(reg.ign_submissions)
                                          .map(([uname, ign]) => `${uname} → ${ign}`)
                                          .join(' · ')}
                                      </p>
                                    )}
                                </div>
                              </div>

                              <div className="flex items-center gap-2">
                                {reg.status === 'pending' ? (
                                  <>
                                    <button
                                      onClick={async () => {
                                        try {
                                          await tournamentAPI.updateTeamStatus(
                                            tournament.id,
                                            reg.id,
                                            'confirmed'
                                          );
                                          showToast('Team approved');
                                          fetchTournamentData();
                                        } catch (error) {
                                          showToast('Approval failed');
                                        }
                                      }}
                                      className="w-7 h-7 bg-green-600 hover:bg-green-500 text-white rounded-md flex items-center justify-center transition-colors text-xs"
                                    >
                                      ✓
                                    </button>
                                    <button
                                      onClick={async () => {
                                        setConfirmModal({
                                          isOpen: true,
                                          title: 'Reject Team',
                                          message: `Reject ${reg.team_name || reg.player?.user?.username}?`,
                                          confirmText: 'Reject',
                                          type: 'danger',
                                          onConfirm: async () => {
                                            try {
                                              await tournamentAPI.updateTeamStatus(
                                                tournament.id,
                                                reg.id,
                                                'rejected'
                                              );
                                              showToast('Team rejected');
                                              fetchTournamentData();
                                            } catch (error) {
                                              showToast('Operation failed', 'error');
                                            }
                                          },
                                        });
                                      }}
                                      className="w-7 h-7 bg-red-600 hover:bg-red-500 text-white rounded-md flex items-center justify-center transition-colors text-xs"
                                    >
                                      ✕
                                    </button>
                                  </>
                                ) : (
                                  <span className="px-2.5 py-1 rounded-full bg-green-500/15 text-green-400 text-[0.6875rem] font-medium flex items-center gap-1.5">
                                    <span className="w-1.5 h-1.5 bg-green-400 rounded-full"></span>
                                    Approved
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                  </>
                )
              ) : registrations.filter((reg) => reg.status === 'rejected').length === 0 ? (
                <div className="text-center py-16">
                  <p className="text-sm text-[hsl(var(--muted-foreground))]">No rejected teams</p>
                </div>
              ) : (
                <div className="grid gap-2">
                  {registrations
                    .filter((reg) => reg.status === 'rejected')
                    .map((reg) => (
                      <div key={reg.id} className="info-grid-card p-3 opacity-75">
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className="w-9 h-9 rounded-full bg-[hsl(var(--secondary)/0.5)] flex items-center justify-center text-[hsl(var(--muted-foreground))] font-bold text-sm flex-shrink-0">
                              {(reg.team_name || reg.player?.user?.username || '?')
                                .charAt(0)
                                .toUpperCase()}
                            </div>
                            <div className="flex-1 min-w-0">
                              <h3 className="text-sm font-semibold text-[hsl(var(--muted-foreground))] truncate">
                                {reg.team_name || reg.player?.user?.username || 'Unknown'}
                              </h3>
                              <p className="text-xs text-red-400/60">Rejected</p>
                            </div>
                          </div>

                          <button
                            onClick={async () => {
                              try {
                                await tournamentAPI.updateTeamStatus(
                                  tournament.id,
                                  reg.id,
                                  'confirmed'
                                );
                                showToast('Team restored');
                                fetchTournamentData();
                              } catch (error) {
                                showToast('Restore failed');
                              }
                            }}
                            className="mt-action-btn"
                          >
                            Restore
                          </button>
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </div>

            <div className="p-4 border-t border-[hsl(var(--border)/0.3)] flex justify-end">
              <button
                onClick={() => setShowTeamsModal(false)}
                className="px-4 py-2 bg-[hsl(var(--secondary))] hover:bg-[hsl(var(--secondary)/0.8)] text-[hsl(var(--foreground))] rounded-lg text-sm font-medium transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <TeamPlayersModal
        isOpen={showTeamDetails}
        onClose={() => {
          setShowTeamDetails(false);
          setSelectedTeam(null);
        }}
        team={selectedTeam}
        tournamentId={id}
        ignSubmissions={selectedTeam?.ign_submissions || {}}
      />

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText={confirmModal.confirmText}
        type={confirmModal.type}
        onConfirm={confirmModal.onConfirm}
        onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
      />

      <RoundNamesModal
        isOpen={showRoundNamesModal}
        onClose={() => setShowRoundNamesModal(false)}
        numRounds={parseInt(editData.rounds) || 0}
        roundNames={roundNames}
        onSave={handleSaveRoundNames}
      />

      {/* Live Stream URL Dialog */}
      {showLiveUrlDialog && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="mt-cyber-card w-full max-w-md overflow-hidden">
            <div className="p-5 border-b border-[hsl(var(--border)/0.3)]">
              <h2 className="text-lg font-bold text-[hsl(var(--foreground))] flex items-center gap-2">
                <Video className="h-5 w-5 text-red-500" />
                Publish Live Stream
              </h2>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs text-[hsl(var(--muted-foreground))] font-medium mb-1.5">
                  Live Stream URL
                </label>
                <input
                  type="url"
                  placeholder="https://youtube.com/live/..."
                  value={tempLiveUrl}
                  onChange={(e) => setTempLiveUrl(e.target.value)}
                  className="w-full px-3 py-2.5 bg-[hsl(var(--card))] border border-[hsl(var(--border)/0.3)] rounded-lg text-[hsl(var(--foreground))] text-sm focus:outline-none focus:border-[hsl(var(--accent)/0.5)] transition-colors"
                />
                <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1.5">
                  Players will see this link in the tournament details page.
                </p>
              </div>

              {tempLiveUrl && (
                <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30">
                  <p className="text-xs text-[hsl(var(--muted-foreground))] mb-1">Preview:</p>
                  <a
                    href={tempLiveUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-red-400 hover:underline flex items-center gap-1 truncate"
                  >
                    <ExternalLink className="h-3 w-3 flex-shrink-0" />
                    <span className="truncate">{tempLiveUrl}</span>
                  </a>
                </div>
              )}
            </div>
            <div className="p-4 border-t border-[hsl(var(--border)/0.3)] flex flex-col sm:flex-row gap-2 justify-end">
              {tournament.live_link && (
                <button
                  onClick={() => {
                    setTempLiveUrl('');
                    handleSaveLiveUrl();
                  }}
                  className="mt-action-btn text-red-400 border-red-500/30 hover:bg-red-500/10"
                >
                  Remove Stream
                </button>
              )}
              <button onClick={() => setShowLiveUrlDialog(false)} className="mt-action-btn">
                Cancel
              </button>
              <button
                onClick={handleSaveLiveUrl}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-red-600 hover:bg-red-700 text-white transition-colors flex items-center gap-1.5 justify-center"
              >
                <Video className="h-3.5 w-3.5" />
                {tournament.live_link ? 'Update' : 'Publish'} Stream
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tournament Overview Dialog */}
      {showOverviewDialog && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="mt-cyber-card w-full max-w-2xl flex flex-col max-h-[85vh] overflow-hidden">
            <div className="p-5 border-b border-[hsl(var(--border)/0.3)] flex items-center justify-between">
              <h2 className="text-lg font-bold text-[hsl(var(--foreground))] flex items-center gap-2">
                <Info className="h-5 w-5 text-[hsl(var(--accent))]" />
                Tournament Overview
              </h2>
              <button
                onClick={() => setShowOverviewDialog(false)}
                className="w-8 h-8 rounded-lg border border-[hsl(var(--border)/0.5)] flex items-center justify-center text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--secondary)/0.5)] transition-colors text-sm"
              >
                ✕
              </button>
            </div>

            <div className="p-5 overflow-y-auto custom-scrollbar space-y-5">
              {/* Title + Status */}
              <div className="flex items-center justify-between gap-3">
                <h3 className="text-base font-bold text-[hsl(var(--foreground))] truncate">
                  {tournament.title}
                </h3>
                <span
                  className={
                    tournament.status === 'ongoing'
                      ? 'mt-status-live'
                      : tournament.status === 'upcoming'
                        ? 'mt-status-upcoming'
                        : 'mt-status-completed'
                  }
                >
                  {tournament.status}
                </span>
              </div>

              {/* Key Stats */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="mt-stat-tile items-center text-center">
                  <span className="text-xl font-bold text-[hsl(var(--accent))]">
                    {tournament.current_participants}
                  </span>
                  <span className="text-[0.6875rem] text-[hsl(var(--muted-foreground))]">
                    Registered
                  </span>
                </div>
                <div className="mt-stat-tile items-center text-center">
                  <span className="text-xl font-bold text-[hsl(var(--foreground))]">
                    {tournament.max_participants}
                  </span>
                  <span className="text-[0.6875rem] text-[hsl(var(--muted-foreground))]">
                    Max Teams
                  </span>
                </div>
                <div className="mt-stat-tile items-center text-center">
                  <span className="text-xl font-bold text-green-400">
                    ₹{Number(tournament.prize_pool || 0).toLocaleString()}
                  </span>
                  <span className="text-[0.6875rem] text-[hsl(var(--muted-foreground))]">
                    Prize Pool
                  </span>
                </div>
                <div className="mt-stat-tile items-center text-center">
                  <span className="text-xl font-bold text-amber-400">
                    {Math.max(0, tournament.max_participants - tournament.current_participants)}
                  </span>
                  <span className="text-[0.6875rem] text-[hsl(var(--muted-foreground))]">
                    Slots Left
                  </span>
                </div>
              </div>

              {/* Tournament Details Grid */}
              <div className="grid grid-cols-2 gap-2 text-xs">
                {[
                  { label: 'Game', value: tournament.game_name },
                  { label: 'Format', value: tournament.game_mode },
                  { label: 'Entry Fee', value: `₹${tournament.entry_fee}` },
                  {
                    label: 'Prize Pool',
                    value: `₹${Number(tournament.prize_pool || 0).toLocaleString()}`,
                  },
                  {
                    label: 'Registration Start',
                    value: tournament.registration_start
                      ? new Date(tournament.registration_start).toLocaleDateString()
                      : '—',
                  },
                  {
                    label: 'Match Start',
                    value: tournament.tournament_start
                      ? new Date(tournament.tournament_start).toLocaleString()
                      : '—',
                  },
                  {
                    label: 'Current Round',
                    value:
                      currentRound > 0
                        ? roundNames?.[String(currentRound)] || `Round ${currentRound}`
                        : 'Not started',
                  },
                  { label: 'Total Rounds', value: tournament.rounds?.length || 0 },
                  { label: 'Event Mode', value: tournament.event_mode },
                  {
                    label: 'Plan',
                    value:
                      tournament.plan_type?.charAt(0).toUpperCase() +
                      tournament.plan_type?.slice(1),
                  },
                ].map((item, i) => (
                  <div
                    key={i}
                    className="flex justify-between p-2.5 bg-[hsl(var(--secondary)/0.15)] rounded-lg border border-[hsl(var(--border)/0.1)]"
                  >
                    <span className="text-[hsl(var(--muted-foreground))]">{item.label}</span>
                    <span className="font-semibold text-[hsl(var(--foreground))]">
                      {item.value}
                    </span>
                  </div>
                ))}
              </div>

              {/* Revenue Summary */}
              {(() => {
                const totalRevenue =
                  Number(tournament.entry_fee || 0) * tournament.current_participants;
                const prizePool = Number(tournament.prize_pool || 0);
                const net = totalRevenue - prizePool;
                return (
                  <div className="info-grid-card p-4">
                    <h4 className="text-sm font-bold text-[hsl(var(--foreground))] mb-3 flex items-center gap-2">
                      <DollarSign className="h-4 w-4 text-green-400" />
                      Revenue Summary
                    </h4>
                    <div className="grid grid-cols-3 gap-3 text-center">
                      <div>
                        <div className="text-lg font-bold text-green-400">
                          ₹{totalRevenue.toLocaleString()}
                        </div>
                        <div className="text-[0.6875rem] text-[hsl(var(--muted-foreground))]">
                          Total Revenue
                        </div>
                      </div>
                      <div>
                        <div className="text-lg font-bold text-amber-400">
                          ₹{prizePool.toLocaleString()}
                        </div>
                        <div className="text-[0.6875rem] text-[hsl(var(--muted-foreground))]">
                          Prize Payout
                        </div>
                      </div>
                      <div>
                        <div
                          className={`text-lg font-bold ${net >= 0 ? 'text-[hsl(var(--accent))]' : 'text-red-400'}`}
                        >
                          ₹{net.toLocaleString()}
                        </div>
                        <div className="text-[0.6875rem] text-[hsl(var(--muted-foreground))]">
                          Net Profit
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Platform Metrics */}
              <div>
                <h4 className="text-sm font-bold text-[hsl(var(--foreground))] mb-2 flex items-center gap-2">
                  <Layers className="h-4 w-4 text-[hsl(var(--accent))]" />
                  Platform Metrics
                </h4>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <div className="mt-stat-tile items-center text-center">
                    <span className="text-lg font-bold text-blue-400">
                      {tournament.max_participants > 0
                        ? Math.round(
                            (tournament.current_participants / tournament.max_participants) * 100
                          )
                        : 0}
                      %
                    </span>
                    <span className="text-[0.6875rem] text-[hsl(var(--muted-foreground))]">
                      Fill Rate
                    </span>
                  </div>
                  <div className="mt-stat-tile items-center text-center">
                    <span className="text-lg font-bold text-[hsl(var(--accent))]">
                      {tournament.current_participants}
                    </span>
                    <span className="text-[0.6875rem] text-[hsl(var(--muted-foreground))]">
                      Teams
                    </span>
                  </div>
                  <div className="mt-stat-tile items-center text-center">
                    <span className="text-lg font-bold text-green-400">
                      ₹
                      {tournament.current_participants > 0
                        ? Math.round(Number(tournament.entry_fee || 0))
                        : 0}
                    </span>
                    <span className="text-[0.6875rem] text-[hsl(var(--muted-foreground))]">
                      Rev/Team
                    </span>
                  </div>
                  <div className="mt-stat-tile items-center text-center">
                    <span className="text-lg font-bold text-amber-400">
                      {roundGroups.length || 0}
                    </span>
                    <span className="text-[0.6875rem] text-[hsl(var(--muted-foreground))]">
                      Groups
                    </span>
                  </div>
                </div>
              </div>

              {/* Round Progress */}
              {tournament.rounds && tournament.rounds.length > 0 && (
                <div>
                  <h4 className="text-sm font-bold text-[hsl(var(--foreground))] mb-2 flex items-center gap-2">
                    <Layers className="h-4 w-4 text-[hsl(var(--accent))]" />
                    Round Progress
                  </h4>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {tournament.rounds.map((round) => {
                      const rNum = round.round;
                      const rStatus = getRoundStatus(rNum);
                      const rName =
                        roundNames?.[String(rNum)] ||
                        tournament.round_names?.[String(rNum)] ||
                        `Round ${rNum}`;
                      return (
                        <div
                          key={rNum}
                          className={`text-center p-2.5 rounded-lg border ${
                            rStatus === 'ongoing'
                              ? 'bg-[hsl(var(--accent)/0.1)] border-[hsl(var(--accent)/0.3)]'
                              : rStatus === 'completed'
                                ? 'bg-green-500/10 border-green-500/30'
                                : 'bg-[hsl(var(--secondary)/0.2)] border-[hsl(var(--border)/0.2)]'
                          }`}
                        >
                          <div className="text-xs font-medium text-[hsl(var(--foreground))]">
                            {rName}
                          </div>
                          <div
                            className={`text-[0.6875rem] mt-0.5 ${
                              rStatus === 'ongoing'
                                ? 'text-[hsl(var(--accent))]'
                                : rStatus === 'completed'
                                  ? 'text-green-400'
                                  : 'text-[hsl(var(--muted-foreground))]'
                            }`}
                          >
                            {rStatus === 'ongoing'
                              ? 'Active'
                              : rStatus === 'completed'
                                ? 'Done'
                                : 'Pending'}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Next Match In */}
              {tournament.tournament_start && (
                <div className="p-3 rounded-lg bg-[hsl(var(--secondary)/0.2)] border border-[hsl(var(--border)/0.2)]">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-[hsl(var(--muted-foreground))]">
                      Next Match In
                    </span>
                    <CountdownTimer
                      targetDate={tournament.tournament_start}
                      className="text-sm font-mono font-bold text-[hsl(var(--foreground))]"
                    />
                  </div>
                </div>
              )}

              {/* Credentials Scheduled */}
              <div className="flex items-center justify-between p-3 rounded-lg bg-[hsl(var(--secondary)/0.2)] border border-[hsl(var(--border)/0.2)]">
                <span className="text-xs text-[hsl(var(--muted-foreground))]">
                  Credentials Scheduled
                </span>
                <span className="text-sm font-semibold text-[hsl(var(--foreground))]">
                  {(() => {
                    let scheduled = 0;
                    let total = 0;
                    roundGroups.forEach((g) => {
                      (g.matches || []).forEach((m) => {
                        total++;
                        if (m.room_id) scheduled++;
                      });
                    });
                    return `${scheduled} / ${total || '—'}`;
                  })()}
                </span>
              </div>

              {/* Registered Teams */}
              {registrations.length > 0 && (
                <div>
                  <h4 className="text-sm font-bold text-[hsl(var(--foreground))] mb-2 flex items-center gap-2">
                    <Users className="h-4 w-4 text-[hsl(var(--accent))]" />
                    Registered Teams (
                    {
                      registrations.filter(
                        (r) => r.status === 'confirmed' || r.status === 'approved'
                      ).length
                    }
                    )
                  </h4>
                  <div className="grid grid-cols-3 sm:grid-cols-5 gap-1.5 max-h-48 overflow-y-auto custom-scrollbar">
                    {registrations
                      .filter((r) => r.status === 'confirmed' || r.status === 'approved')
                      .slice(0, 50)
                      .map((reg) => (
                        <div
                          key={reg.id}
                          className="px-2 py-1.5 bg-[hsl(var(--secondary)/0.3)] rounded-lg text-[0.6875rem] text-center truncate border border-[hsl(var(--border)/0.2)]"
                          title={reg.team_name || reg.player?.user?.username}
                        >
                          {reg.team_name || reg.player?.user?.username || 'Team'}
                        </div>
                      ))}
                    {registrations.filter(
                      (r) => r.status === 'confirmed' || r.status === 'approved'
                    ).length > 50 && (
                      <div className="px-2 py-1.5 bg-[hsl(var(--accent)/0.15)] rounded-lg text-[0.6875rem] text-center text-[hsl(var(--accent))] font-medium border border-[hsl(var(--accent)/0.3)]">
                        +
                        {registrations.filter(
                          (r) => r.status === 'confirmed' || r.status === 'approved'
                        ).length - 50}{' '}
                        more
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="p-4 border-t border-[hsl(var(--border)/0.3)] flex justify-end">
              <button
                onClick={() => setShowOverviewDialog(false)}
                className="px-4 py-2 bg-[hsl(var(--secondary))] hover:bg-[hsl(var(--secondary)/0.8)] text-[hsl(var(--foreground))] rounded-lg text-sm font-medium transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageTournament;
